import React, { useState, useRef, useCallback, useEffect } from "react";
import { ClientJournals } from "./Journal";
import {
  getStorage, ref, uploadBytesResumable,
  getDownloadURL, deleteObject
} from "firebase/storage";
import {
  getFirestore, doc, setDoc, getDoc,
  collection, getDocs, deleteDoc, addDoc,
  query, orderBy, limit, where
} from "firebase/firestore";
import {
  getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged
} from "firebase/auth";
import { auth, db, storage } from "./firebase";
import {
  cuesToVtt, cuesToSrt, mmss, parseMmss,
  sanitizeFilename, downloadText, LANG_LABEL,
} from "./captions";

// Inactivity = a paid client whose last app-open was 3+ days ago, excluding anyone
// snoozed (manual 7-day snooze or auto-snooze after a nudge) or dismissed until they
// next return. Snooze/dismiss state is persisted on the user doc so it survives refresh.
// Clients who finished everything they paid for are NOT at risk — they're graduates,
// surfaced separately on the dashboard (see `completedAll` in HomeTab).
// A "playable" session is real coursework: it counts toward progress, completion and
// sequential unlocking. Contemplations (text prompts) and interactive tools (embedded
// calculators/quizzes) are extras and must be excluded everywhere totals are computed —
// otherwise a program with a tool can never reach 100%.
const isPlayableSession = (s) => s?.type !== "reflection" && s?.type !== "interactive";

const INACTIVE_DAYS = 3;
function isInactiveClient(c, now = Date.now()) {
  if (!c.paid || !c.lastSeen) return false;
  if (c.allCompleted) return false;
  const seen = new Date(c.lastSeen).getTime();
  if (Math.floor((now - seen) / 86400000) < INACTIVE_DAYS) return false;
  if (c.inactivitySnoozedUntil && now < new Date(c.inactivitySnoozedUntil).getTime()) return false;
  if (c.inactivityDismissedAt && seen <= new Date(c.inactivityDismissedAt).getTime()) return false;
  return true;
}

// ─── PROGRAMS ─────────────────────────────────────────────────────────────────
const PROGRAMS = [
  {
    id: "brand-new-me",
    name: "Brand New Me",
    coach: "Coach Elibariki Mkumbo",
    days: 10,
    sessionsPerDay: 3,
    orientationDay: true,
  }
];

// ─── ACCESS HELPERS ───────────────────────────────────────────────────────────
function computeExpiresAt(accessType, fromDate = new Date()) {
  const months = { '3months': 3, '6months': 6, '12months': 12 };
  const m = months[accessType];
  if (!m) return null;
  const d = new Date(fromDate);
  d.setMonth(d.getMonth() + m);
  return d.toISOString();
}

function accessLabel(accessType) {
  return { 'lifetime': 'Lifetime', '3months': '3 Months', '6months': '6 Months', '12months': '12 Months' }[accessType] || 'Lifetime';
}

// ─── FIREBASE HELPERS ─────────────────────────────────────────────────────────
const loadSessionsFromFirestore = async (programId, sessions) => {
  const result = {};
  for (const session of sessions) {
    try {
      const snap = await getDoc(doc(db, "programs", programId, "sessions", session.id));
      if (snap.exists()) result[session.id] = { status: "uploaded", ...snap.data() };
      else result[session.id] = { status: "empty" };
    } catch { result[session.id] = { status: "empty" }; }
  }
  return result;
};

const parseSessionId = (id) => {
  // e.g. brand-new-me-d0-intro-p1  →  { day:0, slot:"intro", part:1 }
  const match = id.match(/-d(\d+)-([a-z]+)-p(\d+)$/);
  if (!match) return null;
  return { day: parseInt(match[1]), slot: match[2], part: parseInt(match[3]) };
};

const loadPartsFromFirestore = async (programId) => {
  const snap = await getDocs(collection(db, "programs", programId, "sessions"));
  const uploads = {};
  const slots = {};
  const repairs = [];

  snap.docs.forEach(d => {
    const data = d.data();
    let { day, slot, part } = data;

    // Repair docs that lost day/slot/part during the old upload bug
    if (day === undefined || slot === undefined || part === undefined) {
      const parsed = parseSessionId(d.id);
      if (parsed) {
        ({ day, slot, part } = parsed);
        repairs.push(setDoc(doc(db, "programs", programId, "sessions", d.id), { day, slot, part }, { merge: true }));
      }
    }

    // Repair MPEG files incorrectly stored as "video" (e.g. WhatsApp audio)
    const audioExts = /\.(mp3|mpeg|m4a|ogg|wav|aac|opus|flac)(\?|%2F|$)/i;
    if (data.contentType === "video" && data.audioUrl && audioExts.test(data.audioUrl)) {
      repairs.push(setDoc(doc(db, "programs", programId, "sessions", d.id), { contentType: "audio" }, { merge: true }));
      data.contentType = "audio";
    }

    uploads[d.id] = { status: data.audioUrl ? "uploaded" : "empty", ...data, day, slot, part };
    const key = `${day}-${slot}`;
    if (!slots[key]) slots[key] = [];
    if (!slots[key].includes(d.id)) slots[key].push(d.id);
  });

  if (repairs.length > 0) await Promise.all(repairs);

  Object.keys(slots).forEach(key => {
    slots[key].sort((a, b) => (uploads[a]?.part || 0) - (uploads[b]?.part || 0));
  });
  return { uploads, slots };
};

const getFileDuration = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const el = file.type.startsWith("video/") ? document.createElement("video") : document.createElement("audio");
  el.src = url;
  el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(el.duration) || 0); };
  el.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
});

// Detect the REAL media type from the file's first bytes. The browser's `file.type`
// is unreliable — some systems report MP3s as "video/mpeg", which previously caused
// audio to be stored as video/mpeg and silently fail to play in a <video> element.
// Returns { kind: "audio" | "video", mime } — mime is set explicitly on the stored
// object so the browser always knows how to decode it.
const sniffMediaType = async (file) => {
  let head = new Uint8Array(0);
  try { head = new Uint8Array(await file.slice(0, 16).arrayBuffer()); } catch {}
  const hex = [...head].map(b => b.toString(16).padStart(2, "0")).join("");
  const ascii = String.fromCharCode(...head);

  if (ascii.includes("ftyp")) {
    const brand = ascii.slice(ascii.indexOf("ftyp") + 4, ascii.indexOf("ftyp") + 8);
    if (/^(M4A|M4B|F4A|mp4a)/i.test(brand)) return { kind: "audio", mime: "audio/mp4" };
    return { kind: "video", mime: "video/mp4" }; // MP4/MOV (ISO-BMFF)
  }
  if (hex.startsWith("1a45dfa3")) return { kind: "video", mime: "video/webm" };
  if (hex.startsWith("494433") || /^fff[0-9a-f]/.test(hex)) return { kind: "audio", mime: "audio/mpeg" }; // MP3
  if (hex.startsWith("4f676753")) return { kind: "audio", mime: "audio/ogg" };
  if (ascii.startsWith("RIFF") && ascii.slice(8, 12) === "WAVE") return { kind: "audio", mime: "audio/wav" };

  // Unrecognised header — fall back to file.type, but classify audio/video sanely.
  const t = file.type || "";
  if (t.startsWith("video/")) return { kind: "video", mime: t };
  if (t.startsWith("audio/")) return { kind: "audio", mime: t };
  return { kind: "audio", mime: t || "application/octet-stream" };
};

// Upload a self-contained interactive tool (.html) and return its public download URL.
// contentType + inline disposition make the browser RENDER it in the sandboxed frame
// instead of downloading it. Unlike session media this isn't paid content, so the
// tokened download URL (same as thumbnails) is fine — no signed-URL function needed.
const uploadHtmlFile = (path, file, onProgress) => new Promise((resolve, reject) => {
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, file, {
    contentType: "text/html; charset=utf-8",
    contentDisposition: "inline",
  });
  task.on("state_changed",
    snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
    reject,
    async () => { const url = await getDownloadURL(task.snapshot.ref); resolve(url); }
  );
});

const uploadFile = (path, file, onProgress) => new Promise((resolve, reject) => {
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, file);
  task.on("state_changed",
    snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
    reject,
    async () => { const url = await getDownloadURL(task.snapshot.ref); resolve(url); }
  );
});

// (H2) Upload session media WITHOUT generating a public download token. Paid media
// must only be reachable via short-lived signed URLs (get-media-url function), so we
// never call getDownloadURL on it — we store the path and serve it on demand.
const uploadFileNoToken = (path, file, onProgress, contentType) => new Promise((resolve, reject) => {
  const storageRef = ref(storage, path);
  // Set the object's Content-Type explicitly (from content sniffing) so playback
  // never depends on the browser-reported file.type, which can be wrong.
  const task = uploadBytesResumable(storageRef, file, contentType ? { contentType } : undefined);
  task.on("state_changed",
    snap => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
    reject,
    () => resolve()
  );
});

// Fetch a signed playback URL (admins included) for previewing protected media.
const fetchSignedMediaUrl = async (programId, sessionId) => {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch('/.netlify/functions/get-media-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ programId, sessionId }),
  });
  if (!res.ok) throw new Error('preview unavailable');
  const data = await res.json();
  return data.url;
};

// ── Captions ───────────────────────────────────────────────────────────────────
// Kick off the on-demand caption pipeline (Netlify background function → 202). The
// caller then polls the captions doc for status.
const triggerCaptionGeneration = async (programId, sessionId) => {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch('/.netlify/functions/generate-captions-background', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ programId, sessionId }),
  });
  // Background functions answer 202 (accepted). Anything else is an up-front failure.
  if (res.status !== 202 && !res.ok) {
    let msg = 'Could not start caption generation';
    try { msg = (await res.text()) || msg; } catch (_) {}
    throw new Error(msg);
  }
};

// Read a session's caption doc (programs/{pid}/sessions/{sid}/captions/data), or null.
const loadCaption = async (programId, sessionId) => {
  const snap = await getDoc(doc(db, 'programs', programId, 'sessions', sessionId, 'captions', 'data'));
  return snap.exists() ? snap.data() : null;
};

// Poll a session's caption doc until generation finishes (ready/error) or times out.
// `prevUpdatedAt` is the doc's updatedAt BEFORE triggering, so a stale earlier "ready"
// isn't mistaken for this run's result. Used to run the bulk generator sequentially.
const waitForCaption = (programId, sessionId, prevUpdatedAt, timeoutMs = 15 * 60 * 1000) => new Promise((resolve) => {
  const start = Date.now();
  const tick = async () => {
    let c = null;
    try { c = await loadCaption(programId, sessionId); } catch (_) { /* transient */ }
    if (c && (c.status === 'ready' || c.status === 'error') && c.updatedAt !== prevUpdatedAt) {
      return resolve(c.status);
    }
    if (Date.now() - start > timeoutMs) return resolve('timeout');
    setTimeout(tick, 6000);
  };
  setTimeout(tick, 5000); // give the function a head start before the first poll
});

// Build a Word-openable HTML document (.doc) from captioned sessions — a title, then
// a heading + clean prose per session. `items` = [{ name, proseSw, proseEn, cues }];
// lang = 'sw'|'en'|'bilingual'. HTML-flavoured .doc opens natively in Word and needs
// no library (the CRA bundle stays lean); the local export-book.js script produces a
// richer true .docx for the same content.
const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const buildCaptionDocHtml = (bookTitle, items, lang) => {
  const proseFor = (it, l) => {
    const key = l === 'en' ? 'proseEn' : 'proseSw';
    if (it[key] && it[key].trim()) return it[key].trim();
    return (it.cues || []).map(c => (c[l] || '').trim()).filter(Boolean).join(' ');
  };
  const paras = (text) => String(text || '').split(/\n{2,}/).map(p => p.trim()).filter(Boolean)
    .map(p => `<p style="margin:0 0 10pt;line-height:1.5">${escapeHtml(p)}</p>`).join('');
  const langBlock = (it, l, withLabel) => {
    const label = withLabel ? `<p style="margin:8pt 0 4pt"><b>${LANG_LABEL[l]}</b></p>` : '';
    return label + (paras(proseFor(it, l)) || '<p><i>(no captions)</i></p>');
  };
  let body = `<h1>${escapeHtml(bookTitle)}</h1>`;
  items.forEach((it, i) => {
    body += `<h2 style="margin:16pt 0 6pt">${escapeHtml(it.name || `Session ${i + 1}`)}</h2>`;
    if (lang === 'bilingual') body += langBlock(it, 'sw', true) + langBlock(it, 'en', true);
    else body += langBlock(it, lang === 'en' ? 'en' : 'sw', false);
  });
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>${escapeHtml(bookTitle)}</title></head><body style="font-family:Calibri,Arial,sans-serif;font-size:12pt">${body}</body></html>`;
};

// Progress is no longer stored on the users doc — the live client app writes it to
// enrollments/{uid}/programs/{pid}.progress as { [sessionId]: { completedAt } }. Merge
// every enrolled program's progress into one map per client, keyed by sessionId and
// normalised to { percent: 100 } so the percent-based admin consumers keep working.
// We also capture each client's enrolled programs (for the program facet + row/overview).
const loadClients = async () => {
  const [snap, progsSnap, paySnap] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "programs")),
    getDocs(collection(db, "payments")),
  ]);
  const progTitle = {};
  progsSnap.docs.forEach(d => { progTitle[d.id] = d.data().title || d.id; });
  // Latest still-pending payment attempt per user → drives the "asked to pay Xd ago"
  // tag and the conversion funnel. (AzamPay checkout writes a payments doc with
  // createdAt; a manual "I've paid" claim has no payment doc, so no attempt time.)
  const attemptByUser = {};
  paySnap.docs.forEach(d => {
    const p = d.data();
    if (!p.userId || !p.createdAt || (p.status && p.status !== "pending")) return;
    if (!attemptByUser[p.userId] || p.createdAt > attemptByUser[p.userId]) attemptByUser[p.userId] = p.createdAt;
  });
  // Playable sessions per program (contemplations aren't played, so they never get
  // marked complete and must not count toward the total). Lets us tell a genuine
  // graduate from someone who drifted off — a graduate isn't "at risk".
  const sessionTotals = {};
  await Promise.all(progsSnap.docs.map(async (p) => {
    try {
      const s = await getDocs(collection(db, "programs", p.id, "sessions"));
      sessionTotals[p.id] = s.docs.filter(d => isPlayableSession(d.data())).length;
    } catch { sessionTotals[p.id] = 0; }
  }));

  const clients = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  clients.forEach(c => { c.paymentAttemptAt = attemptByUser[c.id] || null; });
  await Promise.all(clients.map(async (c) => {
    try {
      const enrollSnap = await getDocs(collection(db, "enrollments", c.id, "programs"));
      const merged = {};
      const programs = [];
      enrollSnap.docs.forEach(e => {
        const data = e.data();
        const prog = data.progress || {};
        Object.entries(prog).forEach(([sid, v]) => {
          merged[sid] = { percent: 100, completedAt: v?.completedAt || null };
        });
        const total = sessionTotals[e.id] || 0;
        const done = countCompleted(prog);
        programs.push({
          id: e.id, title: progTitle[e.id] || e.id, paid: data.paid === true,
          done, total, completed: total > 0 && done >= total,
        });
      });
      c.progress = merged;
      c.programs = programs;
      const active = programs.find(p => p.paid) || programs[0];
      c.activeProgramTitle = active ? active.title : null;
      c.completedSessions = countCompleted(merged);
      // Finished everything they paid for → a graduate, not a churn risk.
      const paidProgs = programs.filter(p => p.paid);
      c.allCompleted = paidProgs.length > 0 && paidProgs.every(p => p.completed);
      c.sessionsTotal = paidProgs.reduce((s, p) => s + p.total, 0);
      c.lastCompletedAt = Object.values(merged).map(v => v.completedAt).filter(Boolean).sort().pop() || null;
    } catch (err) {
      console.error("progress load failed for", c.id, err);
      c.progress = {};
      c.programs = [];
      c.activeProgramTitle = null;
      c.completedSessions = 0;
      c.allCompleted = false;
      c.lastCompletedAt = null;
      c.sessionsTotal = 0;
    }
  }));
  return clients;
};

// New-model progress entries are { percent: 100 }; legacy entries were raw percents.
// Count a session "done" at >= 80% under either shape.
const countCompleted = (progress) =>
  Object.values(progress || {}).filter(p => (p?.percent ?? p) >= 80).length;

// Normalise a stored phone into a wa.me number (digits, country code, no +).
// Handles +255…, 0…, and bare 9-digit local numbers (defaults to TZ +255).
const waNumber = (phone) => {
  let d = (phone || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("0")) d = "255" + d.slice(1);
  else if (d.length === 9) d = "255" + d;
  return d;
};
// ─── NUDGE ────────────────────────────────────────────────────────────────────
// Shared by the Home inactivity alert and the Clients list. WhatsApp can't be sent
// programmatically without the paid Business API, so we open it pre-filled and the
// coach taps send; the email goes out automatically alongside it.
const daysSinceSeen = (c) => c?.lastSeen ? Math.floor((Date.now() - new Date(c.lastSeen).getTime()) / 86400000) : null;

// Which of the two channels this client can actually be reached on.
const nudgeChannels = (c) => [
  waNumber(c?.phone) && "WhatsApp",
  (c?.email || "").includes("@") && "email",
].filter(Boolean);

// The message adapts: someone who never opened the app shouldn't be told it's been
// N days "since your last session".
const nudgeVariant = (c) => { const d = daysSinceSeen(c); return d === null ? "welcome" : d === 0 ? "active" : "inactive"; };

function buildNudgeMessage(client) {
  const first = (client.name || "").split(" ")[0] || "";
  const link = "https://beyondthebody.co.tz/programs";
  const d = daysSinceSeen(client);
  switch (nudgeVariant(client)) {
    case "welcome":
      return `Habari ${first}, karibu Beyond The Body! Tumeona bado hujaanza safari yako — anza leo:\n${link}\n\n`
           + `Hi ${first} — welcome! You haven't started your journey yet. Begin today at the link above.`;
    case "active":
      return `Habari ${first}, hongera kwa kuendelea na safari yako! Endelea na kipindi kinachofuata:\n${link}\n\n`
           + `Hi ${first} — great to see you practising. Continue with your next session at the link above.`;
    default:
      return `Habari ${first}, ni siku ${d} tangu kipindi chako cha mwisho. Tunakukumbuka!\nRudi uendelee na safari yako: ${link}\n\n`
           + `Hi ${first} — it's been ${d} day${d === 1 ? "" : "s"} since your last session. Continue your practice at the link above.`;
  }
}

// MUST be called synchronously from the click handler — any await first and the
// popup blocker kills the WhatsApp tab.
const openNudgeWhatsApp = (client) => {
  const wa = waNumber(client.phone);
  if (!wa) return false;
  window.open(`https://wa.me/${wa}?text=${encodeURIComponent(buildNudgeMessage(client))}`, "_blank", "noopener");
  return true;
};

const sendNudgeEmail = async (client) => {
  if (!(client.email || "").includes("@")) return false;
  const token = await getAuth().currentUser?.getIdToken();
  await fetch("/.netlify/functions/notify-inactive", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      clientName: client.name || "there",
      clientEmail: client.email,
      daysSeen: daysSinceSeen(client),
      variant: nudgeVariant(client),
    }),
  });
  return true;
};

const WhatsAppGlyph = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 00-8.5 15.3L2 22l4.8-1.4A10 10 0 1012 2zm0 18a8 8 0 01-4.1-1.1l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 1112 20zm4.4-6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1-.6.8-.8 1-.3.2-.5.1a6.6 6.6 0 01-1.9-1.2 7.3 7.3 0 01-1.4-1.7c-.1-.2 0-.4.1-.5l.4-.4.2-.4v-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a1 1 0 00-.7.3A2.8 2.8 0 006 8.3c0 1.7 1.2 3.3 1.4 3.5s2.4 3.6 5.7 5c.8.3 1.4.5 1.9.7.8.2 1.5.2 2.1.1.6-.1 1.4-.6 1.6-1.2.2-.6.2-1 .1-1.2z"/></svg>
);

const loadCustomPrograms = async () => {
  const snap = await getDocs(collection(db, "customPrograms"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
};

// ─── SHARED UI ────────────────────────────────────────────────────────────────
// Small, reusable primitives so every list/detail/dashboard reads the same and
// status colours come from semantic tokens (not per-component hardcoded hex).

function Badge({ tone = "stone", dot, children }) {
  return <span className={`ui-badge ui-badge--${tone}`}>{dot && <span className="ui-badge-dot" />}{children}</span>;
}

// Derive a client's primary status pill. `completed` is passed in where the caller
// knows the program total (the list/detail compute it against the active program).
function clientStatusBadge(c, completed) {
  if (completed) return <Badge tone="violet">Completed</Badge>;
  if (c.paid) return c.grantedByAdmin ? <Badge tone="info">Free access</Badge> : <Badge tone="ok">Active</Badge>;
  if (c.paymentPending) return <Badge tone="warn">Pending</Badge>;
  return <Badge tone="danger">Unpaid</Badge>;
}

function KpiCard({ value, label, accent, sub, onClick, active, danger }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`kpi-card${onClick ? " kpi-card--btn" : ""}${active ? " kpi-card--active" : ""}`}
      onClick={onClick}
      style={accent ? { "--kpi-accent": accent } : undefined}
    >
      <span className={`kpi-val${danger ? " kpi-val--danger" : ""}`}>{value}</span>
      <span className="kpi-lab">{label}</span>
      {sub && <span className="kpi-sub">{sub}</span>}
    </Tag>
  );
}

function ProgressBar({ pct = 0, tone = "ok", height }) {
  const w = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className="ui-bar" style={height ? { height } : undefined}>
      <div className={`ui-bar-fill ui-bar-fill--${tone}`} style={{ width: `${w}%` }} />
    </div>
  );
}

// Lightweight inline-SVG area+line chart for revenue-over-time (no charting lib).
function TrendChart({ points, height = 90 }) {
  if (!points || points.length === 0) return <div className="empty-state">No revenue yet.</div>;
  const max = Math.max(...points.map(p => p.value), 1);
  const W = 100, H = 100, pad = 6, n = points.length;
  const x = i => (n <= 1 ? W / 2 : pad + (i * (W - 2 * pad)) / (n - 1));
  const y = v => H - pad - (v / max) * (H - 2 * pad);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const area = `${line} L${x(n - 1).toFixed(1)},${H - pad} L${x(0).toFixed(1)},${H - pad} Z`;
  return (
    <div className="trend">
      <svg className="trend-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height }} role="img" aria-label="Revenue trend (last 6 months)">
        <path className="trend-area" d={area} vectorEffect="non-scaling-stroke" />
        <path className="trend-line" d={line} fill="none" vectorEffect="non-scaling-stroke" />
        {points.map((p, i) => (
          <circle key={i} className="trend-dot" cx={x(i)} cy={y(p.value)} r="1.6" vectorEffect="non-scaling-stroke">
            <title>{p.label}: TZS {p.value.toLocaleString()}</title>
          </circle>
        ))}
      </svg>
      <div className="trend-labels">{points.map((p, i) => <span key={i}>{p.label}</span>)}</div>
    </div>
  );
}

function Skeleton({ rows = 6 }) {
  return (
    <div className="skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton-row">
          <div className="skeleton-avatar" />
          <div className="skeleton-lines"><div className="skeleton-line" /><div className="skeleton-line short" /></div>
        </div>
      ))}
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, error }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const handle = async () => { setLoading(true); await onLogin(email, password); setLoading(false); };
  return (
    <div className="login-wrap"><style>{CSS}</style>
      <div className="login-box">
        <div className="login-logo">
          <svg viewBox="0 0 40 40" width="40" height="40" fill="none"><circle cx="20" cy="20" r="18" stroke="#7FB8C2" strokeWidth="1.5"/><circle cx="20" cy="10" r="2.5" fill="#7FB8C2"/><path d="M17.5 14.5L10 8" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><path d="M22.5 14.5L30 8" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><line x1="20" y1="12.5" x2="20" y2="24" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><line x1="20" y1="24" x2="13" y2="32" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><line x1="20" y1="24" x2="27" y2="32" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <span>Beyond The Body</span>
        </div>
        <p className="login-sub">Admin Portal</p>
        {error && <div className="login-error">{error}</div>}
        <div className="login-field"><label>Email</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} /></div>
        <div className="login-field"><label>Password</label><input type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} /></div>
        <button className="btn-login" onClick={handle} disabled={loading}>{loading?"Signing in…":"Sign In"}</button>
      </div>
    </div>
  );
}

// ─── UPLOAD ROW ───────────────────────────────────────────────────────────────
function UploadRow({ session, uploadState, onUpload, onDelete }) {
  const audioRef = useRef(null); const imageRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [audioPct, setAudioPct] = useState(0);
  const [imagePct, setImagePct] = useState(0);
  const [error, setError] = useState(null);
  const st = uploadState[session.id] || { status: "empty" };

  const handleAudio = async (file) => {
    if (!file) return;
    const allowedTypes = ["audio/mpeg","audio/mp4","audio/ogg","audio/wav","audio/aac","audio/opus","audio/flac","video/mp4","video/webm","video/quicktime"];
    if (!allowedTypes.includes(file.type)) { setError("Invalid file type. Allowed: MP3, MP4, OGG, WAV, AAC, MOV, WEBM."); return; }
    if (file.size > 500 * 1024 * 1024) { setError("File too large. Maximum size is 500 MB."); return; }
    setUploadingAudio(true); setError(null);
    try {
      const ext = file.name.split(".").pop() || "mp3";
      const cleanPath = `${session.storagePath}.${ext}`;
      // Detect the real type from content (file.type can lie) and store it explicitly.
      const { kind, mime } = await sniffMediaType(file);
      // (H2) No public token — store the path only; playback uses signed URLs.
      const [, duration] = await Promise.all([uploadFileNoToken(cleanPath, file, setAudioPct, mime), getFileDuration(file)]);
      const programId = session.storagePath.split("/")[1];
      const meta = { audioUrl: "", audioFileName: file.name, audioSize: `${(file.size/1024/1024).toFixed(1)} MB`, contentType: kind, duration, storagePath: cleanPath, updatedAt: new Date().toISOString(), status: "uploaded" };
      const updated = { ...st, ...meta };
      await setDoc(doc(db, "programs", programId, "sessions", session.id), meta, { merge: true });
      onUpload(session.id, updated);
    } catch(e) {
      setError(`Upload failed: ${e.message}`);
    }
    setUploadingAudio(false);
  };

  const handleImage = async (file) => {
    if (!file) return;
    const allowedTypes = ["image/jpeg","image/png","image/webp"];
    if (!allowedTypes.includes(file.type)) { setError("Invalid file type. Allowed: JPG, PNG, WEBP."); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Image too large. Maximum size is 5 MB."); return; }
    setUploadingImage(true); setError(null);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const cleanPath = `${session.storagePath}-thumbnail.${ext}`;
      console.log("Uploading image to:", cleanPath);
      const url = await uploadFile(cleanPath, file, setImagePct);
      const programId = session.storagePath.split("/")[1];
      const updated = { ...st, thumbnailUrl: url, thumbnailPath: cleanPath, status: "uploaded" };
      await setDoc(doc(db, "programs", programId, "sessions", session.id), { thumbnailUrl: url, thumbnailPath: cleanPath, status: "uploaded" }, { merge: true });
      onUpload(session.id, updated);
    } catch(e) {
      console.error("Image upload error:", e);
      setError(`Upload failed: ${e.message}`);
    }
    setUploadingImage(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${session.label}?`)) return;
    try { if (st.storagePath) await deleteObject(ref(storage, st.storagePath)); } catch {}
    try { if (st.thumbnailPath) await deleteObject(ref(storage, st.thumbnailPath)); } catch {}
    await deleteDoc(doc(db, "programs", session.storagePath.split("/")[1], "sessions", session.id));
    onDelete(session.id);
  };

  return (
    <div className={`upload-row ${st.status} ${dragging?"drag-over":""}`}
      onDragOver={e=>{e.preventDefault();setDragging(true)}}
      onDragLeave={()=>setDragging(false)}
      onDrop={e=>{e.preventDefault();setDragging(false);const f=e.dataTransfer.files[0];if(f.type.startsWith("image/"))handleImage(f);else handleAudio(f);}}
    >
      {/* THUMBNAIL */}
      <div className="upload-thumb" onClick={()=>imageRef.current?.click()} title="Click to upload thumbnail image">
        {st.thumbnailUrl
          ? <img src={st.thumbnailUrl} alt="thumbnail" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:6}} />
          : <div className="thumb-placeholder">{uploadingImage ? <div className="mini-spinner"/> : <span>{session.icon}</span>}</div>
        }
        {uploadingImage && <div className="thumb-progress">{imagePct}%</div>}
      </div>

      <div className="upload-row-left">
        <div className="upload-meta">
          <span className="upload-label">{session.label}</span>
          <span className="upload-path">{session.storagePath}</span>
          {st.audioFileName && <span className="upload-filename">{st.contentType === "video" ? <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{verticalAlign:"middle",marginRight:3}}><rect x="1" y="4.5" width="9" height="7" rx="1"/><path d="M10 7.5l4-2.5v6l-4-2.5V7.5z" strokeLinejoin="round"/></svg> : <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{verticalAlign:"middle",marginRight:3}} strokeLinecap="round"><path d="M11 2v8.5M7 14V6.5m0 0L11 5"/><circle cx="7" cy="14" r="1.5"/><circle cx="11" cy="10.5" r="1.5"/></svg>} {st.audioFileName} · {st.audioSize}</span>}
        </div>
      </div>

      <div className="upload-row-right">
        {error && <span className="upload-error">{error}</span>}

        {/* MEDIA UPLOAD */}
        {!uploadingAudio ? (
          <button className={`btn-upload ${st.audioUrl?"btn-replace":""}`} onClick={()=>audioRef.current?.click()}>
            {st.audioUrl ? `↑ Replace ${st.contentType === "video" ? "Video" : "Audio"}` : "Upload Audio / Video"}
          </button>
        ) : (
          <div className="uploading-state">
            <div className="upload-progress-bar"><div className="upload-progress-fill" style={{width:`${audioPct}%`}}/></div>
            <span>{audioPct}%</span>
          </div>
        )}

        {st.audioUrl && <div className="badge-uploaded">✓ {st.contentType === "video" ? "Video" : "Audio"}</div>}
        {st.thumbnailUrl && <div className="badge-thumb">✓ Image</div>}
        {(st.audioUrl || st.thumbnailUrl) && <button className="btn-delete" onClick={handleDelete}>✕</button>}

        <input ref={audioRef} type="file" accept="audio/*,video/*" style={{display:"none"}} onChange={e=>handleAudio(e.target.files[0])} />
        <input ref={imageRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleImage(e.target.files[0])} />
      </div>
    </div>
  );
}

// ─── CLIENT ROW & DETAIL ──────────────────────────────────────────────────────
const isRecentPayment = (client) => {
  if (!client.paidAt) return false;
  return Date.now() - new Date(client.paidAt).getTime() < 48 * 60 * 60 * 1000;
};

function ClientRow({ client, completedTotal = 0, selected, onToggle, onOpen, onMessage, onUndismiss, onNudge, selectable = true }) {
  const done = countCompleted(client.progress);
  // Prefer the client's own session total (summed across the programs they paid for)
  // over the legacy single-program total passed in.
  const total = client.sessionsTotal || completedTotal || 0;
  const completed = client.allCompleted !== undefined ? !!client.allCompleted : (total > 0 && done >= total);
  const isNew = isRecentPayment(client);
  const inactiveDays = client.lastSeen
    ? Math.floor((Date.now() - new Date(client.lastSeen).getTime()) / 86400000)
    : null;
  const isInactive = client.paid && !completed && inactiveDays !== null && inactiveDays >= 3;
  const seenLabel = inactiveDays === null ? "never seen"
    : inactiveDays === 0 ? "seen today"
    : inactiveDays === 1 ? "seen yesterday"
    : `seen ${inactiveDays}d ago`;
  const agoLabel = (iso, verb) => {
    if (!iso) return null;
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return d <= 0 ? `${verb} today` : d === 1 ? `${verb} yesterday` : `${verb} ${d}d ago`;
  };
  const joinedLabel = agoLabel(client.joinedAt, "joined");
  const isPending = client.paymentPending && !client.paid;
  const attemptLabel = isPending ? agoLabel(client.paymentAttemptAt, "asked to pay") : null;
  const pct = total > 0 ? Math.round((done / total) * 100) : Math.min(done * 10, 100);
  return (
    <div className={`client-row${selected ? " selected" : ""}`} role="button" tabIndex={0}
      onClick={() => onOpen(client)}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(client); } }}>
      <span className="col-check" onClick={e => e.stopPropagation()}>
        {selectable && <input type="checkbox" checked={!!selected} onChange={() => onToggle(client.id)} aria-label={`Select ${client.name || client.email || "client"}`} />}
      </span>
      <div className="col-identity" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <div className="client-avatar" style={isInactive ? { background: "#E8745A", color: "#fff" } : {}}>{(client.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2)}</div>
        <div className="client-info" style={{ minWidth: 0 }}>
          <span className="client-name" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{client.name || "Unknown"}</span>
            {isNew && <Badge tone="ok">New</Badge>}
            {attemptLabel && <Badge tone="warn">{attemptLabel}</Badge>}
            {client.leadDismissedAt && <Badge tone="stone">Dismissed</Badge>}
          </span>
          <span className="client-meta-line">
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 170 }}>{client.email || client.phone || "—"}</span>
            <span>·</span>
            <span className={isInactive ? "at-risk" : ""}>{seenLabel}</span>
            {joinedLabel && <><span>·</span><span>{joinedLabel}</span></>}
          </span>
        </div>
      </div>
      <span className="col-program">{client.activeProgramTitle || "—"}</span>
      <span className="col-status">{clientStatusBadge(client, completed)}</span>
      <span className="client-progress-col col-progress">
        <ProgressBar pct={pct} height={5} />
        <span className="mini-label">{done}{total > 0 ? `/${total}` : ""}</span>
      </span>
      <span className="col-actions">
        {onUndismiss && client.leadDismissedAt && (
          <button className="btn-icon client-quick" title="Undo dismiss — restore to action queue" aria-label="Undo dismiss" onClick={e => { e.stopPropagation(); onUndismiss(client); }}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 8a5.5 5.5 0 1 0 1.7-3.97"/><polyline points="2.5 2.5 2.5 5 5 5"/></svg>
          </button>
        )}
        {waNumber(client.phone) && (
          <a className="btn-icon client-quick" title="WhatsApp" aria-label="Chat on WhatsApp" href={`https://wa.me/${waNumber(client.phone)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: "#25D366" }}>
            <WhatsAppGlyph />
          </a>
        )}
        {onNudge && nudgeChannels(client).length > 0 && (
          <button className="btn-icon client-quick" aria-label="Nudge client"
            title={`Nudge — pre-written message via ${nudgeChannels(client).join(" + ")}`}
            onClick={e => { e.stopPropagation(); onNudge(client); }}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 1.5L7 9"/><path d="M14.5 1.5l-4.8 13-2.7-5.5L1.5 6.3z"/>
            </svg>
          </button>
        )}
        {onMessage && (
          <button className="btn-icon client-quick" title="Message" aria-label="Message client" onClick={e => { e.stopPropagation(); onMessage(client); }}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5A6 6 0 002 9.5c0 1.2.35 2.3.95 3.2L2 14l1.8-.9A6 6 0 0014 9.5z"/></svg>
          </button>
        )}
        <svg viewBox="0 0 16 16" fill="none" stroke="var(--stone)" strokeWidth="1.5" width="14" height="14"><polyline points="6 3 11 8 6 13"/></svg>
      </span>
    </div>
  );
}

function ClientDetail({ client, onClose, onApprove }) {
  const [detailTab, setDetailTab] = useState("progress");
  // Real per-program progress: enrolled programs + their sessions, loaded on demand.
  // null = not loaded yet (shows a loading line); [] = enrolled in nothing.
  const [progPrograms, setProgPrograms] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [privacyEnabled, setPrivacyEnabled] = useState(client.reflectionsPrivate || false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [coachMsg, setCoachMsg] = useState(client.coachDirectMessage || "");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [clientPrograms, setClientPrograms] = useState([]);
  const [clientEnrollments, setClientEnrollments] = useState({});
  const [grantingAccess, setGrantingAccess] = useState({});

  // Load the client's enrolled programs + their sessions for the Progress tab.
  useEffect(() => {
    if (detailTab !== "progress") return;
    let cancelled = false;
    (async () => {
      try {
        const enrollSnap = await getDocs(collection(db, "enrollments", client.id, "programs"));
        const enrolls = enrollSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const loaded = await Promise.all(enrolls.map(async (e) => {
          const [pDoc, sSnap] = await Promise.all([
            getDoc(doc(db, "programs", e.id)),
            getDocs(collection(db, "programs", e.id, "sessions")),
          ]);
          const sessions = sSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(isPlayableSession)
            .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
          return {
            id: e.id,
            title: pDoc.exists() ? (pDoc.data().title || e.id) : e.id,
            paid: e.paid === true,
            progress: e.progress || {},
            sessions,
          };
        }));
        if (!cancelled) setProgPrograms(loaded);
      } catch (err) {
        console.error(err);
        if (!cancelled) setProgPrograms([]);
      }
    })();
    return () => { cancelled = true; };
  }, [detailTab, client.id]);

  const done = countCompleted(client.progress);
  const total = progPrograms ? progPrograms.reduce((s, p) => s + p.sessions.length, 0) : 0;
  const completed = total > 0 && done >= total;

  useEffect(() => {
    if (detailTab !== "programs") return;
    const load = async () => {
      try {
        const [progSnap, enrollSnap] = await Promise.all([
          getDocs(collection(db, "programs")),
          getDocs(collection(db, "enrollments", client.id, "programs")),
        ]);
        const progs = progSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.published)
          .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        const enrollMap = {};
        enrollSnap.docs.forEach(d => { enrollMap[d.id] = d.data(); });
        setClientPrograms(progs);
        setClientEnrollments(enrollMap);
      } catch(e) { console.error(e); }
    };
    load();
  }, [detailTab, client.id]);

  const [editingExpiry, setEditingExpiry] = useState(null); // programId being edited
  const [expiryInput, setExpiryInput] = useState('');

  const saveExpiry = async (programId) => {
    try {
      const data = expiryInput
        ? { expiresAt: new Date(expiryInput).toISOString() }
        : { expiresAt: null };
      await setDoc(doc(db, "enrollments", client.id, "programs", programId), data, { merge: true });
      setClientEnrollments(e => ({
        ...e,
        [programId]: { ...e[programId], expiresAt: data.expiresAt }
      }));
    } catch(e) { console.error(e); }
    setEditingExpiry(null);
    setExpiryInput('');
  };

  const grantAccess = async (prog) => {
    const programId = prog.id;
    setGrantingAccess(g => ({ ...g, [programId]: "granting" }));
    try {
      const expiresAt = computeExpiresAt(prog.accessType);
      const data = { paid: true, enrolledAt: new Date().toISOString(), startDate: new Date().toISOString().split('T')[0], grantedByAdmin: true, accessType: prog.accessType || 'lifetime' };
      if (expiresAt) data.expiresAt = expiresAt;
      await setDoc(doc(db, "enrollments", client.id, "programs", programId), data);
      // Reflect access in the global flag so the client shows as active in the Clients
      // tab. No paidAt — admin grants are free access and never count as revenue.
      await setDoc(doc(db, "users", client.id), { paid: true }, { merge: true });
      setClientEnrollments(e => ({ ...e, [programId]: data }));
    } catch(e) { console.error(e); }
    setGrantingAccess(g => ({ ...g, [programId]: null }));
  };

  const revokeAccess = async (programId) => {
    if (!window.confirm(`Revoke access to ${programId} for ${client.name}?`)) return;
    setGrantingAccess(g => ({ ...g, [programId]: "revoking" }));
    try {
      await setDoc(doc(db, "enrollments", client.id, "programs", programId), { paid: false }, { merge: true });
      setClientEnrollments(e => ({ ...e, [programId]: { ...e[programId], paid: false } }));
    } catch(e) { console.error(e); }
    setGrantingAccess(g => ({ ...g, [programId]: null }));
  };

  const handleApprove = async () => {
    setApproving(true);
    const paidAt = new Date().toISOString();
    try {
      // Access is per-program (enrollments), not the global users.paid flag. Find the
      // client's pending payment(s) and grant the matching enrollment for each — the
      // same thing the Payments tab and the AzamPay webhook do. Without this, approving
      // would set users.paid but grant no actual program access.
      const paySnap = await getDocs(query(
        collection(db, "payments"),
        where("userId", "==", client.id),
      ));
      // Programs grant an enrollment; masterclasses grant a registration (seat).
      const pending = paySnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.status === "pending" && (p.programId || p.masterclassId));

      if (pending.length === 0) {
        alert(`No pending payment found for ${client.name}. To grant access manually, open the Programs tab and use "Grant Access".`);
        setApproving(false);
        return;
      }

      const names = pending.map(p => p.programTitle || p.programId || p.masterclassId).join(", ");
      if (!window.confirm(`Approve ${client.name}'s payment and grant access to: ${names}?`)) {
        setApproving(false);
        return;
      }

      for (const p of pending) {
        if (p.kind === "masterclass" && p.masterclassId) {
          await setDoc(doc(db, "registrations", client.id, "masterclasses", p.masterclassId),
            { paid: true, paymentPending: false, paidAt, registeredAt: paidAt }, { merge: true });
        } else {
          const progSnap = await getDoc(doc(db, "programs", p.programId));
          const accessType = p.accessType || (progSnap.exists() ? (progSnap.data().accessType || "lifetime") : "lifetime");
          const enrollData = { paid: true, paymentPending: false, paidAt, enrolledAt: paidAt, accessType };
          const expiresAt = computeExpiresAt(accessType);
          if (expiresAt) enrollData.expiresAt = expiresAt;
          await setDoc(doc(db, "enrollments", client.id, "programs", p.programId), enrollData, { merge: true });
        }
        await setDoc(doc(db, "payments", p.id), { status: "approved", approvedAt: paidAt }, { merge: true });
      }

      // Manual approval = free/comp access: active for access, but no paidAt on the user
      // doc so it never counts as revenue (revenue = AzamPay payments with a transactionRef).
      await setDoc(doc(db, "users", client.id), { paid: true, paymentPending: false }, { merge: true });
      onApprove(client.id, { paid: true, paymentPending: false });
    } catch(e) { console.error(e); alert("Approval failed: " + (e?.message || e)); }
    setApproving(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "assessments", client.id));
        if (snap.exists()) setAssessment(snap.data());
      } catch(e) { console.error(e); }
    };
    load();
  }, [client.id]);

  const togglePrivacy = async () => {
    setSavingPrivacy(true);
    const newVal = !privacyEnabled;
    setPrivacyEnabled(newVal);
    await setDoc(doc(db, "users", client.id), { reflectionsPrivate: newVal }, { merge: true });
    setSavingPrivacy(false);
  };

  const handleSendMessage = async () => {
    setSendingMsg(true);
    try {
      await setDoc(doc(db, "users", client.id), { coachDirectMessage: coachMsg, coachDirectMessageAt: new Date().toISOString() }, { merge: true });
      setMsgSent(true);
      setTimeout(() => setMsgSent(false), 3000);
    } catch(e) { console.error(e); }
    setSendingMsg(false);
  };

  const inactiveDays = client.lastSeen
    ? Math.floor((Date.now() - new Date(client.lastSeen).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const CATEGORY_LABELS = {
    grounding: "Grounding", past: "Releasing Past",
    selflove: "Self Love", future: "Future Connection"
  };

  // Overview card data (uses progress-tab load once available; falls back to merged totals)
  const overviewProg = progPrograms ? (progPrograms.find(p => p.paid) || progPrograms[0] || null) : null;
  const ovDone = overviewProg ? overviewProg.sessions.filter(s => overviewProg.progress[s.id]).length : done;
  const ovTotal = overviewProg ? overviewProg.sessions.length : total;
  const ovPct = ovTotal > 0 ? Math.round((ovDone / ovTotal) * 100) : Math.min(done * 10, 100);
  const topCat = assessment && assessment.scores ? Object.entries(assessment.scores).sort((a, b) => b[1] - a[1])[0] : null;
  const initials = (client.name || client.email || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const seenText = inactiveDays === null ? "Never" : inactiveDays === 0 ? "Today" : inactiveDays === 1 ? "Yesterday" : `${inactiveDays}d ago`;

  return (
    <div className="overlay" onClick={onClose}>
      <div className="detail-panel" onClick={e=>e.stopPropagation()}>
        <button className="detail-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="detail-header">
          <div className="detail-avatar">{initials}</div>
          <div style={{minWidth:0}}>
            <h2 className="detail-name">{client.name||"Unknown"}</h2>
            <p className="detail-sub">{client.email||"—"}{client.phone?` · ${client.phone}`:""}</p>
          </div>
          <span className="ml">{clientStatusBadge(client, completed)}</span>
        </div>

        {/* Quick actions — the common things in one click, not buried in tabs */}
        <div className="detail-quick">
          {client.paymentPending && !client.paid && (
            <button className="btn-sm btn-sm--ok" onClick={handleApprove} disabled={approving}>{approving ? "Approving…" : "✓ Approve payment"}</button>
          )}
          {waNumber(client.phone) && (
            <a className="btn-sm" href={`https://wa.me/${waNumber(client.phone)}`} target="_blank" rel="noopener noreferrer" style={{ color: "#25D366", borderColor: "#25D366" }}>
              <WhatsAppGlyph size={13} />
              WhatsApp
            </a>
          )}
          <button className="btn-sm btn-sm--primary" onClick={()=>setDetailTab("message")}>
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5A6 6 0 002 9.5c0 1.2.35 2.3.95 3.2L2 14l1.8-.9A6 6 0 0014 9.5z"/></svg>
            Message
          </button>
          <button className="btn-sm" onClick={()=>setDetailTab("programs")}>Manage access</button>
        </div>

        {/* At-a-glance overview */}
        <div className="overview-card">
          <div className="ov-top">
            <span className="ov-prog-name">{overviewProg ? overviewProg.title : (client.activeProgramTitle || "No program yet")}</span>
            <span style={{fontSize:".74rem",color:"var(--stone)"}}>{ovTotal>0 ? `${ovDone}/${ovTotal} · ${ovPct}%` : `${done} done`}</span>
          </div>
          <ProgressBar pct={ovPct} />
          <div className="ov-grid">
            <div className="ov-cell"><b>{done}</b><span>Sessions done</span></div>
            <div className="ov-cell"><b className={inactiveDays!==null&&inactiveDays>=3?"at-risk":""}>{seenText}</b><span>Last seen</span></div>
            <div className="ov-cell"><b>{client.joinedAt?new Date(client.joinedAt).toLocaleDateString("en-GB",{day:"numeric",month:"short"}):"—"}</b><span>Joined</span></div>
            <div className="ov-cell"><b>{topCat?(CATEGORY_LABELS[topCat[0]]||topCat[0]):"—"}</b><span>Top focus</span></div>
          </div>
        </div>

        {/* TABS */}
        <div className="detail-tabs">
          {[
            ["progress",<><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginRight:4,verticalAlign:"middle"}}><line x1="2" y1="14" x2="14" y2="14"/><rect x="2.5" y="9" width="3" height="5" rx="0.5"/><rect x="6.5" y="5" width="3" height="9" rx="0.5"/><rect x="10.5" y="2" width="3" height="12" rx="0.5"/></svg>Progress</>],
            ["assessment",<><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginRight:4,verticalAlign:"middle"}}><rect x="3" y="2" width="10" height="13" rx="1"/><path d="M6 2a2 2 0 004 0"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="5" y1="11" x2="9" y2="11"/></svg>Assessment</>],
            ["journals",<><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginRight:4,verticalAlign:"middle"}}><rect x="3" y="1" width="10" height="14" rx="1"/><path d="M5 5h6M5 8h6M5 11h4"/></svg>Journals</>],
            ["message",<><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginRight:4,verticalAlign:"middle"}} strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5A6 6 0 002 9.5c0 1.2.35 2.3.95 3.2L2 14l1.8-.9A6 6 0 0014 9.5z"/></svg>Message</>],
            ["programs",<><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginRight:4,verticalAlign:"middle"}}><rect x="2" y="3" width="12" height="10" rx="1"/><path d="M6 3V2M10 3V2M2 7h12"/></svg>Programs</>],
          ].map(([id,label])=>(
            <button key={id} className={`detail-tab-btn ${detailTab===id?"active":""}`} onClick={()=>setDetailTab(id)}>{label}</button>
          ))}
        </div>

        {detailTab === "progress" && (
          <div className="session-progress-list">
            {progPrograms === null && <p className="no-entry">Loading progress…</p>}
            {progPrograms && progPrograms.length === 0 && (
              <p className="no-entry">This client is not enrolled in any program yet.</p>
            )}
            {progPrograms && progPrograms.map(prog => {
              const doneCount = prog.sessions.filter(s => prog.progress[s.id]).length;
              const tot = prog.sessions.length;
              const ppct = tot > 0 ? Math.round((doneCount / tot) * 100) : 0;
              return (
                <div key={prog.id} className="progress-program">
                  <div className="progress-prog-head">
                    <span className="progress-prog-title">{prog.title}{!prog.paid && <span className="ps-not-started" style={{marginLeft:8}}>No access</span>}</span>
                    <span className="progress-prog-count">{doneCount}/{tot} · {ppct}%</span>
                  </div>
                  <div className="mini-bar" style={{margin:"6px 0 10px"}}><div className="mini-fill" style={{width:`${ppct}%`}}/></div>
                  <div className="progress-sessions">
                    {tot === 0 && <span className="no-entry">No sessions in this program yet.</span>}
                    {prog.sessions.map(s => {
                      const entry = prog.progress[s.id];
                      const uploaded = !!s.audioUrl;
                      return (
                        <div key={s.id} className="progress-session-item">
                          {s.thumbnailUrl
                            ? <img src={s.thumbnailUrl} alt="" style={{width:28,height:28,borderRadius:4,objectFit:"cover",flexShrink:0}}/>
                            : <span className="ps-icon">◆</span>}
                          <span className="ps-label">{s.name || s.title || "Session"}</span>
                          {!uploaded && <span className="ps-no-file">No file</span>}
                          {uploaded && !entry && <span className="ps-not-started">Not started</span>}
                          {uploaded && entry && (
                            <span className="ps-done">✓ Done{entry.completedAt ? ` · ${new Date(entry.completedAt).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}` : ""}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {detailTab === "assessment" && (
          <div className="assessment-tab">
            {!assessment ? (
              <p className="no-entry">This client has not completed the assessment yet.</p>
            ) : (
              <>
                <div className="assess-completed-at">
                  Completed {new Date(assessment.completedAt).toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"})}
                  {" · "}{assessment.lang === "sw" ? "Swahili" : "English"}
                </div>
                <div className="assess-scores-list">
                  {Object.entries(assessment.scores || {}).map(([cat, score]) => {
                    const pct = (score / 5) * 100;
                    const color = score >= 4 ? "#E8745A" : score >= 2 ? "#D4A849" : "#6A9E6A";
                    const label = score >= 4 ? "Needs attention" : score >= 2 ? "Growing" : "Doing well";
                    return (
                      <div key={cat} className="assess-score-row">
                        <div className="assess-score-left">
                          <span className="assess-cat-name">{CATEGORY_LABELS[cat] || cat}</span>
                          <span className="assess-cat-label" style={{color}}>{label}</span>
                        </div>
                        <div className="assess-score-right">
                          <div className="assess-bar"><div className="assess-bar-fill" style={{width:`${pct}%`,background:color}}/></div>
                          <span className="assess-score-num" style={{color}}>{score}<span className="assess-out-of">/5</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="assess-total">
                  Total: <strong>{Object.values(assessment.scores||{}).reduce((a,b)=>a+b,0)}/20</strong>
                </div>
              </>
            )}
          </div>
        )}

        {detailTab === "journals" && (
          <div>
            {/* PRIVACY TOGGLE */}
            <div className="privacy-toggle-row">
              <div>
                <span className="privacy-label">Reflections visible to coach</span>
                <span className="privacy-sub">{privacyEnabled ? "Client has hidden their reflections" : "You can see this client's reflections"}</span>
              </div>
              <button className={`privacy-toggle-btn ${privacyEnabled?"off":"on"}`} onClick={togglePrivacy} disabled={savingPrivacy}>
                {privacyEnabled ? "Hidden" : "Visible"}
              </button>
            </div>
            {privacyEnabled
              ? <p className="no-entry" style={{marginTop:12}}>This client has chosen to keep their reflections private.</p>
              : <ClientJournals client={client} program={program} />
            }
          </div>
        )}

        {detailTab === "message" && (
          <div style={{padding:"12px 0"}}>
            <p style={{fontSize:"0.82rem",color:"#4F9CA8",marginBottom:12,lineHeight:1.6}}>This message appears at the top of the client's program page. Use it for personal encouragement or updates.</p>
            <textarea
              value={coachMsg}
              onChange={e => setCoachMsg(e.target.value)}
              placeholder="Write a personal message for this client…"
              rows={5}
              style={{width:"100%",padding:"12px",border:"1px solid #D6E2E5",borderRadius:10,fontFamily:"'Inter',sans-serif",fontSize:"0.88rem",resize:"vertical",background:"white",color:"#1E2E33"}}
            />
            <button
              onClick={handleSendMessage}
              disabled={sendingMsg || !coachMsg.trim()}
              style={{marginTop:10,background:"#1F4E5A",color:"white",border:"none",borderRadius:9,padding:"11px 24px",fontFamily:"'Inter',sans-serif",fontSize:"0.88rem",cursor:"pointer",opacity:sendingMsg||!coachMsg.trim()?0.5:1}}
            >
              {sendingMsg ? "Saving…" : msgSent ? "✓ Sent!" : "Send Message"}
            </button>
          </div>
        )}

        {detailTab === "programs" && (
          <div style={{padding:"12px 0"}}>
            <p style={{fontSize:"0.82rem",color:"#4F9CA8",marginBottom:16,lineHeight:1.6}}>Manually grant or revoke a client's access to any program — no payment required.</p>
            {clientPrograms.length === 0 && <p className="no-entry">Loading programs…</p>}
            {clientPrograms.map(prog => {
              const enrollment = clientEnrollments[prog.id];
              const enrolled = enrollment?.paid === true;
              const expired = enrolled && enrollment?.expiresAt && new Date(enrollment.expiresAt) < new Date();
              const busy = grantingAccess[prog.id];
              return (
                <div key={prog.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:"white",border:"1px solid #E3EEF0",borderRadius:10,marginBottom:10}}>
                  <div>
                    <div style={{fontFamily:"'Plus Jakarta Sans',serif",fontSize:"1rem",fontWeight:600,color:"#1E2E33"}}>{prog.title || prog.name || prog.id}</div>
                    <div style={{fontSize:"0.78rem",color:"#A0907A",marginTop:2}}>
                      {enrolled && !expired
                        ? `${enrollment?.grantedByAdmin ? "Admin" : "Paid"} access${enrollment?.expiresAt ? ` · expires ${new Date(enrollment.expiresAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}` : " · Lifetime"}`
                        : expired ? "Access expired" : "No access"}
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {enrolled && !expired && <span style={{background:"#E8F5E9",color:"#388E3C",fontSize:"0.75rem",fontWeight:600,padding:"3px 10px",borderRadius:20}}>Enrolled</span>}
                      {expired && <span style={{background:"#FDE8E8",color:"#C0392B",fontSize:"0.75rem",fontWeight:600,padding:"3px 10px",borderRadius:20}}>Expired</span>}
                      {(!enrolled || expired) ? (
                        <button onClick={() => grantAccess(prog)} disabled={!!busy}
                          style={{background:"#1F4E5A",color:"white",border:"none",borderRadius:8,padding:"8px 14px",fontFamily:"'Inter',sans-serif",fontSize:"0.82rem",cursor:"pointer",opacity:busy?0.5:1}}>
                          {busy === "granting" ? "Granting…" : expired ? "Renew" : "Grant Access"}
                        </button>
                      ) : (
                        <>
                          <button onClick={() => { setEditingExpiry(prog.id); setExpiryInput(enrollment?.expiresAt ? new Date(enrollment.expiresAt).toISOString().slice(0,10) : ''); }}
                            style={{background:"white",color:"#1F4E5A",border:"1px solid #1F4E5A",borderRadius:8,padding:"8px 14px",fontFamily:"'Inter',sans-serif",fontSize:"0.82rem",cursor:"pointer"}}>
                            Edit Expiry
                          </button>
                          <button onClick={() => revokeAccess(prog.id)} disabled={!!busy}
                            style={{background:"white",color:"#C0392B",border:"1px solid #C0392B",borderRadius:8,padding:"8px 14px",fontFamily:"'Inter',sans-serif",fontSize:"0.82rem",cursor:"pointer",opacity:busy?0.5:1}}>
                            {busy === "revoking" ? "Revoking…" : "Revoke"}
                          </button>
                        </>
                      )}
                    </div>
                    {editingExpiry === prog.id && (
                      <div style={{display:"flex",alignItems:"center",gap:8,background:"#F0F6F7",border:"1px solid #E3EEF0",borderRadius:10,padding:"10px 12px"}}>
                        <div>
                          <div style={{fontSize:"0.75rem",color:"#4F9CA8",marginBottom:4}}>Set expiry date (leave blank for lifetime)</div>
                          <input type="date" value={expiryInput} onChange={e=>setExpiryInput(e.target.value)}
                            style={{border:"1px solid #D6E2E5",borderRadius:6,padding:"6px 10px",fontFamily:"'Inter',sans-serif",fontSize:"0.85rem",color:"#1E2E33"}} />
                        </div>
                        <button onClick={() => saveExpiry(prog.id)}
                          style={{background:"#1F4E5A",color:"white",border:"none",borderRadius:7,padding:"8px 14px",fontFamily:"'Inter',sans-serif",fontSize:"0.82rem",cursor:"pointer"}}>
                          Save
                        </button>
                        <button onClick={() => setEditingExpiry(null)}
                          style={{background:"white",color:"#4F9CA8",border:"1px solid #D6E2E5",borderRadius:7,padding:"8px 14px",fontFamily:"'Inter',sans-serif",fontSize:"0.82rem",cursor:"pointer"}}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FEEDBACK TAB ────────────────────────────────────────────────────────────
function FeedbackTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(collection(db, "feedback"))
      .then(snap => {
        const items = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));
        setEntries(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const avgRating = entries.length
    ? (entries.reduce((s, e) => s + (e.rating || 0), 0) / entries.length).toFixed(1)
    : "—";

  const stars = (r) => "★".repeat(r) + "☆".repeat(5 - r);

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div>
          <h1 className="tab-title">Client Feedback</h1>
          <p className="tab-sub">Overall feedback on the Brand New Me program.</p>
        </div>
        <div className="header-stats">
          <div className="hstat"><span className="hstat-val">{entries.length}</span><span className="hstat-lab">Responses</span></div>
          <div className="hstat"><span className="hstat-val">{avgRating}</span><span className="hstat-lab">Avg rating</span></div>
        </div>
      </div>
      {loading && <div className="empty-state">Loading…</div>}
      {!loading && entries.length === 0 && <div className="empty-state">No feedback submitted yet.</div>}
      <div className="feedback-list">
        {entries.map(e => (
          <div key={e.id} className="feedback-entry">
            <div className="fe-header">
              <div className="fe-user">
                <span className="fe-name">{e.userName || e.userEmail || "Anonymous"}</span>
                <span className="fe-date">{e.submittedAt ? new Date(e.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
              </div>
              <span className="fe-stars" title={`${e.rating}/5`}>{stars(e.rating || 0)}</span>
            </div>
            {e.text && (
              <div className="fe-block">
                <span className="fe-label">What was valuable</span>
                <p className="fe-text">{e.text}</p>
              </div>
            )}
            {e.improve && (
              <div className="fe-block">
                <span className="fe-label">What to improve</span>
                <p className="fe-text">{e.improve}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── JOURNAL ADMIN TAB ────────────────────────────────────────────────────────
function JournalAdminTab({ clients, onSelectClient }) {
  const paid = clients.filter(c => c.paid);
  return (
    <div className="tab-content">
      <div className="tab-header">
        <div>
          <h1 className="tab-title">Client Reflections</h1>
          <p className="tab-sub">Select a client to read and respond to their journal entries.</p>
        </div>
        <div className="header-stats">
          <div className="hstat"><span className="hstat-val">{paid.length}</span><span className="hstat-lab">Active clients</span></div>
        </div>
      </div>
      <div className="clients-table">
        <div className="clients-thead"><span/><span>Client</span><span>Program</span><span>Status</span><span>Progress</span><span/></div>
        {paid.length === 0 && <div className="empty-state">No active clients yet.</div>}
        {paid.map(c => <ClientRow key={c.id} client={c} selectable={false} onOpen={onSelectClient} />)}
      </div>
    </div>
  );
}

// ─── COACH PROGRAMS MANAGER ───────────────────────────────────────────────────
function CoachProgramsManager() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editDesc, setEditDesc] = useState("");

  useEffect(() => { loadPrograms(); }, []);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "coachPrograms"));
      setPrograms(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const ref = await addDoc(collection(db, "coachPrograms"), {
        title: newTitle.trim(), url: newUrl.trim(), description: newDesc.trim(),
        active: true, order: programs.length, createdAt: new Date().toISOString(),
      });
      setPrograms(prev => [...prev, { id: ref.id, title: newTitle.trim(), url: newUrl.trim(), description: newDesc.trim(), active: true, order: prev.length }]);
      setNewTitle(""); setNewUrl(""); setNewDesc("");
    } catch (e) { console.error(e); }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this program link?")) return;
    try { await deleteDoc(doc(db, "coachPrograms", id)); setPrograms(prev => prev.filter(p => p.id !== id)); }
    catch (e) { console.error(e); }
  };

  const handleToggleActive = async (p) => {
    const newActive = !p.active;
    try {
      await setDoc(doc(db, "coachPrograms", p.id), { active: newActive }, { merge: true });
      setPrograms(prev => prev.map(x => x.id === p.id ? { ...x, active: newActive } : x));
    } catch (e) { console.error(e); }
  };

  const startEdit = (p) => { setEditingId(p.id); setEditTitle(p.title||""); setEditUrl(p.url||""); setEditDesc(p.description||""); };

  const handleSaveEdit = async () => {
    try {
      await setDoc(doc(db, "coachPrograms", editingId), { title: editTitle.trim(), url: editUrl.trim(), description: editDesc.trim() }, { merge: true });
      setPrograms(prev => prev.map(p => p.id === editingId ? { ...p, title: editTitle.trim(), url: editUrl.trim(), description: editDesc.trim() } : p));
      setEditingId(null);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div>
          <h1 className="tab-title">Other Programs</h1>
          <p className="tab-sub">Manage links to Coach Elibariki's other programs. These appear on the client's Day 10 completion screen.</p>
        </div>
        <div className="header-stats">
          <div className="hstat"><span className="hstat-val">{programs.filter(p=>p.active).length}</span><span className="hstat-lab">Active</span></div>
          <div className="hstat"><span className="hstat-val">{programs.length}</span><span className="hstat-lab">Total</span></div>
        </div>
      </div>

      {loading ? <div className="empty-state">Loading…</div> : (<>
        <div className="programs-list">
          {programs.length === 0 && <div className="empty-state">No programs added yet. Use the form below to add the first one.</div>}
          {programs.map(p => (
            <div key={p.id} className={`program-link-row ${p.active ? "" : "pl-inactive"}`}>
              {editingId === p.id ? (
                <div className="program-link-edit">
                  <input className="pl-input" value={editTitle} onChange={e=>setEditTitle(e.target.value)} placeholder="Program title *" />
                  <input className="pl-input" value={editUrl} onChange={e=>setEditUrl(e.target.value)} placeholder="URL (https://…)" />
                  <input className="pl-input" value={editDesc} onChange={e=>setEditDesc(e.target.value)} placeholder="Short description (optional)" />
                  <div className="pl-edit-actions">
                    <button className="btn-save-pl" onClick={handleSaveEdit}>Save</button>
                    <button className="btn-cancel-pl" onClick={()=>setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (<>
                <div className="pl-info">
                  <span className="pl-title">{p.title}</span>
                  {p.description && <span className="pl-desc">{p.description}</span>}
                  {p.url && <a className="pl-url" href={p.url} target="_blank" rel="noreferrer">{p.url}</a>}
                </div>
                <div className="pl-actions">
                  <button className={`pl-toggle ${p.active?"pl-on":"pl-off"}`} onClick={()=>handleToggleActive(p)}>{p.active?"Active":"Hidden"}</button>
                  <button className="pl-edit-btn" onClick={()=>startEdit(p)}>Edit</button>
                  <button className="pl-delete" onClick={()=>handleDelete(p.id)}>✕</button>
                </div>
              </>)}
            </div>
          ))}
        </div>

        <div className="program-link-add">
          <h3 className="pla-title">Add a program link</h3>
          <div className="pla-fields">
            <input className="pl-input" value={newTitle} onChange={e=>setNewTitle(e.target.value)} placeholder="Program title *" />
            <input className="pl-input" value={newUrl} onChange={e=>setNewUrl(e.target.value)} placeholder="URL (https://…)" />
            <input className="pl-input" value={newDesc} onChange={e=>setNewDesc(e.target.value)} placeholder="Short description (optional)" />
            <button className="btn-add-pl" onClick={handleAdd} disabled={!newTitle.trim()||adding}>{adding?"Adding…":"Add Program"}</button>
          </div>
        </div>
      </>)}
    </div>
  );
}

// ─── ACCESS DENIED ────────────────────────────────────────────────────────────
function AccessDenied({ onSignOut }) {
  return (
    <div className="access-denied-screen"><style>{CSS}</style>
      <div className="access-denied-card">
        <svg viewBox="0 0 32 32" width="36" height="36" fill="none"><circle cx="16" cy="16" r="14" stroke="#7FB8C2" strokeWidth="1.5"/><circle cx="16" cy="16" r="5" fill="#7FB8C2"/></svg>
        <h2 className="access-denied-title">Access Denied</h2>
        <p className="access-denied-msg">Your account does not have admin access. Contact the main administrator to be granted access.</p>
        <button className="btn-login" style={{maxWidth:200,margin:"0 auto"}} onClick={onSignOut}>Sign Out</button>
      </div>
    </div>
  );
}

// ─── COACHES MANAGER ──────────────────────────────────────────────────────────
function CoachesManager({ clients }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    getDocs(collection(db, "coaches"))
      .then(snap => setCoaches(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const suggestions = search.trim().length > 0
    ? clients.filter(c => {
        if (coaches.find(co => co.id === c.id)) return false;
        const q = search.toLowerCase();
        return (c.name||"").toLowerCase().includes(q) || (c.email||"").toLowerCase().includes(q);
      }).slice(0, 6)
    : [];

  const handleSelect = (client) => { setSelectedUser(client); setSearch(""); setErr(""); };

  const handleAdd = async () => {
    if (!selectedUser) { setErr("Select a user first."); return; }
    if (coaches.find(c => c.id === selectedUser.id)) { setErr("This user is already a coach."); return; }
    setSaving(true); setErr("");
    try {
      const name = selectedUser.name || selectedUser.email;
      const addedAt = new Date().toISOString();
      await Promise.all([
        setDoc(doc(db, "coaches", selectedUser.id), { name, canUpload: true, addedAt }),
        setDoc(doc(db, "admins", selectedUser.id), { name, grantedAt: addedAt }),
      ]);
      setCoaches(prev => [...prev, { id: selectedUser.id, name, canUpload: true }]);
      setSelectedUser(null);
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const toggleUpload = async (coach) => {
    const next = !coach.canUpload;
    await setDoc(doc(db, "coaches", coach.id), { canUpload: next }, { merge: true });
    setCoaches(prev => prev.map(c => c.id === coach.id ? { ...c, canUpload: next } : c));
  };

  const handleRemove = async (id) => {
    await Promise.all([
      deleteDoc(doc(db, "coaches", id)),
      deleteDoc(doc(db, "admins", id)),
    ]);
    setCoaches(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="tab-content">
      <h2 className="tab-title">Coaches</h2>
      <p className="tab-sub">Coaches have full admin access. Remove a coach to revoke it.</p>

      {loading ? <p className="loading-msg">Loading…</p> : (
        <div className="coaches-list">
          {coaches.length === 0 && <p className="no-coaches">No coaches added yet.</p>}
          {coaches.map(coach => (
            <div key={coach.id} className="coach-row">
              <div className="coach-info">
                <span className="coach-name">{coach.name || "Unnamed"}</span>
                <span className="coach-uid">{coach.id}</span>
              </div>
              <div className="coach-actions">
                <button className={`coach-toggle ${coach.canUpload ? "on" : "off"}`} onClick={() => toggleUpload(coach)}
                  title={coach.canUpload ? "Revoke upload access" : "Grant upload access"}>
                  {coach.canUpload ? "Upload: ON" : "Upload: OFF"}
                </button>
                <button className="coach-remove" onClick={() => handleRemove(coach.id)} title="Remove coach">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="coach-add">
        <h3 className="coach-add-title">Add a coach</h3>

        {selectedUser ? (
          <div className="coach-selected-user">
            <div className="csu-avatar">{(selectedUser.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
            <div className="csu-info">
              <span className="csu-name">{selectedUser.name}</span>
              <span className="csu-email">{selectedUser.email}</span>
            </div>
            <button className="csu-clear" onClick={() => setSelectedUser(null)}>✕</button>
          </div>
        ) : (
          <div className="coach-search-wrap">
            <input className="coach-input" placeholder="Search by name or email…" value={search}
              onChange={e => { setSearch(e.target.value); setErr(""); }} autoComplete="off" />
            {suggestions.length > 0 && (
              <div className="coach-suggestions">
                {suggestions.map(c => (
                  <button key={c.id} className="coach-suggestion-row" onClick={() => handleSelect(c)}>
                    <div className="cs-avatar">{(c.name||"?").split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
                    <div className="cs-info">
                      <span className="cs-name">{c.name}</span>
                      <span className="cs-email">{c.email}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {search.trim().length > 1 && suggestions.length === 0 && (
              <p className="coach-no-results">No matching users found.</p>
            )}
          </div>
        )}

        {err && <p className="coach-err">{err}</p>}
        <button className="btn-add-coach" onClick={handleAdd} disabled={saving || !selectedUser}>
          {saving ? "Saving…" : "Add as Coach"}
        </button>
      </div>
    </div>
  );
}

// ─── REVENUE PANEL ────────────────────────────────────────────────────────────
const PRICE = 5000;

function RevenuePanel({ clients }) {
  const paid = clients.filter(c => c.paid && c.paidAt);
  const now = new Date();

  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday
  startOfWeek.setHours(0, 0, 0, 0);

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const thisWeek  = paid.filter(c => new Date(c.paidAt) >= startOfWeek);
  const thisMonth = paid.filter(c => new Date(c.paidAt) >= startOfMonth);

  const months = Array.from({ length: 6 }, (_, i) => {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end   = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
    const count = paid.filter(c => { const d = new Date(c.paidAt); return d >= start && d <= end; }).length;
    return {
      label: start.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
      count, revenue: count * PRICE,
    };
  });

  const maxCount = Math.max(...months.map(m => m.count), 1);

  const fmt = (n) => `TZS ${(n).toLocaleString()}`;

  return (
    <div className="revenue-panel">
      <div className="revenue-cards">
        {[
          { label: "All-time",   val: paid.length,        color: "rev-all"   },
          { label: "This month", val: thisMonth.length,   color: "rev-month" },
          { label: "This week",  val: thisWeek.length,    color: "rev-week"  },
        ].map(({ label, val, color }) => (
          <div key={label} className={`rev-card ${color}`}>
            <span className="rev-amount">{fmt(val * PRICE)}</span>
            <span className="rev-label">{label}</span>
            <span className="rev-clients">{val} {val === 1 ? "client" : "clients"}</span>
          </div>
        ))}
      </div>

      <div className="rev-breakdown">
        <p className="rev-breakdown-title">Monthly breakdown</p>
        {months.map(m => (
          <div key={m.label} className="rev-row">
            <span className="rev-month">{m.label}</span>
            <div className="rev-bar-track">
              <div className="rev-bar-fill" style={{ width: `${(m.count / maxCount) * 100}%` }} />
            </div>
            <span className="rev-row-clients">{m.count}</span>
            <span className="rev-row-amount">{fmt(m.revenue)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── NEW PROGRAM MODAL ────────────────────────────────────────────────────────
const SESSION_ICONS = ["◆","●","▲","◉","◇","○","△","▪","◎","✦","▸","◈","▹","◃","▫","◁","◦","▾"];

function NewProgramModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [coach, setCoach] = useState("");
  const [count, setCount] = useState(5);
  const [sessionNames, setSessionNames] = useState(["Session 1","Session 2","Session 3","Session 4","Session 5"]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const handleCountChange = (val) => {
    const n = Math.min(50, Math.max(1, Number(val) || 1));
    setCount(n);
    setSessionNames(prev => {
      const next = [...prev];
      while (next.length < n) next.push(`Session ${next.length + 1}`);
      return next.slice(0, n);
    });
  };

  const goToStep2 = () => {
    if (!name.trim()) { setErr("Program name is required."); return; }
    setErr(""); setStep(2);
  };

  const handleCreate = async () => {
    setErr(""); setSaving(true);
    try {
      const slug = name.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const programId = `${slug}-${Date.now()}`;
      const sessions = sessionNames.map((label, i) => ({
        id: `${programId}-s${i + 1}`,
        label: label.trim() || `Session ${i + 1}`,
        icon: SESSION_ICONS[i % SESSION_ICONS.length],
        storagePath: `programs/${programId}/session${i + 1}`,
      }));
      await setDoc(doc(db, "customPrograms", programId), {
        name: name.trim(), coach: coach.trim(), sessions,
        createdAt: new Date().toISOString(),
      });
      onCreated({ id: programId, name: name.trim(), coach: coach.trim(), sessions });
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{step === 1 ? "New Program" : "Name Your Sessions"}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {step === 1 ? (
          <div className="modal-body">
            <div className="mf-group">
              <label className="mf-label">Program name *</label>
              <input className="mf-input" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Soul Cleansing" autoFocus onKeyDown={e => e.key === "Enter" && goToStep2()} />
            </div>
            <div className="mf-group">
              <label className="mf-label">Coach name</label>
              <input className="mf-input" value={coach} onChange={e => setCoach(e.target.value)}
                placeholder="e.g. Coach Elibariki" />
            </div>
            <div className="mf-group">
              <label className="mf-label">Number of sessions</label>
              <input className="mf-input mf-count" type="number" min="1" max="50"
                value={count} onChange={e => handleCountChange(e.target.value)} />
            </div>
            {err && <p className="modal-err">{err}</p>}
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={onClose}>Cancel</button>
              <button className="btn-modal-next" onClick={goToStep2}>Next →</button>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <p className="modal-step-hint">Give each session a name. You can use defaults and rename later.</p>
            <div className="session-names-list">
              {sessionNames.map((label, i) => (
                <div key={i} className="sn-row">
                  <span className="sn-num">{i + 1}</span>
                  <span className="sn-icon">{SESSION_ICONS[i % SESSION_ICONS.length]}</span>
                  <input className="mf-input sn-input" value={label}
                    onChange={e => setSessionNames(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                    placeholder={`Session ${i + 1}`} />
                </div>
              ))}
            </div>
            {err && <p className="modal-err">{err}</p>}
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setStep(1)}>← Back</button>
              <button className="btn-modal-save" onClick={handleCreate} disabled={saving}>
                {saving ? "Creating…" : "Create Program"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PROGRAMS TAB ─────────────────────────────────────────────────────────────
function ProgramsTab() {
  const [programs, setPrograms] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  // Persist the open program so a page refresh returns here instead of Home.
  const [editingId, setEditingId] = useState(() => { try { return localStorage.getItem('adminEditingProgram') || null; } catch { return null; } });
  useEffect(() => {
    try { if (editingId) localStorage.setItem('adminEditingProgram', editingId); else localStorage.removeItem('adminEditingProgram'); } catch {}
  }, [editingId]);

  const loadPrograms = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, 'programs'));
    const all = snap.docs.map(d => ({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999));
    setPrograms(all.filter(p => p.archived !== true));
    setArchived(all.filter(p => p.archived === true));
    setLoading(false);
  };
  useEffect(() => { loadPrograms(); }, []);

  // Soft delete → archived (recycle bin). Restore/permanent-delete live in Archived.
  const archiveProgram = async (p) => {
    if (!window.confirm(`Archive "${p.title}"? It's hidden from clients and the list, but you can restore it from Archived.`)) return;
    await setDoc(doc(db,'programs',p.id), { archived:true, archivedAt:new Date().toISOString() }, { merge:true });
    loadPrograms();
  };
  const restoreProgram = async (p) => {
    await setDoc(doc(db,'programs',p.id), { archived:false }, { merge:true });
    loadPrograms();
  };
  const deleteForever = async (p) => {
    if (!window.confirm(`Permanently delete "${p.title}"? This cannot be undone.`)) return;
    await deleteDoc(doc(db,'programs',p.id));
    loadPrograms();
  };

  if (editingId !== null) return <ProgramBuilder programId={editingId} onBack={() => { setEditingId(null); loadPrograms(); }} />;

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div><h1 className="tab-title">Programs</h1><p className="tab-sub">Create and manage client programs. Tap a program to edit it. Each appears in the client catalog once published.</p></div>
        <button className="btn-upload" onClick={() => setEditingId('new')}>+ New Program</button>
      </div>
      {loading ? <div className="empty-state">Loading…</div> : programs.length === 0 ? (
        <div className="empty-state">No programs yet. Create your first one above.</div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {programs.map(p => (
            <div key={p.id} onClick={() => setEditingId(p.id)}
              onMouseEnter={() => setHoveredId(p.id)} onMouseLeave={() => setHoveredId(null)}
              style={{background:'white',borderRadius:12,border:`1px solid ${hoveredId===p.id?'var(--stone)':'var(--border)'}`,padding:'16px 20px',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',cursor:'pointer',transition:'border-color .15s'}}>
              {p.coverImageUrl
                ? <img src={p.coverImageUrl} style={{width:64,height:64,objectFit:'cover',borderRadius:8,flexShrink:0}} alt="" />
                : <div style={{width:64,height:64,background:'var(--parchment)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg viewBox="0 0 32 32" width="28" height="28" fill="none" stroke="#4F9CA8" strokeWidth="1.5" strokeLinecap="round"><path d="M6 26s-2-14 10-22c0 0 2 14-10 22z"/><line x1="6" y1="26" x2="14" y2="18"/></svg></div>
              }
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:500,color:'var(--text)',marginBottom:2}}>{p.title}</div>
                {p.tagline && <div style={{fontSize:'.78rem',color:'var(--stone)',marginBottom:4}}>{p.tagline}</div>}
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <span style={{fontSize:'.72rem',color:'var(--stone)'}}>{p.price ? `TZS ${Number(p.price).toLocaleString()}` : 'Free'}</span>
                  <span style={{fontSize:'.72rem',background:p.published?'#D4E8CF':'var(--parchment)',color:p.published?'var(--moss)':'var(--stone)',padding:'1px 8px',borderRadius:20}}>{p.published?'Published':'Draft'}</span>
                </div>
              </div>
              <div style={{display:'flex',gap:8,flexShrink:0}} onClick={e => e.stopPropagation()}>
                <button className="btn-upload btn-replace" onClick={() => setEditingId(p.id)}>Edit</button>
                <button className="btn-delete" onClick={() => archiveProgram(p)}>Archive</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archived (recycle bin) */}
      {archived.length > 0 && (
        <div style={{marginTop:22}}>
          <button onClick={() => setShowArchived(s => !s)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--stone)',fontSize:'.8rem',fontWeight:600,display:'flex',alignItems:'center',gap:6,padding:'6px 0'}}>
            {showArchived ? '▾' : '▸'} Archived ({archived.length})
          </button>
          {showArchived && (
            <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:8}}>
              {archived.map(p => (
                <div key={p.id} style={{background:'var(--cream)',borderRadius:12,border:'1px solid var(--border)',padding:'12px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',opacity:.9}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:500,color:'var(--text)'}}>{p.title}</div>
                    <div style={{fontSize:'.72rem',color:'var(--stone)'}}>Archived{p.archivedAt?` · ${new Date(p.archivedAt).toLocaleDateString()}`:''}</div>
                  </div>
                  <button className="btn-upload btn-replace" onClick={() => restoreProgram(p)}>Restore</button>
                  <button className="btn-delete" onClick={() => deleteForever(p)}>Delete permanently</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgramBuilder({ programId, onBack }) {
  const isNew = programId === 'new';
  // Persist the wizard step per program so a refresh mid-edit returns to the same step.
  const stepKey = 'adminBuilderStep:' + programId;
  const [step, setStep] = useState(() => { try { return Number(localStorage.getItem(stepKey)) || 1; } catch { return 1; } });
  useEffect(() => { try { localStorage.setItem(stepKey, String(step)); } catch {} }, [step, stepKey]);
  const [saving, setSaving] = useState(false);
  const [progId, setProgId] = useState(isNew ? null : programId);
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [discountActive, setDiscountActive] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [accessType, setAccessType] = useState('lifetime');
  const [enrollmentOpen, setEnrollmentOpen] = useState(true);
  const [hasAssessment, setHasAssessment] = useState(false);
  const [unlockSchedule, setUnlockSchedule] = useState('sequential');
  const [programSlug, setProgramSlug] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [captionBulk, setCaptionBulk] = useState('');
  const [exportingBook, setExportingBook] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setSessionsLoading(true);
    Promise.all([
      getDoc(doc(db,'programs',programId)),
      getDocs(collection(db,'programs',programId,'sessions')),
      getDocs(collection(db,'programs',programId,'groups')),
      getDocs(collection(db,'programs',programId,'testimonials')),
    ]).then(([progSnap,sessSnap,groupSnap,testSnap]) => {
      if (progSnap.exists()) {
        const d = progSnap.data();
        setTitle(d.title||''); setTagline(d.tagline||''); setDescription(d.description||'');
        setPrice(d.price?.toString()||''); setDiscountActive(!!d.discountActive); setSalePrice(d.salePrice?.toString()||''); setAccessType(d.accessType||'lifetime'); setEnrollmentOpen(d.enrollmentOpen !== false); setHasAssessment(d.hasAssessment||false);
        setUnlockSchedule(d.unlockSchedule||'sequential');
        setCoverUrl(d.coverImageUrl||''); setQuestions(d.assessmentQuestions||[]);
      }
      setSessions(sessSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999)));
      setGroups(groupSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999)));
      setTestimonials(testSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999)));
      setSessionsLoading(false);
    });
  }, [programId, isNew]);

  const saveBasicInfo = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const data = { title:title.trim(), tagline:tagline.trim(), description:description.trim(),
        price:price?Number(price):0, discountActive:!!discountActive, salePrice:salePrice?Number(salePrice):0,
        accessType, enrollmentOpen, hasAssessment, unlockSchedule, coverImageUrl:coverUrl, updatedAt:new Date().toISOString() };
      let pid = progId;
      if (!pid) {
        const slug = programSlug.trim() || title.trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'') || `prog-${Date.now()}`;
        await setDoc(doc(db,'programs',slug), {...data, published:false, createdAt:new Date().toISOString()});
        pid = slug;
        setProgId(slug);
      } else {
        await setDoc(doc(db,'programs',pid), data, {merge:true});
      }
      setSessionsLoading(true);
      const [sessSnap,groupSnap,testSnap] = await Promise.all([
        getDocs(collection(db,'programs',pid,'sessions')),
        getDocs(collection(db,'programs',pid,'groups')),
        getDocs(collection(db,'programs',pid,'testimonials')),
      ]);
      setSessions(sessSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999)));
      setGroups(groupSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999)));
      setTestimonials(testSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??9999)-(b.order??9999)));
      setSessionsLoading(false);
      setStep(2);
    } catch(err) {
      console.error('Failed to save program info:', err);
      alert('Save failed: ' + (err?.message || 'Unknown error. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const addSession = async () => {
    const id = `session-${Date.now()}`;
    const data = { name:`Session ${sessions.filter(isPlayableSession).length+1}`, type:'session', order:sessions.length, createdAt:new Date().toISOString() };
    await setDoc(doc(db,'programs',progId,'sessions',id), data);
    setSessions(p=>[...p,{id,...data}]);
  };

  const addReflection = async () => {
    const id = `reflection-${Date.now()}`;
    const data = { name:'Contemplation', type:'reflection', prompt:'', order:sessions.length, createdAt:new Date().toISOString() };
    await setDoc(doc(db,'programs',progId,'sessions',id), data);
    setSessions(p=>[...p,{id,...data}]);
  };

  // An interactive tool (calculator / quiz / worksheet) — either a link to a hosted
  // page or an uploaded self-contained .html. Not coursework: excluded from progress.
  const addInteractive = async () => {
    const id = `tool-${Date.now()}`;
    const data = { name:'Tool', type:'interactive', interactiveSource:'link', interactiveUrl:'',
      description:'', order:sessions.length, createdAt:new Date().toISOString() };
    await setDoc(doc(db,'programs',progId,'sessions',id), data);
    setSessions(p=>[...p,{id,...data}]);
  };

  const addGroup = async () => {
    const id = `group-${Date.now()}`;
    const data = { name:'New Group', order:groups.length, createdAt:new Date().toISOString() };
    await setDoc(doc(db,'programs',progId,'groups',id), data);
    setGroups(p=>[...p,{id,...data}]);
  };

  const updateSession = async (id, upd) => {
    setSessions(p=>p.map(s=>s.id===id?{...s,...upd}:s));
    await setDoc(doc(db,'programs',progId,'sessions',id), upd, {merge:true});
  };

  const deleteSession = async (id) => {
    // Clean up an uploaded tool file so deleting the session doesn't orphan it in Storage.
    const gone = sessions.find(s => s.id === id);
    if (gone?.interactivePath) {
      try { await deleteObject(ref(storage, gone.interactivePath)); } catch (_) { /* already gone */ }
    }
    await deleteDoc(doc(db,'programs',progId,'sessions',id));
    setSessions(p=>p.filter(s=>s.id!==id));
  };

  const deleteGroup = async (id) => {
    await deleteDoc(doc(db,'programs',progId,'groups',id));
    setGroups(p=>p.filter(g=>g.id!==id));
    sessions.filter(s=>s.groupId===id).forEach(s=>updateSession(s.id,{groupId:null}));
  };

  const reorderSession = async (idx, dir) => {
    const arr = [...sessions];
    const t = idx+dir;
    if (t<0||t>=arr.length) return;
    [arr[idx],arr[t]] = [arr[t],arr[idx]];
    await Promise.all(arr.map((s,i)=>setDoc(doc(db,'programs',progId,'sessions',s.id),{order:i},{merge:true})));
    setSessions(arr.map((s,i)=>({...s,order:i})));
  };

  // Fire the caption pipeline for every media session in this course (background;
  // each finishes on its own — reopen a session to see its status).
  // Generate captions for the whole course SEQUENTIALLY — wait for each session to
  // finish before starting the next, so only one runs at a time (mirrors the local
  // script). Firing them all at once overruns OpenAI's per-minute token limit.
  const bulkGenerateCaptions = async () => {
    const targets = sessions.filter(s => isPlayableSession(s) && s.storagePath);
    if (!targets.length) { alert('No sessions with uploaded media yet.'); return; }
    if (!window.confirm(`Generate captions for ${targets.length} session(s), one at a time? Keep this tab open until it finishes — it can take a few minutes.`)) return;
    let ok = 0, failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const s = targets[i];
      setCaptionBulk(`Generating ${i + 1}/${targets.length}: ${s.name || 'session'}…`);
      try {
        const prev = (await loadCaption(progId, s.id))?.updatedAt || null;
        await triggerCaptionGeneration(progId, s.id);
        const outcome = await waitForCaption(progId, s.id, prev);
        if (outcome === 'ready') ok++; else failed++;
      } catch (_) { failed++; }
    }
    setCaptionBulk(`Done — ${ok} captioned${failed ? `, ${failed} failed or timed out (re-run to retry)` : ''}.`);
  };

  // Export this course's captions as one Word doc (a chapter per captioned session).
  const exportCourseWord = async (lang) => {
    setExportingBook(true);
    try {
      const items = [];
      for (const s of sessions.filter(isPlayableSession)) {
        const cap = await loadCaption(progId, s.id);
        if (cap && cap.status === 'ready') items.push({ name: s.name, proseSw: cap.proseSw, proseEn: cap.proseEn, cues: cap.cues });
      }
      if (!items.length) { alert('No captioned sessions yet — generate captions first.'); return; }
      const html = buildCaptionDocHtml(title || 'Course', items, lang);
      const suffix = lang === 'bilingual' ? 'SW-EN' : lang.toUpperCase();
      downloadText(`${sanitizeFilename(title || 'Course')} (${suffix}).doc`, html, 'application/msword');
    } catch (e) { alert('Export failed: ' + (e?.message || e)); }
    finally { setExportingBook(false); }
  };

  const addTestimonial = async (type) => {
    const id = `testimonial-${Date.now()}`;
    const data = { type, url:'', caption:'', authorName:'', order:testimonials.length, createdAt:new Date().toISOString() };
    await setDoc(doc(db,'programs',progId,'testimonials',id), data);
    setTestimonials(p=>[...p,{id,...data}]);
  };

  const updateTestimonial = async (id, upd) => {
    setTestimonials(p=>p.map(t=>t.id===id?{...t,...upd}:t));
    await setDoc(doc(db,'programs',progId,'testimonials',id), upd, {merge:true});
  };

  const deleteTestimonial = async (id) => {
    await deleteDoc(doc(db,'programs',progId,'testimonials',id));
    setTestimonials(p=>p.filter(t=>t.id!==id));
  };

  const saveAndFinish = async (published) => {
    setSaving(true);
    try {
      await setDoc(doc(db,'programs',progId), {assessmentQuestions:questions, unlockSchedule, published, updatedAt:new Date().toISOString()}, {merge:true});
      onBack();
    } catch(err) {
      console.error('Failed to save program:', err);
      alert('Save failed: ' + (err?.message || 'Unknown error. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const STEPS = ['Basic Info','Sessions','Testimonials','Assessment','Publish'];

  return (
    <div className="tab-content">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24}}>
        <button className="btn-delete" onClick={onBack} style={{padding:'6px 14px'}}>← Back</button>
        <h1 className="tab-title" style={{margin:0}}>{isNew?'New Program':'Edit Program'}</h1>
      </div>
      <div style={{display:'flex',gap:4,marginBottom:28,flexWrap:'wrap'}}>
        {STEPS.map((s,i) => (
          <button key={i} onClick={() => { if (progId||i===0) setStep(i+1); }}
            style={{padding:'6px 14px',borderRadius:20,border:'none',cursor:(progId||i===0)?'pointer':'not-allowed',fontFamily:"'Inter',sans-serif",fontSize:'.8rem',
              background:step===i+1?'var(--soil)':'var(--parchment)',color:step===i+1?'white':'var(--text)',opacity:(!progId&&i>0)?0.4:1}}>
            {i+1}. {s}
          </button>
        ))}
      </div>

      {step===1 && (
        <div style={{display:'flex',flexDirection:'column',gap:16,maxWidth:600}}>
          {[['Program Title *',title,setTitle,'text','e.g. Brand New Me'],['Tagline',tagline,setTagline,'text','Short description on catalog card'],['Price (TZS)',price,setPrice,'number','0 for free']].map(([label,val,set,type,ph])=>(
            <div key={label}>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</label>
              <input type={type} value={val} onChange={e=>set(e.target.value)} placeholder={ph}
                style={{width:type==='number'?180:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
            </div>
          ))}
          <div>
            <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>Discount</label>
            <div style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer'}} onClick={()=>setDiscountActive(v=>!v)}>
              <div style={{width:44,height:24,borderRadius:12,background:discountActive?'#C0392B':'#C9BCAE',position:'relative',transition:'background .2s',flexShrink:0}}>
                <div style={{width:18,height:18,borderRadius:'50%',background:'white',position:'absolute',top:3,left:discountActive?23:3,transition:'left .2s'}}/>
              </div>
              <span style={{fontSize:'.9rem',color:'var(--text)',fontWeight:discountActive?600:400}}>
                {discountActive ? 'On — clients see a discounted price' : 'Off — full price'}
              </span>
            </div>
            {discountActive && (
              <div style={{marginTop:10}}>
                <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Sale price (TZS)</label>
                <input type="number" value={salePrice} onChange={e=>setSalePrice(e.target.value)} placeholder="e.g. 40000"
                  style={{width:180,padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
                {(() => {
                  const base = Number(price)||0, sale = Number(salePrice)||0;
                  if (!(sale>0 && sale<base)) return <p style={{fontSize:'.75rem',color:'#C0392B',marginTop:6}}>Enter a sale price lower than the full price{base?` (TZS ${base.toLocaleString()})`:' — set a price first'}.</p>;
                  return <p style={{fontSize:'.78rem',color:'var(--soil)',marginTop:6}}>Clients pay <strong>TZS {sale.toLocaleString()}</strong> instead of <span style={{textDecoration:'line-through'}}>TZS {base.toLocaleString()}</span> — {Math.round((1-sale/base)*100)}% OFF.</p>;
                })()}
              </div>
            )}
          </div>
          <div>
            <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Access Duration</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {[['lifetime','Lifetime'],['3months','3 Months'],['6months','6 Months'],['12months','12 Months']].map(([val,label])=>(
                <button key={val} type="button" onClick={()=>setAccessType(val)}
                  style={{padding:'8px 18px',borderRadius:20,border:`2px solid ${accessType===val?'var(--soil)':'var(--border)'}`,background:accessType===val?'var(--soil)':'white',color:accessType===val?'white':'var(--text)',fontFamily:"'Inter',sans-serif",fontSize:'.85rem',cursor:'pointer',fontWeight:accessType===val?600:400}}>
                  {label}
                </button>
              ))}
            </div>
            <p style={{fontSize:'.75rem',color:'var(--stone)',marginTop:6}}>
              {accessType==='lifetime' ? 'Clients keep access forever after purchase.' : `Clients lose access ${accessLabel(accessType)} after purchase and must re-enroll.`}
            </p>
          </div>
          <div>
            <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:8,textTransform:'uppercase',letterSpacing:'.06em'}}>Enrollment Status</label>
            <div style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer'}} onClick={()=>setEnrollmentOpen(v=>!v)}>
              <div style={{width:44,height:24,borderRadius:12,background:enrollmentOpen?'var(--soil)':'#C9BCAE',position:'relative',transition:'background .2s',flexShrink:0}}>
                <div style={{width:18,height:18,borderRadius:'50%',background:'white',position:'absolute',top:3,left:enrollmentOpen?23:3,transition:'left .2s'}}/>
              </div>
              <span style={{fontSize:'.9rem',color:'var(--text)',fontWeight:enrollmentOpen?600:400}}>
                {enrollmentOpen ? 'Open — new clients can enroll' : 'Paused — no new enrollments'}
              </span>
            </div>
            {!enrollmentOpen && <p style={{fontSize:'.75rem',color:'#C0392B',marginTop:6}}>Existing enrolled clients are not affected and can still access the course.</p>}
          </div>
          <div>
            <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Description</label>
            <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={5} placeholder="Full description shown on program overview…"
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
          </div>
          {isNew && (
            <div>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Program ID (slug)</label>
              <input value={programSlug} onChange={e=>setProgramSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,''))} placeholder="auto-generated — set to 'brand-new-me' for main program"
                style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:'monospace',fontSize:'.85rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
              <div style={{fontSize:'.7rem',color:'var(--stone)',marginTop:4}}>Only set this for Brand New Me. Leave blank for all other programs.</div>
            </div>
          )}
          <div>
            <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Cover Image</label>
            <DropUpload accept="image/*" label="Cover image" previewUrl={coverUrl} previewType="image"
              uploading={coverUploading} progress={coverProgress}
              onFile={async file => {
                setCoverUploading(true); setCoverProgress(0);
                let pid=progId;
                if (!pid) {
                  const slug=programSlug.trim()||title.trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')||`prog-${Date.now()}`;
                  await setDoc(doc(db,'programs',slug),{title:title||'Draft',tagline,description,price:price?Number(price):0,hasAssessment,published:false,createdAt:new Date().toISOString()});
                  pid=slug; setProgId(slug);
                }
                const url=await uploadFile(`programs/${pid}/cover`,file,p=>setCoverProgress(p));
                setCoverUrl(url);
                await setDoc(doc(db,'programs',pid),{coverImageUrl:url},{merge:true});
                setCoverUploading(false);
              }} />
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <input type="checkbox" id="hasAssess" checked={hasAssessment} onChange={e=>setHasAssessment(e.target.checked)} />
            <label htmlFor="hasAssess" style={{fontSize:'.88rem',color:'var(--text)',cursor:'pointer'}}>Include a wellness assessment before enrollment</label>
          </div>
          <div>
            <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Unlock Schedule</label>
            <select value={unlockSchedule} onChange={e=>setUnlockSchedule(e.target.value)}
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)'}}>
              <option value="sequential">Sequential — complete previous session to unlock next</option>
              <option value="daily">Day-gated — multiple sessions per day, next day locked until calendar day changes</option>
              <option value="timed">Timed — sessions unlock at 6am / 12pm / 6pm on their assigned day</option>
            </select>
            {unlockSchedule==='daily' && (
              <div style={{fontSize:'.72rem',color:'var(--stone)',marginTop:4}}>Assign a Day number to each session in Step 2. Day 0 = enrollment day. Multiple sessions can share the same day.</div>
            )}
            {unlockSchedule==='timed' && (
              <div style={{fontSize:'.72rem',color:'var(--stone)',marginTop:4}}>Assign a Day number and time slot (Morning / Afternoon / Evening) to each session in Step 2.</div>
            )}
          </div>
          <button className="btn-upload" onClick={saveBasicInfo} disabled={!title.trim()||saving} style={{alignSelf:'flex-start'}}>
            {saving?'Saving…':'Save & Continue →'}
          </button>
        </div>
      )}

      {step===2 && progId && (
        <div>
          {sessionsLoading ? <div className="empty-state">Loading…</div> : <>
            {groups.length>0 && (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:'.75rem',fontWeight:600,color:'var(--bark)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Groups</div>
                {groups.map(g=>(
                  <div key={g.id} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                    <input value={g.name} onChange={async e=>{
                      const name=e.target.value;
                      setGroups(p=>p.map(x=>x.id===g.id?{...x,name}:x));
                      await setDoc(doc(db,'programs',progId,'groups',g.id),{name},{merge:true});
                    }} style={{flex:1,padding:'6px 10px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.85rem',background:'white',color:'var(--text)'}} />
                    <button className="btn-delete" onClick={()=>deleteGroup(g.id)}>Delete</button>
                  </div>
                ))}
              </div>
            )}
            {/* Captions — bulk generate for the whole course + Word export (top of Step 2) */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',padding:'10px 12px',marginBottom:16,background:'var(--cream)',border:'1px solid var(--border)',borderRadius:10}}>
              <span style={{fontSize:'.72rem',fontWeight:600,color:'var(--bark)',textTransform:'uppercase',letterSpacing:'.05em'}}>Captions</span>
              <button className="btn-upload btn-replace" style={{fontSize:'.74rem'}} onClick={bulkGenerateCaptions}>Generate for all sessions</button>
              <span style={{fontSize:'.72rem',color:'var(--stone)',marginLeft:6}}>Export Word:</span>
              <button className="btn-upload" style={{fontSize:'.74rem'}} disabled={exportingBook} onClick={()=>exportCourseWord('sw')}>Swahili</button>
              <button className="btn-upload" style={{fontSize:'.74rem'}} disabled={exportingBook} onClick={()=>exportCourseWord('en')}>English</button>
              <button className="btn-upload" style={{fontSize:'.74rem'}} disabled={exportingBook} onClick={()=>exportCourseWord('bilingual')}>Both</button>
              {exportingBook && <span style={{fontSize:'.7rem',color:'var(--stone)'}}>Building…</span>}
              {captionBulk && <span style={{fontSize:'.7rem',color:'var(--moss)',flexBasis:'100%'}}>{captionBulk}</span>}
            </div>

            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
              {sessions.length===0 && <div className="empty-state" style={{padding:20}}>No sessions yet. Use the buttons below.</div>}
              {sessions.map((s,idx)=>(
                <SessionBuilderRow key={s.id} session={s} idx={idx} total={sessions.length} groups={groups} progId={progId} programTitle={title} unlockSchedule={unlockSchedule}
                  onUpdate={upd=>updateSession(s.id,upd)} onDelete={()=>deleteSession(s.id)}
                  onMoveUp={()=>reorderSession(idx,-1)} onMoveDown={()=>reorderSession(idx,1)} />
              ))}
            </div>

            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              <button className="btn-upload" onClick={addSession}>+ Session</button>
              <button className="btn-upload btn-replace" onClick={addReflection}>+ Contemplation</button>
              <button className="btn-upload btn-replace" onClick={addInteractive}>+ Tool</button>
              <button className="btn-upload btn-replace" onClick={addGroup}>+ Group</button>
              <button className="btn-upload" style={{marginLeft:'auto'}} onClick={()=>setStep(3)}>Continue →</button>
            </div>
          </>}
        </div>
      )}

      {step===3 && progId && (
        <div>
          <div style={{display:'flex',flexDirection:'column',gap:12,marginBottom:16}}>
            {testimonials.length===0 && <div className="empty-state" style={{padding:20}}>No testimonials yet.</div>}
            {testimonials.map(t=>(
              <TestimonialBuilderRow key={t.id} testimonial={t} progId={progId}
                onUpdate={upd=>updateTestimonial(t.id,upd)} onDelete={()=>deleteTestimonial(t.id)} />
            ))}
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <button className="btn-upload" onClick={()=>addTestimonial('photo')}>+ Photo</button>
            <button className="btn-upload btn-replace" onClick={()=>addTestimonial('audio')}>+ Audio</button>
            <button className="btn-upload btn-replace" onClick={()=>addTestimonial('video')}>+ Video</button>
            <button className="btn-upload" style={{marginLeft:'auto'}} onClick={()=>setStep(4)}>Continue →</button>
          </div>
        </div>
      )}

      {step===4 && progId && (
        <div>
          {!hasAssessment ? (
            <div className="empty-state">Assessment disabled — enable it in Step 1. <button className="btn-upload" style={{marginLeft:8,display:'inline-block'}} onClick={()=>setStep(5)}>Continue →</button></div>
          ) : <>
            <div style={{display:'flex',flexDirection:'column',gap:12,marginBottom:16}}>
              {questions.length===0 && <div className="empty-state" style={{padding:20}}>No questions yet.</div>}
              {questions.map((q,idx)=>(
                <QuestionBuilderRow key={q.id} question={q} idx={idx}
                  onUpdate={upd=>setQuestions(p=>p.map(x=>x.id===q.id?{...x,...upd}:x))}
                  onDelete={()=>setQuestions(p=>p.filter(x=>x.id!==q.id))} />
              ))}
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              <button className="btn-upload" onClick={()=>setQuestions(p=>[...p,{id:`q-${Date.now()}`,type:'rating',question:'',order:p.length}])}>+ Rating 1–5</button>
              <button className="btn-upload btn-replace" onClick={()=>setQuestions(p=>[...p,{id:`q-${Date.now()}`,type:'multiple_choice',question:'',options:['',''],order:p.length}])}>+ Multiple Choice</button>
              <button className="btn-upload btn-replace" onClick={()=>setQuestions(p=>[...p,{id:`q-${Date.now()}`,type:'text',question:'',order:p.length}])}>+ Free Text</button>
              <button className="btn-upload" style={{marginLeft:'auto'}} onClick={()=>setStep(5)}>Continue →</button>
            </div>
          </>}
        </div>
      )}

      {step===5 && progId && (
        <div style={{display:'flex',flexDirection:'column',gap:16,maxWidth:500}}>
          <div style={{background:'white',borderRadius:12,border:'1px solid var(--border)',padding:20}}>
            <div style={{fontWeight:600,color:'var(--soil)',marginBottom:12}}>Program Summary</div>
            <div style={{fontSize:'.85rem',color:'var(--text)',lineHeight:2}}>
              <div><strong>Title:</strong> {title}</div>
              <div><strong>Price:</strong> {price?`TZS ${Number(price).toLocaleString()}`:'Free'}{discountActive && Number(salePrice)>0 && Number(salePrice)<Number(price) ? ` · on sale: TZS ${Number(salePrice).toLocaleString()} (${Math.round((1-Number(salePrice)/Number(price))*100)}% OFF)` : ''}</div>
              <div><strong>Access:</strong> {accessLabel(accessType)}</div>
              <div><strong>Enrollment:</strong> {enrollmentOpen ? 'Open' : 'Paused'}</div>
              <div><strong>Sessions:</strong> {sessions.filter(isPlayableSession).length}</div>
              <div><strong>Contemplations:</strong> {sessions.filter(s=>s.type==='reflection').length}</div>
              <div><strong>Testimonials:</strong> {testimonials.length}</div>
              <div><strong>Assessment:</strong> {hasAssessment?`${questions.length} questions`:'None'}</div>
            </div>
          </div>
          <div style={{display:'flex',gap:12}}>
            <button className="btn-upload" onClick={()=>saveAndFinish(true)} disabled={saving}>{saving?'Saving…':'Publish'}</button>
            <button className="btn-upload btn-replace" onClick={()=>saveAndFinish(false)} disabled={saving}>Save as Draft</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DropUpload({ accept, label, onFile, previewUrl, previewType, uploading, progress, compact }) {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  const handleDrop = e => {
    e.preventDefault(); setOver(false);
    const file = e.dataTransfer.files[0]; if (file) onFile(file);
  };
  return (
    <div
      onDragOver={e=>{ e.preventDefault(); setOver(true); }}
      onDragLeave={()=>setOver(false)}
      onDrop={handleDrop}
      onClick={()=>ref.current?.click()}
      style={{
        border:`2px dashed ${over?'var(--bark)':'var(--border)'}`,
        borderRadius:8,
        padding:compact?'7px 10px':'14px 16px',
        cursor:'pointer',
        background:over?'#F5EFE6':'white',
        display:'flex',
        flexDirection:'row',
        alignItems:'center',
        gap:8,
        flex:1,
        minWidth:compact?100:140,
        transition:'border-color .15s,background .15s',
        boxSizing:'border-box',
        userSelect:'none',
      }}>
      <input ref={ref} type="file" accept={accept} style={{display:'none'}}
        onChange={e=>{ const f=e.target.files[0]; if(f) onFile(f); e.target.value=''; }} />
      {previewUrl && (previewType==='video'
        ? <span style={{fontSize:'.68rem',color:'var(--moss)',flexShrink:0}}>✓ Video</span>
        : previewType==='audio'
        ? <audio controls src={previewUrl} style={{height:26,maxWidth:140,flexShrink:0}} onClick={e=>e.stopPropagation()} />
        : <img src={previewUrl} alt="" style={{width:30,height:30,objectFit:'cover',borderRadius:4,border:'1px solid var(--border)',flexShrink:0}} />
      )}
      <div style={{flex:1,minWidth:0,overflow:'hidden'}}>
        {uploading
          ? <span style={{fontSize:'.71rem',color:'var(--bark)'}}>Uploading… {progress}%</span>
          : <span style={{fontSize:'.71rem',color:'var(--stone)',lineHeight:1.35,display:'block'}}>
              {previewUrl?`Replace ${label}`:label}
              <span style={{display:'block',color:'#BFB8AD',fontSize:'.65rem'}}>drag & drop or click</span>
            </span>
        }
      </div>
    </div>
  );
}

function SessionBuilderRow({ session:s, idx, total, groups, progId, programTitle, unlockSchedule, onUpdate, onDelete, onMoveUp, onMoveDown }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [thumbUploading, setThumbUploading] = useState(false);
  // (H2) Media preview source — a signed URL for saved media, or a local blob right
  // after upload. (s.audioUrl is only set for legacy/unmigrated sessions.)
  const [mediaPreview, setMediaPreview] = useState(s.audioUrl || null);
  const isReflection = s.type === 'reflection';
  const isInteractive = s.type === 'interactive';

  useEffect(() => {
    if (mediaPreview || isReflection || isInteractive || !s.storagePath) return;
    let cancelled = false;
    fetchSignedMediaUrl(progId, s.id)
      .then(url => { if (!cancelled) setMediaPreview(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [s.storagePath]);

  const handleMediaFile = async file => {
    setUploading(true); setProgress(0);
    try {
      const { kind, mime } = await sniffMediaType(file);
      const path = `programs/${progId}/${s.id}/media`;
      await uploadFileNoToken(path, file, p => setProgress(p), mime);
      const duration = await getFileDuration(file);
      onUpdate({ audioUrl:'', contentType:kind, duration, storagePath:path });
      setMediaPreview(URL.createObjectURL(file)); // instant local preview
    } catch(err) { alert('Upload failed: ' + (err?.message||err)); }
    finally { setUploading(false); }
  };

  const handleThumbFile = async file => {
    setThumbUploading(true);
    try {
      const url = await uploadFile(`programs/${progId}/${s.id}/thumb`, file, ()=>{});
      onUpdate({ thumbnailUrl:url });
    } catch(err) { alert('Upload failed: ' + (err?.message||err)); }
    finally { setThumbUploading(false); }
  };

  // Interactive tool: upload a single self-contained .html (inline CSS/JS — external
  // files won't resolve, and the sandbox has no storage access, so keep state in memory).
  const handleToolFile = async file => {
    if (!/\.html?$/i.test(file.name)) { alert('Please upload a single .html file.'); return; }
    setUploading(true); setProgress(0);
    try {
      const path = `programs/${progId}/${s.id}/tool.html`;
      const url = await uploadHtmlFile(path, file, p => setProgress(p));
      onUpdate({ interactiveUrl:url, interactivePath:path, interactiveSource:'upload' });
    } catch(err) { alert('Upload failed: ' + (err?.message||err)); }
    finally { setUploading(false); }
  };

  return (
    <div style={{background:'white',borderRadius:12,border:'1px solid var(--border)',padding:'14px 16px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:'.68rem',background:isReflection?'var(--parchment)':isInteractive?'#E3E8F5':'#D4E8CF',color:isReflection?'var(--bark)':isInteractive?'#4A5A8A':'var(--moss)',padding:'2px 8px',borderRadius:20,flexShrink:0}}>
          {isReflection?'contemplation':isInteractive?'tool':'session'}
        </span>
        <input value={s.name||''} onChange={e=>onUpdate({name:e.target.value})}
          style={{flex:1,minWidth:80,border:'none',borderBottom:'1px solid var(--border)',padding:'4px 0',fontFamily:"'Inter',sans-serif",fontSize:'.88rem',color:'var(--text)',background:'none'}} />
        {groups.length>0 && (
          <select value={s.groupId||''} onChange={e=>onUpdate({groupId:e.target.value||null})}
            style={{fontSize:'.75rem',border:'1px solid var(--border)',borderRadius:6,padding:'3px 6px',fontFamily:"'Inter',sans-serif",color:'var(--text)',background:'white'}}>
            <option value="">No group</option>
            {groups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        {!isReflection && (
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:'.72rem',color:'var(--moss)',cursor:'pointer',flexShrink:0}}>
            <input type="checkbox" checked={s.isPreview||false} onChange={e=>onUpdate({isPreview:e.target.checked})} style={{accentColor:'var(--moss)'}} />
            Preview
          </label>
        )}
        <button onClick={onMoveUp} disabled={idx===0} style={{background:'none',border:'none',cursor:idx===0?'default':'pointer',color:idx===0?'var(--border)':'var(--stone)',fontSize:'.75rem',padding:'2px 4px'}}>▲</button>
        <button onClick={onMoveDown} disabled={idx===total-1} style={{background:'none',border:'none',cursor:idx===total-1?'default':'pointer',color:idx===total-1?'var(--border)':'var(--stone)',fontSize:'.75rem',padding:'2px 4px'}}>▼</button>
        <button className="btn-delete" onClick={onDelete}>✕</button>
      </div>
      {(unlockSchedule==='daily'||unlockSchedule==='timed') && !isReflection && !isInteractive && (
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8,flexWrap:'wrap'}}>
          <label style={{fontSize:'.72rem',color:'var(--stone)',flexShrink:0}}>Day</label>
          <input type="number" min="0" value={s.releaseDay??''} onChange={e=>onUpdate({releaseDay:e.target.value===''?null:Number(e.target.value)})} placeholder="0"
            style={{width:64,padding:'4px 8px',border:'1px solid var(--border)',borderRadius:6,fontFamily:"'Inter',sans-serif",fontSize:'.82rem',background:'white',color:'var(--text)'}} />
          {unlockSchedule==='timed' && (
            <select value={s.timeSlot||'morning'} onChange={e=>onUpdate({timeSlot:e.target.value})}
              style={{padding:'4px 8px',border:'1px solid var(--border)',borderRadius:6,fontFamily:"'Inter',sans-serif",fontSize:'.82rem',background:'white',color:'var(--text)'}}>
              <option value="morning">Morning — 6:00 AM</option>
              <option value="afternoon">Afternoon — 12:00 PM</option>
              <option value="evening">Evening — 6:00 PM</option>
            </select>
          )}
        </div>
      )}
      {isInteractive ? (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:'.72rem',color:'var(--stone)'}}>Source</span>
            {['link','upload'].map(src=>(
              <label key={src} style={{display:'flex',alignItems:'center',gap:4,fontSize:'.75rem',color:'var(--text)',cursor:'pointer'}}>
                <input type="radio" name={`src-${s.id}`} checked={(s.interactiveSource||'link')===src}
                  onChange={()=>onUpdate({interactiveSource:src})} style={{accentColor:'var(--moss)'}} />
                {src==='link'?'Link to a page':'Upload .html file'}
              </label>
            ))}
            {s.interactiveUrl && (
              <a href={s.interactiveUrl} target="_blank" rel="noopener noreferrer"
                style={{marginLeft:'auto',fontSize:'.72rem',color:'var(--moss)'}}>Open preview ↗</a>
            )}
          </div>

          {(s.interactiveSource||'link')==='link' ? (
            <input value={s.interactiveUrl||''} onChange={e=>onUpdate({interactiveUrl:e.target.value.trim(),interactiveSource:'link'})}
              placeholder="https://your-tool.netlify.app"
              style={{width:'100%',padding:'8px 10px',border:`1px solid ${s.interactiveUrl && !/^https:\/\//i.test(s.interactiveUrl) ? '#C0392B' : 'var(--border)'}`,borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.83rem',color:'var(--text)',background:'white',boxSizing:'border-box'}} />
          ) : (
            <div>
              <label style={{display:'inline-block',padding:'8px 14px',border:'1px dashed var(--border)',borderRadius:8,cursor:'pointer',fontSize:'.78rem',color:'var(--bark)',background:'var(--cream)'}}>
                {uploading ? `Uploading… ${progress}%` : s.interactivePath ? 'Replace .html file' : 'Choose .html file'}
                <input type="file" accept=".html,text/html" style={{display:'none'}}
                  onChange={e=>{ const f=e.target.files?.[0]; if(f) handleToolFile(f); e.target.value=''; }} />
              </label>
              <div style={{fontSize:'.68rem',color:'var(--stone)',marginTop:4,lineHeight:1.4}}>
                One self-contained file — put the CSS and JavaScript inside it. It runs sandboxed, so keep any state in memory (no localStorage).
              </div>
            </div>
          )}

          {s.interactiveUrl && !/^https:\/\//i.test(s.interactiveUrl) && (
            <div style={{fontSize:'.7rem',color:'#C0392B'}}>Link must start with https://</div>
          )}

          <textarea value={s.description||''} onChange={e=>onUpdate({description:e.target.value})} rows={2}
            placeholder="Optional intro shown before the tool opens…"
            style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.83rem',color:'var(--text)',background:'var(--cream)',boxSizing:'border-box',resize:'vertical'}} />
        </div>
      ) : isReflection ? (
        <textarea value={s.prompt||''} onChange={e=>onUpdate({prompt:e.target.value})} rows={2}
          placeholder="Write the contemplation text for this session…"
          style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.83rem',color:'var(--text)',background:'var(--cream)',boxSizing:'border-box',resize:'vertical'}} />
      ) : (
        <>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <DropUpload accept="audio/*,video/*" label="Media (audio / video)"
              onFile={handleMediaFile} previewUrl={mediaPreview} previewType={s.contentType}
              uploading={uploading} progress={progress} compact />
            <DropUpload accept="image/*" label="Thumbnail image"
              onFile={handleThumbFile} previewUrl={s.thumbnailUrl} previewType="image"
              uploading={thumbUploading} progress={0} compact />
          </div>
          {s.storagePath && (
            <SessionCaptions programId={progId} sessionId={s.id} sessionName={s.name} programTitle={programTitle} />
          )}
        </>
      )}
    </div>
  );
}

// Per-session caption strip shown under the media uploader: status, generate/
// regenerate, edit/download, and the "show to clients" visibility toggle.
function SessionCaptions({ programId, sessionId, sessionName, programTitle }) {
  const [cap, setCap] = useState(undefined); // undefined = loading, null = none
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try { setCap(await loadCaption(programId, sessionId)); } catch { setCap(null); }
  }, [programId, sessionId]);

  useEffect(() => {
    refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [refresh]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 6;
      const c = await loadCaption(programId, sessionId);
      setCap(c);
      if (!c || c.status === 'ready' || c.status === 'error' || elapsed > 15 * 60) {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      }
    }, 6000);
  };

  const generate = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await triggerCaptionGeneration(programId, sessionId);
      setCap(c => ({ ...(c || {}), status: 'processing', errorMsg: '' }));
      startPolling();
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const togglePublished = async (val) => {
    setCap(c => ({ ...c, published: val }));
    await setDoc(doc(db, 'programs', programId, 'sessions', sessionId, 'captions', 'data'),
      { published: val, updatedAt: new Date().toISOString() }, { merge: true });
  };

  if (cap === undefined) return <div style={{ fontSize: '.72rem', color: 'var(--stone)', marginTop: 8 }}>Captions…</div>;
  const status = cap?.status;
  const ready = status === 'ready';
  const processing = status === 'processing' || busy;

  return (
    <div style={{ marginTop: 10, borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--bark)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Captions</span>
        {ready && <span style={{ fontSize: '.68rem', background: '#D4E8CF', color: 'var(--moss)', padding: '2px 8px', borderRadius: 20 }}>✓ {cap.cues?.length || 0} lines · {(cap.sourceLang || '').toUpperCase()}</span>}
        {processing && <span style={{ fontSize: '.68rem', background: 'var(--parchment)', color: 'var(--bark)', padding: '2px 8px', borderRadius: 20 }}>Processing… (a few minutes)</span>}
        {status === 'error' && <span style={{ fontSize: '.68rem', background: '#F4D9D6', color: '#B4544F', padding: '2px 8px', borderRadius: 20 }}>Error</span>}
        {!status && !processing && <span style={{ fontSize: '.7rem', color: 'var(--stone)' }}>none yet</span>}

        {!processing && (
          <button className="btn-upload btn-replace" style={{ padding: '3px 10px', fontSize: '.72rem' }} onClick={generate}>
            {ready || status === 'error' ? 'Regenerate' : 'Generate'}
          </button>
        )}
        {ready && <button className="btn-upload" style={{ padding: '3px 10px', fontSize: '.72rem' }} onClick={() => setEditing(true)}>Edit / Download</button>}
        {ready && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.7rem', color: 'var(--moss)', cursor: 'pointer' }}>
            <input type="checkbox" checked={cap.published !== false} onChange={e => togglePublished(e.target.checked)} style={{ accentColor: 'var(--moss)' }} />
            Show to clients
          </label>
        )}
      </div>
      {error && <div style={{ fontSize: '.68rem', color: '#B4544F', marginTop: 4 }}>{error}</div>}
      {status === 'error' && cap.errorMsg && <div style={{ fontSize: '.68rem', color: '#B4544F', marginTop: 4 }}>{cap.errorMsg}</div>}

      {editing && (
        <CaptionEditorModal
          programId={programId} sessionId={sessionId} sessionName={sessionName} programTitle={programTitle}
          caption={cap} onClose={() => setEditing(false)}
          onSaved={(newCap) => setCap(newCap)}
          onRegenerate={() => { setEditing(false); generate(); }}
        />
      )}
    </div>
  );
}

// Full-screen editor: a cue table (timestamps + Swahili + English), subtitle
// downloads (.srt/.vtt), and Word export. Editing text or timing then Save writes
// the whole cue array back to the captions doc.
function CaptionEditorModal({ programId, sessionId, sessionName, programTitle, caption, onClose, onSaved, onRegenerate }) {
  const [rows, setRows] = useState(() => (caption.cues || []).map(c => ({
    id: c.id, start: c.start, end: c.end, startStr: mmss(c.start), endStr: mmss(c.end), orig: c.orig || '', sw: c.sw || '', en: c.en || '',
  })));
  const anyOrig = (caption.cues || []).some(c => c.orig && c.orig.trim());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [exporting, setExporting] = useState(false);

  const update = (i, field, val) => { setRows(p => p.map((r, idx) => idx === i ? { ...r, [field]: val } : r)); setDirty(true); };
  const addRow = (i) => {
    setRows(p => {
      const n = [...p]; const at = i == null ? p.length : i + 1;
      const prev = n[at - 1]; const start = prev ? prev.end : 0;
      n.splice(at, 0, { id: Date.now(), start, end: start + 3, startStr: mmss(start), endStr: mmss(start + 3), orig: '', sw: '', en: '' });
      return n;
    });
    setDirty(true);
  };
  const delRow = (i) => { setRows(p => p.filter((_, idx) => idx !== i)); setDirty(true); };

  // Preserve untouched sub-second timestamps: only re-parse a field the admin changed.
  const buildCues = () => rows.map((r, i) => ({
    id: i,
    start: r.startStr !== mmss(r.start) ? (parseMmss(r.startStr) ?? r.start) : r.start,
    end: r.endStr !== mmss(r.end) ? (parseMmss(r.endStr) ?? r.end) : r.end,
    orig: (r.orig || '').trim(), sw: (r.sw || '').trim(), en: (r.en || '').trim(),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const cues = buildCues();
      const upd = { cues, editedBy: auth.currentUser?.uid || null, updatedAt: new Date().toISOString() };
      await setDoc(doc(db, 'programs', programId, 'sessions', sessionId, 'captions', 'data'), upd, { merge: true });
      onSaved({ ...caption, ...upd });
      setDirty(false);
    } catch (e) { alert('Save failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const fileBase = sanitizeFilename(`${programTitle || 'Program'} - ${sessionName || 'Session'}`);
  const dlSub = (kind, lang) => {
    const cues = buildCues();
    if (kind === 'vtt') downloadText(`${fileBase} (${lang.toUpperCase()}).vtt`, cuesToVtt(cues, lang), 'text/vtt;charset=utf-8');
    else downloadText(`${fileBase} (${lang.toUpperCase()}).srt`, cuesToSrt(cues, lang), 'application/x-subrip;charset=utf-8');
  };
  const exportWord = (lang) => {
    setExporting(true);
    try {
      const item = { name: sessionName || 'Session', proseSw: caption.proseSw, proseEn: caption.proseEn, cues: buildCues() };
      const html = buildCaptionDocHtml(sessionName || 'Session', [item], lang);
      const suffix = lang === 'bilingual' ? 'SW-EN' : lang.toUpperCase();
      downloadText(`${fileBase} (${suffix}).doc`, html, 'application/msword');
    } catch (e) { alert('Word export failed: ' + (e?.message || e)); }
    finally { setExporting(false); }
  };

  const cell = { border: '1px solid var(--border)', padding: '4px 6px', verticalAlign: 'top' };
  const tsInput = { width: 58, padding: '3px 4px', border: '1px solid var(--border)', borderRadius: 5, fontSize: '.72rem', fontFamily: "'Inter',sans-serif", color: 'var(--text)', background: 'white' };
  const txtInput = { width: '100%', minWidth: 140, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 5, fontSize: '.8rem', fontFamily: "'Inter',sans-serif", color: 'var(--text)', background: 'white', boxSizing: 'border-box', resize: 'vertical' };
  const miniBtn = { padding: '3px 8px', fontSize: '.68rem' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,40,46,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 14, maxWidth: 920, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, color: 'var(--soil)', fontSize: '.95rem' }}>Captions — {sessionName}</div>
          <button className="btn-delete" style={{ marginLeft: 'auto' }} onClick={onClose}>✕</button>
        </div>

        {/* Download / export toolbar */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--cream)' }}>
          <span style={{ fontSize: '.7rem', color: 'var(--stone)' }}>Subtitles:</span>
          {anyOrig && <button className="btn-upload btn-replace" style={miniBtn} onClick={() => dlSub('srt', 'orig')}>Orig .srt</button>}
          <button className="btn-upload btn-replace" style={miniBtn} onClick={() => dlSub('srt', 'sw')}>SW .srt</button>
          <button className="btn-upload btn-replace" style={miniBtn} onClick={() => dlSub('srt', 'en')}>EN .srt</button>
          {anyOrig && <button className="btn-upload btn-replace" style={miniBtn} onClick={() => dlSub('vtt', 'orig')}>Orig .vtt</button>}
          <button className="btn-upload btn-replace" style={miniBtn} onClick={() => dlSub('vtt', 'sw')}>SW .vtt</button>
          <button className="btn-upload btn-replace" style={miniBtn} onClick={() => dlSub('vtt', 'en')}>EN .vtt</button>
          <span style={{ fontSize: '.7rem', color: 'var(--stone)', marginLeft: 8 }}>Word:</span>
          <button className="btn-upload" style={miniBtn} disabled={exporting} onClick={() => exportWord('sw')}>SW</button>
          <button className="btn-upload" style={miniBtn} disabled={exporting} onClick={() => exportWord('en')}>EN</button>
          <button className="btn-upload" style={miniBtn} disabled={exporting} onClick={() => exportWord('bilingual')}>Both</button>
          {exporting && <span style={{ fontSize: '.68rem', color: 'var(--stone)' }}>Building…</span>}
        </div>

        {/* Cue table */}
        <div style={{ maxHeight: '58vh', overflowY: 'auto', overflowX: 'auto', padding: '8px 12px' }}>
          <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: '.8rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--stone)', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                <th style={cell}>Start</th><th style={cell}>End</th><th style={cell}>Original (as spoken)</th><th style={cell}>Swahili</th><th style={cell}>English</th><th style={cell}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id}>
                  <td style={cell}><input value={r.startStr} onChange={e => update(i, 'startStr', e.target.value)} style={tsInput} /></td>
                  <td style={cell}><input value={r.endStr} onChange={e => update(i, 'endStr', e.target.value)} style={tsInput} /></td>
                  <td style={cell}><textarea rows={2} value={r.orig} onChange={e => update(i, 'orig', e.target.value)} style={txtInput} /></td>
                  <td style={cell}><textarea rows={2} value={r.sw} onChange={e => update(i, 'sw', e.target.value)} style={txtInput} /></td>
                  <td style={cell}><textarea rows={2} value={r.en} onChange={e => update(i, 'en', e.target.value)} style={txtInput} /></td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    <button title="Add line below" onClick={() => addRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--moss)', fontSize: '.9rem' }}>＋</button>
                    <button title="Delete line" onClick={() => delRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B4544F', fontSize: '.85rem' }}>✕</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} style={{ ...cell, textAlign: 'center', color: 'var(--stone)' }}>No caption lines.</td></tr>}
            </tbody>
          </table>
          <button className="btn-upload btn-replace" style={{ ...miniBtn, marginTop: 8 }} onClick={() => addRow(null)}>+ Add line</button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 18px', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button className="btn-upload btn-replace" style={{ fontSize: '.75rem' }}
            onClick={() => { if (window.confirm('Regenerate captions from the audio? This overwrites the current text and any edits.')) onRegenerate(); }}>
            Regenerate from audio
          </button>
          <button className="btn-upload btn-replace" style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>
          <button className="btn-upload" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>
        </div>
      </div>
    </div>
  );
}

function TestimonialBuilderRow({ testimonial:t, progId, onUpdate, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const accept = t.type==='photo'?'image/*':t.type==='audio'?'audio/*':'video/*';
  const previewType = t.type==='photo'?'image':t.type;
  const handleFile = async file => {
    setUploading(true); setProgress(0);
    try {
      const url = await uploadFile(`programs/${progId}/testimonials/${t.id}`, file, p=>setProgress(p));
      onUpdate({url});
    } catch(err) { alert('Upload failed: ' + (err?.message||err)); }
    finally { setUploading(false); }
  };
  return (
    <div style={{background:'white',borderRadius:12,border:'1px solid var(--border)',padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:'.68rem',background:'var(--parchment)',color:'var(--bark)',padding:'2px 8px',borderRadius:20,flexShrink:0}}>{t.type}</span>
        <DropUpload accept={accept} label={t.type} onFile={handleFile}
          previewUrl={t.url} previewType={previewType} uploading={uploading} progress={progress} compact />
        <button className="btn-delete" style={{marginLeft:'auto',flexShrink:0}} onClick={onDelete}>Delete</button>
      </div>
      <input value={t.caption||''} onChange={e=>onUpdate({caption:e.target.value})} placeholder="Quote or caption…"
        style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.83rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
      <input value={t.authorName||''} onChange={e=>onUpdate({authorName:e.target.value})} placeholder="Author name"
        style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.83rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
    </div>
  );
}

function QuestionBuilderRow({ question:q, idx, onUpdate, onDelete }) {
  return (
    <div style={{background:'white',borderRadius:12,border:'1px solid var(--border)',padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:'.68rem',background:'var(--parchment)',color:'var(--bark)',padding:'2px 8px',borderRadius:20,flexShrink:0}}>
          {q.type==='rating'?'Rating 1–5':q.type==='multiple_choice'?'Multiple choice':'Free text'}
        </span>
        <span style={{fontSize:'.75rem',color:'var(--stone)',flex:1}}>Question {idx+1}</span>
        <button className="btn-delete" onClick={onDelete}>Delete</button>
      </div>
      <input value={q.question||''} onChange={e=>onUpdate({question:e.target.value})} placeholder="Question text…"
        style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.85rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
      {q.type==='multiple_choice' && (
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {(q.options||[]).map((opt,i)=>(
            <div key={i} style={{display:'flex',gap:6,alignItems:'center'}}>
              <input value={opt} onChange={e=>{const opts=[...(q.options||[])];opts[i]=e.target.value;onUpdate({options:opts});}} placeholder={`Option ${i+1}`}
                style={{flex:1,padding:'6px 10px',border:'1px solid var(--border)',borderRadius:6,fontFamily:"'Inter',sans-serif",fontSize:'.82rem',background:'white',color:'var(--text)'}} />
              <button className="btn-delete" onClick={()=>onUpdate({options:(q.options||[]).filter((_,j)=>j!==i)})}>✕</button>
            </div>
          ))}
          <button className="btn-upload btn-replace" style={{alignSelf:'flex-start',fontSize:'.75rem',padding:'4px 12px'}}
            onClick={()=>onUpdate({options:[...(q.options||[]),'']})}>+ Add option</button>
        </div>
      )}
    </div>
  );
}

// ─── PAYMENTS TAB ─────────────────────────────────────────────────────────────
function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);

  useEffect(() => {
    getDocs(collection(db,'payments')).then(snap => {
      setPayments(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>p.status==='pending').sort((a,b)=>(b.createdAt||'').localeCompare(a.createdAt||'')));
      setLoading(false);
    });
  }, []);

  const approve = async (p) => {
    setApprovingId(p.id);
    try {
      const paidAt = new Date().toISOString();
      if (p.kind === 'masterclass' && p.masterclassId) {
        // Masterclass seats live in registrations/{uid}/masterclasses/{id}, not enrollments.
        await setDoc(doc(db,'registrations',p.userId,'masterclasses',p.masterclassId),
          { paid: true, paymentPending: false, paidAt, registeredAt: paidAt }, {merge:true});
      } else {
        const progSnap = await getDoc(doc(db,'programs',p.programId));
        const accessType = p.accessType || (progSnap.exists() ? (progSnap.data().accessType || 'lifetime') : 'lifetime');
        const expiresAt = computeExpiresAt(accessType);
        const enrollData = { paid: true, paymentPending: false, paidAt, accessType };
        if (expiresAt) enrollData.expiresAt = expiresAt;
        await setDoc(doc(db,'enrollments',p.userId,'programs',p.programId), enrollData, {merge:true});
      }
      // Manual approval = free/comp access: mark the client active (grants access + shows
      // as active in the Clients tab), but set NO paidAt — revenue counts only AzamPay-
      // confirmed payments (those carry a transactionRef), so this never counts as money.
      await setDoc(doc(db,'users',p.userId),{paid:true,paymentPending:false},{merge:true});
      await setDoc(doc(db,'payments',p.id),{status:'approved',approvedAt:paidAt},{merge:true});
      setPayments(prev=>prev.filter(x=>x.id!==p.id));
    } catch(e) { console.error('Approve error:',e); alert('Approve failed: ' + (e?.message || e)); }
    setApprovingId(null);
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div><h1 className="tab-title">Payments</h1><p className="tab-sub">Pending M-Pesa payments across all programs.</p></div>
        <div className="header-stats">
          <div className="hstat hstat-pending"><span className="hstat-val">{payments.length}</span><span className="hstat-lab">Pending</span></div>
        </div>
      </div>
      {loading ? <div className="empty-state">Loading…</div> : payments.length===0 ? (
        <div className="empty-state">No pending payments.</div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {payments.map(p=>(
            <div key={p.id} style={{background:'white',borderRadius:12,border:'1px solid #FFE69C',padding:'16px 20px',display:'flex',alignItems:'flex-start',gap:16,flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:200}}>
                <div style={{fontWeight:500,color:'var(--text)',marginBottom:3}}>{p.userName||p.email}</div>
                <div style={{fontSize:'.78rem',color:'var(--stone)',marginBottom:2}}>{p.email} · {p.phone}</div>
                <div style={{fontSize:'.78rem',color:'var(--bark)',marginBottom:4}}>Program: <strong>{p.programTitle||p.programId}</strong></div>
                <div style={{fontSize:'.72rem',color:'var(--stone)'}}>
                  Code: <code style={{background:'var(--parchment)',padding:'1px 5px',borderRadius:4,fontFamily:'monospace'}}>{p.transactionCode}</code>
                  {' · '}TZS {Number(p.amount||0).toLocaleString()}
                  {p.createdAt && <> · {new Date(p.createdAt).toLocaleDateString('en-GB')}</>}
                </div>
              </div>
              <button className="btn-upload" disabled={approvingId===p.id} onClick={()=>approve(p)}>
                {approvingId===p.id?'Approving…':'Approve ✓'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BROADCAST TAB ────────────────────────────────────────────────────────────
function BroadcastTab() {
  const [programs, setPrograms] = useState([]);
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState('all');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'programs')),
      getDocs(collection(db, 'customPrograms')),
      getDocs(query(collection(db, 'broadcasts'), orderBy('sentAt', 'desc'), limit(30))),
    ]).then(([progSnap, custSnap, bcastSnap]) => {
      const uploaded = progSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.published).sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
      const custom = custSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      setPrograms([...uploaded, ...custom]);
      setHistory(bcastSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingHistory(false);
    });
  }, []);

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    const selectedProgram = programs.find(p => p.id === target);
    const newDoc = {
      message: message.trim(),
      target,
      programTitle: selectedProgram?.title || null,
      sentAt: new Date().toISOString(),
    };
    const ref = await addDoc(collection(db, 'broadcasts'), newDoc);
    setHistory(prev => [{ id: ref.id, ...newDoc }, ...prev]);
    setMessage('');
    setSent(true);
    setSending(false);
    setTimeout(() => setSent(false), 3000);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this broadcast?')) return;
    await deleteDoc(doc(db, 'broadcasts', id));
    setHistory(prev => prev.filter(b => b.id !== id));
  };

  const targetLabel = (b) => b.target === 'all' ? 'All users' : (b.programTitle || b.target);

  return (
    <div>
      <div className="tab-header">
        <div><h1 className="tab-title">Broadcast Message</h1><p className="tab-sub">Send a message to all users or everyone enrolled in a specific program.</p></div>
      </div>

      <div style={{maxWidth:640,display:'flex',flexDirection:'column',gap:16,marginBottom:40}}>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Send To</label>
          <select value={target} onChange={e=>setTarget(e.target.value)}
            style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)'}}>
            <option value="all">All users</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Message</label>
          <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={5}
            placeholder="Write your broadcast message…"
            style={{width:'100%',padding:'12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',resize:'vertical',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
        </div>
        <button className="btn-upload" onClick={send} disabled={sending||!message.trim()} style={{alignSelf:'flex-start',minWidth:160}}>
          {sending?'Sending…':sent?'✓ Sent!':'Send Broadcast'}
        </button>
      </div>

      <div style={{maxWidth:640}}>
        <h2 style={{fontSize:'.9rem',fontWeight:600,color:'var(--bark)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:16}}>Sent History</h2>
        {loadingHistory ? <div className="empty-state">Loading…</div> : history.length===0 ? <div className="empty-state">No broadcasts yet.</div> : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {history.map(b => (
              <div key={b.id} style={{background:'white',borderRadius:12,border:'1px solid var(--border)',padding:'14px 18px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:8}}>
                  <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                    <span style={{fontSize:'.72rem',background:b.target==='all'?'#D4E8CF':'var(--parchment)',color:b.target==='all'?'var(--moss)':'var(--bark)',padding:'2px 10px',borderRadius:20,fontFamily:"'Inter',sans-serif",fontWeight:600}}>
                      {targetLabel(b)}
                    </span>
                    <span style={{fontSize:'.72rem',color:'var(--stone)'}}>
                      {new Date(b.sentAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                    </span>
                  </div>
                  <button onClick={()=>del(b.id)} className="btn-delete" style={{flexShrink:0}}>✕</button>
                </div>
                <p style={{color:'var(--text)',fontSize:'.88rem',lineHeight:1.65,margin:0,whiteSpace:'pre-line'}}>{b.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MASTERCLASSES TAB ──────────────────────────────────────────────────────
function MasterclassesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventLocal, setEventLocal] = useState('');
  const [price, setPrice] = useState('');
  const [enrollmentOpen, setEnrollmentOpen] = useState(true);
  const [published, setPublished] = useState(false);
  const [coverUrl, setCoverUrl] = useState('');
  const [imgUploading, setImgUploading] = useState(false);
  const [imgProgress, setImgProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'masterclasses'));
      setList(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0)));
    } catch (e) { console.error('Load masterclasses failed:', e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toLocalInput = iso => {
    if (!iso) return '';
    const d = new Date(iso); if (isNaN(d)) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openNew = () => { setEditingId(null); setTitle(''); setDescription(''); setEventLocal(''); setPrice(''); setEnrollmentOpen(true); setPublished(false); setCoverUrl(''); setErr(''); setShowForm(true); };
  const openEdit = m => { setEditingId(m.id); setTitle(m.title || ''); setDescription(m.description || ''); setEventLocal(toLocalInput(m.eventDate)); setPrice(m.price != null ? String(m.price) : ''); setEnrollmentOpen(m.enrollmentOpen !== false); setPublished(!!m.published); setCoverUrl(m.coverImageUrl || ''); setErr(''); setShowForm(true); };

  const save = async () => {
    if (!title.trim()) { setErr('Title is required.'); return; }
    if (!eventLocal) { setErr('Date & time are required.'); return; }
    setSaving(true); setErr('');
    try {
      const data = {
        title: title.trim(), description: description.trim(),
        eventDate: new Date(eventLocal).toISOString(),
        price: price ? Number(price) : 0,
        enrollmentOpen, published,
        coverImageUrl: coverUrl,
        updatedAt: new Date().toISOString(),
      };
      if (editingId) await setDoc(doc(db, 'masterclasses', editingId), data, { merge: true });
      else await addDoc(collection(db, 'masterclasses'), { ...data, createdAt: new Date().toISOString() });
      setShowForm(false);
      await load();
    } catch (e) { console.error('Save masterclass failed:', e); setErr(e?.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const del = async m => {
    if (!window.confirm(`Delete "${m.title}"? This cannot be undone.`)) return;
    try { await deleteDoc(doc(db, 'masterclasses', m.id)); await load(); }
    catch (e) { console.error('Delete masterclass failed:', e); alert('Delete failed: ' + (e?.message || e)); }
  };

  const inp = { width: '100%', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, fontFamily: "'Inter',sans-serif", fontSize: '.9rem', background: 'white', color: 'var(--text)', boxSizing: 'border-box' };
  const lab = { display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--bark)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.06em' };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div><h1 className="tab-title">Masterclasses</h1><p className="tab-sub">Live events shown on the landing page and in-app. Clients enroll &amp; pay via AzamPay.</p></div>
        {!showForm && <button className="btn-upload" onClick={openNew}>+ New Masterclass</button>}
      </div>

      {showForm ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
          <div><label style={lab}>Title</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Breathwork Masterclass" style={inp} /></div>
          <div><label style={lab}>Description</label><textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="What it's about…" style={{ ...inp, resize: 'vertical' }} /></div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}><label style={lab}>Date &amp; Time</label><input type="datetime-local" value={eventLocal} onChange={e => setEventLocal(e.target.value)} style={inp} /></div>
            <div style={{ flex: 1, minWidth: 140 }}><label style={lab}>Price (TZS)</label><input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0 for free" style={inp} /></div>
          </div>
          <div><label style={lab}>Cover Image</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {coverUrl && <img src={coverUrl} alt="" style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }} />}
              <DropUpload accept="image/*" label="Cover image" previewUrl={null} previewType="image" uploading={imgUploading} progress={imgProgress}
                onFile={async file => { setImgUploading(true); setImgProgress(0); try { const url = await uploadFile(`masterclasses/${editingId || 'tmp-' + Date.now()}/cover`, file, p => setImgProgress(p)); setCoverUrl(url); } catch (e) { alert('Upload failed: ' + (e?.message || e)); } finally { setImgUploading(false); } }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', color: 'var(--text)', cursor: 'pointer' }}><input type="checkbox" checked={enrollmentOpen} onChange={e => setEnrollmentOpen(e.target.checked)} /> Enrollment open</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', color: 'var(--text)', cursor: 'pointer' }}><input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} /> Published (visible to clients)</label>
          </div>
          {err && <p style={{ color: '#C0392B', fontSize: '.82rem', margin: 0 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-upload" onClick={save} disabled={saving || imgUploading}>{saving ? 'Saving…' : 'Save Masterclass'}</button>
            <button onClick={() => setShowForm(false)} style={{ background: 'white', color: 'var(--bark)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 18px', fontFamily: "'Inter',sans-serif", fontSize: '.85rem', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      ) : loading ? (
        <div className="empty-state">Loading…</div>
      ) : list.length === 0 ? (
        <div className="empty-state">No masterclasses yet. Click “New Masterclass” to create one.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 680 }}>
          {list.map(m => {
            const past = m.eventDate && new Date(m.eventDate).getTime() < Date.now();
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 12, background: 'white', border: '1px solid var(--border)', borderRadius: 10, opacity: past ? 0.6 : 1 }}>
                {m.coverImageUrl
                  ? <img src={m.coverImageUrl} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                  : <div style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--parchment)', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '.95rem' }}>{m.title || 'Untitled'}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--bark)' }}>{m.eventDate ? new Date(m.eventDate).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : 'No date'}{past ? ' · past' : ''}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--stone)' }}>TZS {Number(m.price || 0).toLocaleString()} · {m.published ? 'Published' : 'Draft'}{m.enrollmentOpen === false ? ' · closed' : ''}</div>
                </div>
                <button onClick={() => openEdit(m)} style={{ background: 'white', color: 'var(--bark)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 12px', fontSize: '.8rem', cursor: 'pointer' }}>Edit</button>
                <button onClick={() => del(m)} style={{ background: 'white', color: '#C0392B', border: '1px solid #E8C4BE', borderRadius: 7, padding: '7px 10px', fontSize: '.8rem', cursor: 'pointer' }}>Delete</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── CATALOG TAB ──────────────────────────────────────────────────────────────
function CatalogTab() {
  const [coachName, setCoachName] = useState('');
  const [coachRole, setCoachRole] = useState('');
  const [coachOverview, setCoachOverview] = useState('');
  const [coachImageUrl, setCoachImageUrl] = useState('');
  const [contacts, setContacts] = useState('');
  const [heroTitle, setHeroTitle] = useState('');
  const [heroTagline, setHeroTagline] = useState('');
  const [heroDesc, setHeroDesc] = useState('');
  const [aboutLead, setAboutLead] = useState('');
  const [benefits, setBenefits] = useState([{title:'',desc:''},{title:'',desc:''},{title:'',desc:''},{title:'',desc:''}]);
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [aboutBannerUrl, setAboutBannerUrl] = useState('');
  const [mcBannerUrl, setMcBannerUrl] = useState('');
  const [testimonials, setTestimonials] = useState([]);
  const [bannerKey, setBannerKey] = useState('');
  const [bannerProgress, setBannerProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [imgUploading, setImgUploading] = useState(false);
  const [imgProgress, setImgProgress] = useState(0);

  useEffect(() => {
    getDoc(doc(db,'config','catalog')).then(snap => {
      if (snap.exists()) {
        const d=snap.data();
        setCoachName(d.coachName||''); setCoachRole(d.coachRole||'');
        setCoachOverview(d.coachOverview||'');
        setCoachImageUrl(d.coachImageUrl||''); setContacts(d.contacts||'');
        setHeroTitle(d.heroTitle||''); setHeroTagline(d.heroTagline||'');
        setHeroDesc(d.heroDesc||''); setAboutLead(d.aboutLead||'');
        setBenefits([
          {title:d.benefit1Title||'',desc:d.benefit1Desc||''},
          {title:d.benefit2Title||'',desc:d.benefit2Desc||''},
          {title:d.benefit3Title||'',desc:d.benefit3Desc||''},
          {title:d.benefit4Title||'',desc:d.benefit4Desc||''},
        ]);
        setHeroImageUrl(d.heroImageUrl||''); setAboutBannerUrl(d.aboutBannerUrl||''); setMcBannerUrl(d.masterclassBannerUrl||'');
        setTestimonials(Array.isArray(d.testimonials)?d.testimonials:[]);
      }
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load catalog config:', err);
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true); setSaveError('');
    try {
      await setDoc(doc(db,'config','catalog'),{coachName,coachRole,coachOverview,coachImageUrl,contacts,heroTitle,heroTagline,heroDesc,aboutLead,
        benefit1Title:benefits[0].title,benefit1Desc:benefits[0].desc,
        benefit2Title:benefits[1].title,benefit2Desc:benefits[1].desc,
        benefit3Title:benefits[2].title,benefit3Desc:benefits[2].desc,
        benefit4Title:benefits[3].title,benefit4Desc:benefits[3].desc,
        heroImageUrl,aboutBannerUrl,masterclassBannerUrl:mcBannerUrl,
        testimonials:testimonials.filter(t=>t.quote&&t.quote.trim()),
        updatedAt:new Date().toISOString()},{merge:true});
      setSaved(true); setTimeout(()=>setSaved(false),2000);
    } catch(err) {
      console.error('Failed to save catalog config:', err);
      setSaveError(err?.message || 'Save failed — check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tab-content"><div className="empty-state">Loading…</div></div>;

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div><h1 className="tab-title">Catalog Settings</h1><p className="tab-sub">Coach profile and contact info — shown in the client catalog and on the public landing page.</p></div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:18,maxWidth:600}}>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Coach Name</label>
          <input value={coachName} onChange={e=>setCoachName(e.target.value)}
            style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
        </div>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Coach Title / Role</label>
          <input value={coachRole} onChange={e=>setCoachRole(e.target.value)} placeholder="e.g. Meditation Coach & Founder"
            style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
        </div>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Coach Photo</label>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:4}}>
            {coachImageUrl && <img src={coachImageUrl} alt="" style={{width:72,height:72,borderRadius:'50%',objectFit:'cover',flexShrink:0,border:'3px solid var(--bark)'}} />}
            <DropUpload accept="image/*" label="Coach photo" previewUrl={null}
              previewType="image" uploading={imgUploading} progress={imgProgress}
              onFile={async file => {
                setImgUploading(true); setImgProgress(0);
                try {
                  const url=await uploadFile('config/coach-photo',file,p=>setImgProgress(p));
                  setCoachImageUrl(url);
                } catch(err) { alert('Upload failed: ' + (err?.message||err)); }
                finally { setImgUploading(false); }
              }} />
          </div>
        </div>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Coach Overview</label>
          <textarea value={coachOverview} onChange={e=>setCoachOverview(e.target.value)} rows={5} placeholder="Bio shown at the top of the client catalog…"
            style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
        </div>
        <div>
          <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Contact Information</label>
          <textarea value={contacts} onChange={e=>setContacts(e.target.value)} rows={4} placeholder="Phone, email, social links…"
            style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
        </div>

        <div style={{borderTop:'2px solid var(--border)',paddingTop:20,marginTop:4}}>
          <div style={{fontSize:'.8rem',fontWeight:700,color:'var(--soil)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:16}}>Landing Page Wording</div>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <div>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Hero Title</label>
              <input value={heroTitle} onChange={e=>setHeroTitle(e.target.value)} placeholder="Become Who You're Meant to Be."
                style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
            </div>
            <div>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Hero Tagline</label>
              <input value={heroTagline} onChange={e=>setHeroTagline(e.target.value)} placeholder="Your Journey. Your Truth. Your Transformation."
                style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
            </div>
            <div>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Hero Description</label>
              <textarea value={heroDesc} onChange={e=>setHeroDesc(e.target.value)} rows={3} placeholder="Beyond The Body offers transformative programs…"
                style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
            </div>
            <div>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>About Section Text</label>
              <textarea value={aboutLead} onChange={e=>setAboutLead(e.target.value)} rows={4} placeholder="Beyond The Body is a platform for complete life transformation…"
                style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:8,fontFamily:"'Inter',sans-serif",fontSize:'.9rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
            </div>
            <div>
              <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>Benefit Cards (About section)</label>
              <div style={{fontSize:'.72rem',color:'var(--stone)',marginBottom:10}}>Four cards shown under the About heading. Icons are fixed; leave a field blank to use the default text.</div>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {benefits.map((b,i)=>{
                  const ph=[
                    {t:'Discover Your Purpose',d:'Uncover what truly drives you and align your life with your deepest values.'},
                    {t:'Transform Your Mind',d:'Break limiting beliefs and rewire your thinking through guided daily practice.'},
                    {t:'Elevate Your Energy',d:'Cultivate inner energy and vitality that radiates into every area of your life.'},
                    {t:'Live Beyond Limitations',d:'Step into a life of freedom, confidence, and unlimited possibility.'},
                  ][i];
                  const upd=(field,val)=>setBenefits(bs=>bs.map((x,idx)=>idx===i?{...x,[field]:val}:x));
                  return (
                    <div key={i} style={{border:'1px solid var(--border)',borderRadius:8,padding:12,display:'flex',flexDirection:'column',gap:8}}>
                      <input value={b.title} onChange={e=>upd('title',e.target.value)} placeholder={ph.t}
                        style={{width:'100%',padding:'9px 11px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.88rem',fontWeight:600,background:'white',color:'var(--text)',boxSizing:'border-box'}} />
                      <textarea value={b.desc} onChange={e=>upd('desc',e.target.value)} rows={2} placeholder={ph.d}
                        style={{width:'100%',padding:'9px 11px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.85rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div style={{borderTop:'2px solid var(--border)',paddingTop:20,marginTop:4}}>
          <div style={{fontSize:'.8rem',fontWeight:700,color:'var(--soil)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>Landing Page Images</div>
          <div style={{fontSize:'.72rem',color:'var(--stone)',marginBottom:16}}>Large images shown on the public homepage. Leave blank to use a plain gradient.</div>
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {[
              ['hero','Hero background','heroImageUrl',heroImageUrl,setHeroImageUrl],
              ['about-banner','About section banner','aboutBannerUrl',aboutBannerUrl,setAboutBannerUrl],
              ['masterclass-banner','Masterclasses banner','masterclassBannerUrl',mcBannerUrl,setMcBannerUrl],
            ].map(([key,label,,url,setter])=>(
              <div key={key}>
                <label style={{display:'block',fontSize:'.75rem',fontWeight:600,color:'var(--bark)',marginBottom:5,textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</label>
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {url && <img src={url} alt="" style={{width:96,height:54,borderRadius:8,objectFit:'cover',flexShrink:0,border:'1px solid var(--border)'}} />}
                  <DropUpload accept="image/*" label={label} previewUrl={null} previewType="image"
                    uploading={bannerKey===key} progress={bannerProgress}
                    onFile={async file=>{ setBannerKey(key); setBannerProgress(0); try { const u=await uploadFile(`config/${key}`,file,p=>setBannerProgress(p)); setter(u); } catch(e){ alert('Upload failed: '+(e?.message||e)); } finally { setBannerKey(''); } }} />
                  {url && <button onClick={()=>setter('')} style={{background:'white',color:'#C0392B',border:'1px solid #E8C4BE',borderRadius:7,padding:'7px 10px',fontSize:'.78rem',cursor:'pointer',flexShrink:0}}>Remove</button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{borderTop:'2px solid var(--border)',paddingTop:20,marginTop:4}}>
          <div style={{fontSize:'.8rem',fontWeight:700,color:'var(--soil)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>Testimonials</div>
          <div style={{fontSize:'.72rem',color:'var(--stone)',marginBottom:16}}>Client quotes shown in the "What Our Clients Say" section. While empty, three sample placeholders are shown on the live site — add real quotes to replace them.</div>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {testimonials.map((t,i)=>{
              const upd=(field,val)=>setTestimonials(ts=>ts.map((x,idx)=>idx===i?{...x,[field]:val}:x));
              return (
                <div key={i} style={{border:'1px solid var(--border)',borderRadius:8,padding:12,display:'flex',flexDirection:'column',gap:8}}>
                  <textarea value={t.quote||''} onChange={e=>upd('quote',e.target.value)} rows={2} placeholder="What the client said…"
                    style={{width:'100%',padding:'9px 11px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.88rem',background:'white',color:'var(--text)',boxSizing:'border-box',resize:'vertical'}} />
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <input value={t.name||''} onChange={e=>upd('name',e.target.value)} placeholder="Name (e.g. Amina H.)"
                      style={{flex:'1 1 140px',padding:'9px 11px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.85rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
                    <input value={t.role||''} onChange={e=>upd('role',e.target.value)} placeholder="Role / city (e.g. Arusha)"
                      style={{flex:'1 1 140px',padding:'9px 11px',border:'1px solid var(--border)',borderRadius:7,fontFamily:"'Inter',sans-serif",fontSize:'.85rem',background:'white',color:'var(--text)',boxSizing:'border-box'}} />
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    {t.photoUrl && <img src={t.photoUrl} alt="" style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',flexShrink:0,border:'2px solid var(--bark)'}} />}
                    <DropUpload accept="image/*" label="Photo (optional)" previewUrl={null} previewType="image"
                      uploading={bannerKey===`testi-${i}`} progress={bannerProgress}
                      onFile={async file=>{ setBannerKey(`testi-${i}`); setBannerProgress(0); try { const u=await uploadFile(`config/testimonials/${i}-${Date.now()}`,file,p=>setBannerProgress(p)); upd('photoUrl',u); } catch(e){ alert('Upload failed: '+(e?.message||e)); } finally { setBannerKey(''); } }} />
                    {t.photoUrl && <button onClick={()=>upd('photoUrl','')} style={{background:'white',color:'#C0392B',border:'1px solid #E8C4BE',borderRadius:7,padding:'7px 10px',fontSize:'.78rem',cursor:'pointer'}}>Clear photo</button>}
                    <button onClick={()=>setTestimonials(ts=>ts.filter((_,idx)=>idx!==i))}
                      style={{marginLeft:'auto',background:'white',color:'#C0392B',border:'1px solid #E8C4BE',borderRadius:7,padding:'7px 12px',fontSize:'.78rem',cursor:'pointer'}}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="btn-upload btn-replace" onClick={()=>setTestimonials(ts=>[...ts,{quote:'',name:'',role:'',photoUrl:''}])} style={{marginTop:12}}>+ Add testimonial</button>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:8,alignSelf:'flex-start'}}>
          <button className="btn-upload" onClick={save} disabled={saving||imgUploading||!!bannerKey} style={{alignSelf:'flex-start'}}>
            {saving?'Saving…':saved?'Saved ✓':'Save Changes'}
          </button>
          {saveError && <div style={{fontSize:'.8rem',color:'#C0392B',padding:'6px 10px',background:'#FBF0EE',borderRadius:6,border:'1px solid #E8C4BE'}}>{saveError}</div>}
        </div>
      </div>
    </div>
  );
}

// ─── INACTIVITY ALERT ─────────────────────────────────────────────────────────
function InactivityAlert({ inactive, onUpdateClient }) {
  const [busy, setBusy] = useState({});
  const [nudged, setNudged] = useState({});
  const [hidden, setHidden] = useState({}); // removed from view this session

  const SNOOZE_MS = 7 * 86400000;

  const persist = async (client, updates) => {
    await setDoc(doc(db, 'users', client.id), updates, { merge: true });
    if (onUpdateClient) onUpdateClient(client.id, updates);
  };

  // Nudge on two channels: open WhatsApp pre-filled (the coach taps send — WhatsApp
  // can't be sent programmatically without the paid Business API) AND fire the email.
  // WhatsApp opens FIRST and synchronously: any `await` before window.open would let
  // the popup blocker kill it.
  const sendNudge = (client) => {
    openNudgeWhatsApp(client); // synchronous first — popup blockers
    setBusy(p => ({ ...p, [client.id]: true }));
    (async () => {
      try {
        await sendNudgeEmail(client);
        // Nudging auto-clears the client from the list for 7 days.
        await persist(client, { inactivitySnoozedUntil: new Date(Date.now()+SNOOZE_MS).toISOString(), lastNudgedAt: new Date().toISOString() });
        setNudged(p => ({ ...p, [client.id]: true }));
      } catch (e) { alert('Failed to send: ' + e.message); }
      setBusy(p => ({ ...p, [client.id]: false }));
    })();
  };

  const snooze = async (client) => {
    setBusy(p => ({ ...p, [client.id]: true }));
    try {
      await persist(client, { inactivitySnoozedUntil: new Date(Date.now()+SNOOZE_MS).toISOString() });
      setHidden(p => ({ ...p, [client.id]: true }));
    } catch (e) { alert('Failed: ' + e.message); }
    setBusy(p => ({ ...p, [client.id]: false }));
  };

  const dismiss = async (client) => {
    setBusy(p => ({ ...p, [client.id]: true }));
    try {
      await persist(client, { inactivityDismissedAt: new Date().toISOString() });
      setHidden(p => ({ ...p, [client.id]: true }));
    } catch (e) { alert('Failed: ' + e.message); }
    setBusy(p => ({ ...p, [client.id]: false }));
  };

  const visible = inactive.filter(c => !hidden[c.id]);
  if (visible.length === 0) return null;

  const btn = (bg) => ({ background:bg, color:'white', border:'none', borderRadius:6, padding:'5px 10px', fontSize:'.72rem', cursor:'pointer', fontFamily:"'Inter',sans-serif", flexShrink:0 });

  return (
    <div style={{background:"#FBF0EE",border:"1px solid #E8C4BE",borderRadius:10,padding:"12px 16px",marginBottom:12}}>
      <div style={{fontSize:".85rem",color:"#8C3A2E",marginBottom:8}}>
        ⚠️ <strong>{visible.length}</strong> client{visible.length>1?"s":""} inactive for 3+ days
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {visible.map(c => {
          const days = Math.floor((Date.now()-new Date(c.lastSeen).getTime())/86400000);
          const isBusy = busy[c.id];
          // What a nudge can actually reach them on — so "Sent" never overstates.
          const channels = nudgeChannels(c).join(' + ');
          return (
            <div key={c.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,background:'white',borderRadius:7,padding:'8px 12px',flexWrap:'wrap'}}>
              <div>
                <span style={{fontSize:'.85rem',color:'var(--text)',fontWeight:500}}>{c.name||c.email}</span>
                <span style={{fontSize:'.75rem',color:'var(--stone)',marginLeft:8}}>{days}d inactive</span>
                <span style={{fontSize:'.7rem',color:channels?'var(--stone)':'#B4544F',marginLeft:8}}>
                  {channels ? `· ${channels}` : '· no contact details'}
                </span>
              </div>
              <div style={{display:'flex',gap:6,flexShrink:0}}>
                <button onClick={() => sendNudge(c)} disabled={isBusy||nudged[c.id]||!channels}
                  title={channels ? `Nudge via ${channels}` : 'No phone or email on file'}
                  style={{...btn(nudged[c.id]?'var(--moss)':'var(--bark)'), opacity: channels ? 1 : .5, cursor: channels ? 'pointer' : 'not-allowed'}}>
                  {isBusy?'…':nudged[c.id]?'✓ Sent':'Send nudge'}
                </button>
                <button onClick={() => snooze(c)} disabled={isBusy} title="Hide for 7 days; reappears if still inactive" style={btn('var(--stone)')}>Snooze 7d</button>
                <button onClick={() => dismiss(c)} disabled={isBusy} title="Hide until they next open the app" style={btn('var(--stone)')}>Dismiss</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── HOME TAB ─────────────────────────────────────────────────────────────────
function HomeTab({ clients, onOpenClient, onDrill, onUpdateClient }) {
  const [programs, setPrograms] = useState([]);
  const [dismissing, setDismissing] = useState({}); // leads being removed from the action queue
  const [allPayments, setAllPayments] = useState([]);
  const [sessionStats, setSessionStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'programs')),
      getDocs(collection(db, 'customPrograms')),
      getDocs(collection(db, 'payments')),
    ]).then(async ([progSnap, custSnap, paySnap]) => {
      const uploadedProgs = progSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.published);
      const custProgs = custSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPrograms([...uploadedProgs, ...custProgs]);

      const pays = paySnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllPayments(pays);

      const stats = {};
      for (const prog of uploadedProgs) {
        try {
          const sessSnap = await getDocs(collection(db, 'programs', prog.id, 'sessions'));
          stats[prog.id] = sessSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(isPlayableSession);
        } catch {}
      }
      setSessionStats(stats);
      setLoading(false);
    });
  }, [clients]);

  const now = Date.now();
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));

  // Dismissed leads stay in the records (Clients tab) but drop out of the dashboard
  // action queue — for people not worth chasing or who were just trying the service.
  const notDismissed = c => !c.leadDismissedAt;

  const paid = clients.filter(c => c.paid);
  const pendingAll = clients.filter(c => c.paymentPending && !c.paid && notDismissed(c));
  const inactiveAll = clients.filter(c => isInactiveClient(c, now));
  const newThisWeekAll = clients.filter(c => c.joinedAt && (now - new Date(c.joinedAt).getTime()) < 7 * 86400000 && notDismissed(c));

  // Graduates — finished everything they paid for. Kept out of the at-risk list and
  // surfaced here instead: testimonial / next-course candidates.
  const completedAll = clients.filter(c => c.allCompleted && notDismissed(c))
    .sort((a, b) => (b.lastCompletedAt || '').localeCompare(a.lastCompletedAt || ''));

  // Soft-launch funnel: where people fall off between signing up and paying.
  const reachedNotConfirmed = clients.filter(c => c.reachedPaymentAt && !c.paymentPending && !c.paid && notDismissed(c));
  const newSignupsUnpaid = clients.filter(c => !c.paid && !c.paymentPending && !c.reachedPaymentAt && notDismissed(c)
    && c.joinedAt && (now - new Date(c.joinedAt).getTime()) < 14 * 86400000);
  // Monotonic funnel counts (each stage is a superset condition of the next).
  const fSignedUp   = clients.length;
  const fReachedPay = clients.filter(c => c.reachedPaymentAt || c.paymentPending || c.paid).length;
  const fTriedPay   = clients.filter(c => c.paymentPending || c.paid).length;
  const fPaid       = paid.length;
  const pctOf = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const funnelStages = [
    { label: "Signed up",       value: fSignedUp,   pct: null },
    { label: "Reached payment", value: fReachedPay, pct: pctOf(fReachedPay, fSignedUp) },
    { label: "Tried to pay",    value: fTriedPay,   pct: pctOf(fTriedPay, fReachedPay) },
    { label: "Paid",            value: fPaid,       pct: pctOf(fPaid, fTriedPay) },
  ];
  const agoLabel = iso => { if (!iso) return "—"; const d = Math.floor((now - new Date(iso).getTime()) / 86400000); return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d}d ago`; };

  const dismissLead = async (c, e) => {
    if (e) e.stopPropagation();
    setDismissing(p => ({ ...p, [c.id]: true }));
    try {
      const updates = { leadDismissedAt: new Date().toISOString() };
      await setDoc(doc(db, 'users', c.id), updates, { merge: true });
      if (onUpdateClient) onUpdateClient(c.id, updates); // parent re-render drops the row
    } catch (err) {
      alert('Failed to dismiss: ' + err.message);
      setDismissing(p => ({ ...p, [c.id]: false }));
    }
  };
  // Real money only: AzamPay confirms genuine mobile-money payments with a transactionRef.
  // Manual/admin approvals (free access) have none, so they never inflate revenue.
  const totalRevenueAll = allPayments.filter(p => p.status === 'approved' && p.transactionRef).reduce((s, p) => s + Number(p.amount || 0), 0);
  const recentPayments = [...allPayments].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 6);

  const programStats = programs.map(prog => {
    const progPays = allPayments.filter(p => p.programId === prog.id);
    const approved = progPays.filter(p => p.status === 'approved');
    const pending = progPays.filter(p => p.status === 'pending');
    const enrolledClients = approved.map(p => clientMap[p.userId]).filter(Boolean);
    const inactive = enrolledClients.filter(c => isInactiveClient(c, now));
    const newThisWeek = approved.filter(p => { const d = p.approvedAt || p.createdAt; return d && (now - new Date(d).getTime()) < 7 * 86400000; });
    const revenue = approved.filter(p => p.transactionRef).reduce((s, p) => s + Number(p.amount || 0), 0);
    const sessions = sessionStats[prog.id];
    let completed = 0;
    if (sessions && sessions.length > 0) {
      enrolledClients.forEach(c => { if (sessions.every(s => (c.progress?.[s.id]?.percent ?? 0) >= 80)) completed++; });
    }
    return { prog, enrolled: approved.length, pending: pending.length, inactive: inactive.length, newThisWeek: newThisWeek.length, revenue, completed, hasSessionData: !!(sessions && sessions.length > 0) };
  }).filter(s => s.enrolled > 0 || s.pending > 0);

  const initials = (c) => (c?.name || c?.email || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  // Revenue trend (last 6 months) — real money only (AzamPay-confirmed).
  const realPays = allPayments.filter(p => p.status === "approved" && p.transactionRef);
  const base = new Date();
  const trendPoints = Array.from({ length: 6 }, (_, k) => {
    const i = 5 - k;
    const start = new Date(base.getFullYear(), base.getMonth() - i, 1).getTime();
    const end = new Date(base.getFullYear(), base.getMonth() - i + 1, 1).getTime();
    const value = realPays.reduce((s, p) => { const t = new Date(p.approvedAt || p.createdAt || 0).getTime(); return (t >= start && t < end) ? s + Number(p.amount || 0) : s; }, 0);
    return { label: new Date(start).toLocaleDateString("en-GB", { month: "short" }), value };
  });
  const thisMonthRevenue = trendPoints[trendPoints.length - 1]?.value || 0;
  const weekStart = (() => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const thisWeekRevenue = realPays.reduce((s, p) => { const t = new Date(p.approvedAt || p.createdAt || 0).getTime(); return t >= weekStart ? s + Number(p.amount || 0) : s; }, 0);

  if (loading) return <div className="tab-content"><div className="tab-header"><div><h1 className="tab-title">Dashboard</h1></div></div><Skeleton rows={5} /></div>;

  const AttnGroup = ({ title, tone, items, render, viewAll }) => (
    <div style={{ marginBottom: items.length ? 6 : 0 }}>
      <div className="sec-head" style={{ marginBottom: 6 }}>
        <span className="sec-title" style={{ fontSize: ".82rem" }}>{title}<span className="sec-count">{items.length}</span></span>
        {items.length > 4 && viewAll && <button className="sec-link" onClick={viewAll}>View all →</button>}
      </div>
      {items.length === 0
        ? <div style={{ fontSize: ".78rem", color: "var(--stone)", padding: "2px 4px 8px" }}>All clear.</div>
        : <div className="card" style={{ marginBottom: 4 }}>{items.slice(0, 4).map(render)}</div>}
    </div>
  );

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div><h1 className="tab-title">Dashboard</h1><p className="tab-sub">Overview of your platform activity.</p></div>
      </div>

      {/* KPI row — every card drills into Clients */}
      <div className="kpi-row">
        <KpiCard value={paid.length} label="Active clients" accent="var(--moss)" onClick={() => onDrill({ status: "active" })} />
        <KpiCard value={pendingAll.length} label="Awaiting approval" accent="var(--warn)" onClick={() => onDrill({ status: "pending" })} />
        <KpiCard value={inactiveAll.length} label="Inactive 3+ days" danger={inactiveAll.length > 0} onClick={() => onDrill({ activity: "atrisk" })} />
        <KpiCard value={newThisWeekAll.length} label="New this week" accent="var(--bark)" onClick={() => onDrill({ activity: "new" })} />
        <KpiCard value={`TZS ${totalRevenueAll.toLocaleString()}`} label="Total revenue" accent="var(--moss)" sub={`TZS ${thisMonthRevenue.toLocaleString()} this month`} />
      </div>

      {/* Conversion funnel — where people fall off from signup to payment */}
      <div className="sec">
        <div className="sec-head"><span className="sec-title">Conversion funnel</span></div>
        <div className="card" style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: 16, alignItems: "stretch" }}>
          {funnelStages.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <div style={{ alignSelf: "center", color: "var(--stone)", fontSize: "1rem" }}>→</div>}
              <div style={{ flex: "1 1 120px", minWidth: 110 }}>
                <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--soil)", lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: ".72rem", color: "var(--stone)", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 2 }}>{s.label}</div>
                {s.pct !== null && (
                  <div style={{ fontSize: ".72rem", color: s.pct >= 50 ? "var(--moss)" : "var(--warn)", marginTop: 2 }}>{s.pct}% of previous</div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Needs attention */}
      <div className="sec">
        <div className="sec-head"><span className="sec-title">Needs attention</span></div>
        <AttnGroup title="Awaiting approval" items={pendingAll} viewAll={() => onDrill({ status: "pending" })}
          render={c => (
            <div key={c.id} className="arow">
              <button className="arow-open" onClick={() => onOpenClient(c)}>
                <span className="arow-av">{initials(c)}</span>
                <span className="arow-main"><span className="arow-title">{c.name || c.email || "Unknown"}</span><span className="arow-sub">{c.activeProgramTitle || c.phone || "—"}</span></span>
              </button>
              <span className="arow-actions">
                <Badge tone="warn">Pending</Badge>
                <button className="btn-sm btn-sm--primary" onClick={() => onOpenClient(c)}>Review</button>
                <button className="arow-dismiss" title="Dismiss — remove from your action queue" disabled={dismissing[c.id]} onClick={e => dismissLead(c, e)}>Dismiss</button>
              </span>
            </div>
          )} />
        {reachedNotConfirmed.length > 0 && (
          <AttnGroup title="Reached checkout — didn't confirm" items={reachedNotConfirmed} viewAll={() => onDrill({ status: "unpaid" })}
            render={c => (
              <div key={c.id} className="arow">
                <button className="arow-open" onClick={() => onOpenClient(c)}>
                  <span className="arow-av">{initials(c)}</span>
                  <span className="arow-main"><span className="arow-title">{c.name || c.email || "Unknown"}</span><span className="arow-sub">Reached checkout {agoLabel(c.reachedPaymentAt)}</span></span>
                </button>
                <span className="arow-actions">
                  <Badge tone="warn">Stalled</Badge>
                  <button className="btn-sm btn-sm--primary" onClick={() => onOpenClient(c)}>Review</button>
                  <button className="arow-dismiss" title="Dismiss — remove from your action queue" disabled={dismissing[c.id]} onClick={e => dismissLead(c, e)}>Dismiss</button>
                </span>
              </div>
            )} />
        )}
        {newSignupsUnpaid.length > 0 && (
          <AttnGroup title="New signups — not yet paid" items={newSignupsUnpaid} viewAll={() => onDrill({ status: "unpaid" })}
            render={c => (
              <div key={c.id} className="arow">
                <button className="arow-open" onClick={() => onOpenClient(c)}>
                  <span className="arow-av">{initials(c)}</span>
                  <span className="arow-main"><span className="arow-title">{c.name || c.email || "Unknown"}</span><span className="arow-sub">Joined {agoLabel(c.joinedAt)}</span></span>
                </button>
                <span className="arow-actions">
                  <Badge tone="stone">Signed up</Badge>
                  <button className="arow-dismiss" title="Dismiss — remove from your action queue" disabled={dismissing[c.id]} onClick={e => dismissLead(c, e)}>Dismiss</button>
                </span>
              </div>
            )} />
        )}
        {inactiveAll.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <div className="sec-head" style={{ marginBottom: 6 }}>
              <span className="sec-title" style={{ fontSize: ".82rem" }}>Inactive 3+ days<span className="sec-count">{inactiveAll.length}</span></span>
              {inactiveAll.length > 4 && <button className="sec-link" onClick={() => onDrill({ activity: "atrisk" })}>View all →</button>}
            </div>
            <InactivityAlert inactive={inactiveAll} />
          </div>
        )}
        {completedAll.length > 0 && (
          <AttnGroup title="Completed — ready for what's next" items={completedAll} viewAll={() => onDrill({ status: "completed" })}
            render={c => (
              <div key={c.id} className="arow">
                <button className="arow-open" onClick={() => onOpenClient(c)}>
                  <span className="arow-av">{initials(c)}</span>
                  <span className="arow-main">
                    <span className="arow-title">{c.name || c.email || "Unknown"}</span>
                    <span className="arow-sub">Finished {c.activeProgramTitle || 'their program'} · {agoLabel(c.lastCompletedAt)}</span>
                  </span>
                </button>
                <span className="arow-actions">
                  <Badge tone="violet">Completed</Badge>
                  {waNumber(c.phone) && (
                    <a className="btn-sm btn-sm--primary" href={`https://wa.me/${waNumber(c.phone)}`} target="_blank" rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }} onClick={e => e.stopPropagation()}>WhatsApp</a>
                  )}
                  <button className="arow-dismiss" title="Dismiss — remove from your action queue" disabled={dismissing[c.id]} onClick={e => dismissLead(c, e)}>Dismiss</button>
                </span>
              </div>
            )} />
        )}
        <AttnGroup title="New this week" items={newThisWeekAll} viewAll={() => onDrill({ activity: "new" })}
          render={c => (
            <div key={c.id} className="arow">
              <button className="arow-open" onClick={() => onOpenClient(c)}>
                <span className="arow-av">{initials(c)}</span>
                <span className="arow-main"><span className="arow-title">{c.name || c.email || "Unknown"}</span><span className="arow-sub">Joined {c.joinedAt ? new Date(c.joinedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</span></span>
              </button>
              <span className="arow-actions">
                {c.paid ? <Badge tone="ok">Active</Badge> : <Badge tone="stone">Signed up</Badge>}
                <button className="arow-dismiss" title="Dismiss — remove from your action queue" disabled={dismissing[c.id]} onClick={e => dismissLead(c, e)}>Dismiss</button>
              </span>
            </div>
          )} />
      </div>

      {/* Revenue & growth */}
      <div className="sec">
        <div className="sec-head"><span className="sec-title">Revenue &amp; growth</span></div>
        <div className="card" style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
            <div><div style={{ fontSize: "1.4rem", fontWeight: 600, color: "var(--soil)", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>TZS {thisWeekRevenue.toLocaleString()}</div><div className="kpi-lab">This week</div></div>
            <div><div style={{ fontSize: "1.4rem", fontWeight: 600, color: "var(--soil)", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>TZS {thisMonthRevenue.toLocaleString()}</div><div className="kpi-lab">This month</div></div>
            <div><div style={{ fontSize: "1.4rem", fontWeight: 600, color: "var(--moss)", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>TZS {totalRevenueAll.toLocaleString()}</div><div className="kpi-lab">All time</div></div>
          </div>
          <TrendChart points={trendPoints} />
        </div>
      </div>

      {/* Per-program performance */}
      {programStats.length > 0 && (
        <div className="sec">
          <div className="sec-head"><span className="sec-title">Program performance</span></div>
          <div className="card prog-perf">
            {programStats.map(({ prog, enrolled, pending, inactive, revenue, completed, hasSessionData }) => {
              const cpct = hasSessionData && enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;
              return (
                <button key={prog.id} className="pp-row" onClick={() => onDrill({ program: prog.id })}>
                  <span className="pp-name">{prog.title || prog.id}</span>
                  <span className="pp-metrics">
                    <span className="pp-metric"><b>{enrolled}</b><span>Active</span></span>
                    <span className="pp-metric"><b style={pending > 0 ? { color: "var(--warn)" } : undefined}>{pending}</b><span>Pending</span></span>
                    <span className="pp-metric"><b style={inactive > 0 ? { color: "var(--danger)" } : undefined}>{inactive}</b><span>At-risk</span></span>
                    <span className="pp-metric"><b style={{ color: "var(--moss)" }}>TZS {revenue.toLocaleString()}</b><span>Revenue</span></span>
                  </span>
                  <span className="pp-completion"><ProgressBar pct={cpct} tone="info" /><small>{hasSessionData ? `${completed} done` : "—"}</small></span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent payments */}
      <div className="sec">
        <div className="sec-head"><span className="sec-title">Recent payments</span></div>
        {recentPayments.length === 0 ? <div className="empty-action"><span>No payments yet.</span></div> : (
          <div className="card">
            {recentPayments.map(p => {
              const c = clientMap[p.userId];
              return (
                <button key={p.id} className="arow" onClick={() => c && onOpenClient(c)}>
                  <span className="arow-main">
                    <span className="arow-title">{p.userName || p.email || (c && (c.name || c.email)) || "Unknown"}</span>
                    <span className="arow-sub">{p.programTitle || p.programId} · {p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</span>
                  </span>
                  <span style={{ fontSize: ".82rem", fontWeight: 600, color: "var(--text)" }}>TZS {Number(p.amount || 0).toLocaleString()}</span>
                  <Badge tone={p.status === "approved" ? "ok" : p.status === "pending" ? "warn" : "stone"}>{p.status}</Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function AdminPanel({ onGoToLanding, onPreviewAsClient }) {
  const [user, setUser] = useState(null); const [authChecked, setAuthChecked] = useState(false); const [loginError, setLoginError] = useState(""); const [isAuthorized, setIsAuthorized] = useState(false); const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('adminTab') || "home"; } catch { return "home"; } }); const [selectedProgram, setSelectedProgram] = useState(PROGRAMS[0].id);
  const [uploads, setUploads] = useState({}); const [slotParts, setSlotParts] = useState({}); const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null); const [expandedDay, setExpandedDay] = useState(1);
  const [searchQuery, setSearchQuery] = useState(""); const [loadingData, setLoadingData] = useState(false);
  const [clientFilter, setClientFilter] = useState("all"); // status: all|active|pending|completed|unpaid|free
  const [activityFilter, setActivityFilter] = useState("any"); // any|new|active|atrisk
  const [programFilter, setProgramFilter] = useState("all");   // program id | all
  const [clientSort, setClientSort] = useState("newest"); // name|newest|oldest|lastseen|progress
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [customPrograms, setCustomPrograms] = useState([]);
  const [showNewProgramModal, setShowNewProgramModal] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);

  // Remember the current tab so a page refresh returns here instead of Home.
  useEffect(() => { try { localStorage.setItem('adminTab', tab); } catch {} }, [tab]);

  // Drill into the Clients view from anywhere (dashboard cards) with facets pre-applied.
  const drillToClients = (facets = {}) => {
    setClientFilter(facets.status ?? "all");
    setActivityFilter(facets.activity ?? "any");
    setProgramFilter(facets.program ?? "all");
    setSearchQuery("");
    setSelectedIds(new Set());
    setTab("clients");
  };
  const openClient = (c) => { setSelectedClient(c); };
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelectedIds(new Set());

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) { setIsAuthorized(false); setIsSuperAdmin(false); setAuthChecked(true); return; }
      const tokenResult = await u.getIdTokenResult();
      if (tokenResult.claims.superAdmin === true) {
        setIsSuperAdmin(true); setIsAuthorized(true); setAuthChecked(true); return;
      }
      setIsSuperAdmin(false);
      const snap = await getDoc(doc(db, "admins", u.uid));
      setIsAuthorized(snap.exists());
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoadingData(true);
    loadClients().then(c => setClients(c)).catch(console.error).finally(() => setLoadingData(false));
  }, [user]);

  const handleLogin = async (email, password) => {
    setLoginError("");
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch { setLoginError("Invalid email or password."); }
  };

  const handleUpload = useCallback((id, data) => setUploads(p => ({...p,[id]:data})), []);
  const handleDelete = useCallback((id) => {
    setUploads(p => { const n = {...p}; delete n[id]; return n; });
    setSlotParts(prev => {
      const next = {...prev};
      Object.keys(next).forEach(k => { next[k] = next[k].filter(sid => sid !== id); });
      return next;
    });
  }, []);

  const handleAddPart = async (day, slot) => {
    const key = `${day}-${slot}`;
    const existingIds = slotParts[key] || [];
    const partNum = existingIds.length + 1;
    const id = `${selectedProgram}-d${day}-${slot}-p${partNum}`;
    const storagePath = `programs/${selectedProgram}/d${day}-${slot}-p${partNum}`;
    try {
      await setDoc(doc(db, "programs", selectedProgram, "sessions", id), {
        day, slot, part: partNum, storagePath, status: "empty",
        createdAt: new Date().toISOString(),
      });
      setUploads(prev => ({ ...prev, [id]: { status: "empty", day, slot, part: partNum, storagePath } }));
      setSlotParts(prev => ({ ...prev, [key]: [...existingIds, id] }));
    } catch (e) { console.error("Failed to add part:", e); }
  };

  const isCustomProgram = customPrograms.some(p => p.id === selectedProgram);
  const currentCustomProg = customPrograms.find(p => p.id === selectedProgram);
  const currentSessions = currentCustomProg?.sessions || [];
  const currentTotal = isCustomProgram ? currentSessions.length : Object.values(slotParts).reduce((sum, ids) => sum + ids.length, 0);
  const uploadedCount = Object.values(uploads).filter(u => u.status==="uploaded"&&u.audioUrl).length;
  const unrepliedCount = 0;
  // Program options for the program facet (union of every client's enrolled programs).
  const programOptions = (() => {
    const m = new Map();
    clients.forEach(c => (c.programs || []).forEach(p => { if (!m.has(p.id)) m.set(p.id, p.title); }));
    return [...m.entries()].map(([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title));
  })();
  const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null;

  const filteredClients = clients.filter(c => {
    // "Completed" = finished every program they paid for (computed per-program in
    // loadClients), not a count against whichever legacy program is selected.
    const completed = !!c.allCompleted;
    const matchesStatus =
      clientFilter === "all" ? true :
      clientFilter === "pending" ? c.paymentPending && !c.paid :
      clientFilter === "active" ? c.paid && !completed :
      clientFilter === "completed" ? c.paid && completed :
      clientFilter === "free" ? c.paid && c.grantedByAdmin :
      clientFilter === "dismissed" ? !!c.leadDismissedAt :
      clientFilter === "unpaid" ? !c.paid && !c.paymentPending : true;
    const seen = daysSince(c.lastSeen);
    const matchesActivity =
      activityFilter === "any" ? true :
      activityFilter === "new" ? isRecentPayment(c) :
      activityFilter === "active" ? (c.paid && seen !== null && seen <= 3) :
      activityFilter === "atrisk" ? isInactiveClient(c) : true;
    const matchesProgram = programFilter === "all" || (c.programs || []).some(p => p.id === programFilter);
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      (c.name||"").toLowerCase().includes(q) ||
      (c.email||"").toLowerCase().includes(q) ||
      (c.phone||"").includes(searchQuery);
    return matchesStatus && matchesActivity && matchesProgram && matchesSearch;
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    if (clientSort === "name") return (a.name || a.email || "").localeCompare(b.name || b.email || "");
    if (clientSort === "newest") return (b.joinedAt || "").localeCompare(a.joinedAt || "");
    if (clientSort === "oldest") return (a.joinedAt || "").localeCompare(b.joinedAt || "");
    if (clientSort === "lastseen") return (b.lastSeen || "").localeCompare(a.lastSeen || "");
    if (clientSort === "progress") return (b.completedSessions || 0) - (a.completedSessions || 0);
    return 0;
  });
  const allFilteredSelected = sortedClients.length > 0 && sortedClients.every(c => selectedIds.has(c.id));
  const [bulkProgram, setBulkProgram] = useState("");
  const toggleSelectAll = () => setSelectedIds(allFilteredSelected ? new Set() : new Set(sortedClients.map(c => c.id)));
  const selectedClients = () => clients.filter(c => selectedIds.has(c.id));

  const exportCsv = (list) => {
    const rows = [["Name","Email","Phone","Status","Program","Joined"],
      ...list.map(c => [c.name||"", c.email||"", c.phone||"", c.paid?"Active":c.paymentPending?"Pending":"Unpaid", c.activeProgramTitle||"", c.joinedAt?new Date(c.joinedAt).toLocaleDateString("en-GB"):""])
    ].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8,"+encodeURIComponent(rows); a.download = "clients.csv"; a.click();
  };

  const bulkMessage = async () => {
    const msg = window.prompt(`Send a direct message to ${selectedIds.size} client(s):`);
    if (!msg) return;
    setBulkBusy(true);
    try {
      const at = new Date().toISOString();
      await Promise.all(selectedClients().map(c => setDoc(doc(db, "users", c.id), { coachDirectMessage: msg, coachDirectMessageAt: at }, { merge: true })));
      setClients(prev => prev.map(c => selectedIds.has(c.id) ? { ...c, coachDirectMessage: msg, coachDirectMessageAt: at } : c));
      clearSelection();
    } catch (e) { console.error(e); alert("Some messages failed: " + (e?.message || e)); }
    setBulkBusy(false);
  };

  const bulkGrant = async () => {
    const programId = bulkProgram;
    if (!programId) { alert("Pick a program to grant access to."); return; }
    const prog = programOptions.find(p => p.id === programId);
    if (!window.confirm(`Grant FREE access to "${prog?.title || programId}" for ${selectedIds.size} client(s)? (does not count as revenue)`)) return;
    setBulkBusy(true);
    try {
      const progSnap = await getDoc(doc(db, "programs", programId));
      const accessType = progSnap.exists() ? (progSnap.data().accessType || "lifetime") : "lifetime";
      const expiresAt = computeExpiresAt(accessType);
      const at = new Date().toISOString();
      await Promise.all(selectedClients().map(async c => {
        const data = { paid: true, enrolledAt: at, startDate: at.split("T")[0], grantedByAdmin: true, accessType };
        if (expiresAt) data.expiresAt = expiresAt;
        await setDoc(doc(db, "enrollments", c.id, "programs", programId), data, { merge: true });
        await setDoc(doc(db, "users", c.id), { paid: true }, { merge: true });
      }));
      setClients(prev => prev.map(c => selectedIds.has(c.id) ? { ...c, paid: true, grantedByAdmin: true } : c));
      setBulkProgram(""); clearSelection();
    } catch (e) { console.error(e); alert("Some grants failed: " + (e?.message || e)); }
    setBulkBusy(false);
  };

  const undismissOne = async (c) => {
    try {
      await setDoc(doc(db, "users", c.id), { leadDismissedAt: null }, { merge: true });
      setClients(prev => prev.map(x => x.id === c.id ? { ...x, leadDismissedAt: null } : x));
    } catch (e) { console.error(e); alert("Failed to restore: " + (e?.message || e)); }
  };

  // Nudge from the Clients list — same two channels as the Home inactivity alert.
  // Not async: WhatsApp must open synchronously from the click or it's blocked.
  const nudgeOne = (c) => {
    openNudgeWhatsApp(c);
    (async () => {
      try {
        await sendNudgeEmail(c);
        const updates = { lastNudgedAt: new Date().toISOString(), inactivitySnoozedUntil: new Date(Date.now() + 7 * 86400000).toISOString() };
        await setDoc(doc(db, "users", c.id), updates, { merge: true });
        setClients(prev => prev.map(x => x.id === c.id ? { ...x, ...updates } : x));
      } catch (e) { console.error("Nudge failed:", e); alert("Nudge failed: " + (e?.message || e)); }
    })();
  };

  const messageOne = async (c) => {
    const msg = window.prompt(`Message ${c.name || c.email || "client"}:`);
    if (!msg) return;
    const at = new Date().toISOString();
    try {
      await setDoc(doc(db, "users", c.id), { coachDirectMessage: msg, coachDirectMessageAt: at }, { merge: true });
      setClients(prev => prev.map(x => x.id === c.id ? { ...x, coachDirectMessage: msg, coachDirectMessageAt: at } : x));
    } catch (e) { console.error(e); alert("Message failed: " + (e?.message || e)); }
  };

  if (!authChecked) return <div className="loading-screen"><style>{CSS}</style><div className="spinner-lg"/></div>;
  if (!user) return <LoginScreen onLogin={handleLogin} error={loginError} />;
  if (!isAuthorized) return <AccessDenied onSignOut={() => signOut(auth)} />;

  return (
    <div className="admin-app"><style>{CSS}</style>
      <aside className="sidebar">
        <div className="sidebar-logo">
          <svg viewBox="0 0 32 32" width="28" height="28" fill="none"><circle cx="16" cy="16" r="14" stroke="#7FB8C2" strokeWidth="1.5"/><circle cx="16" cy="8" r="2" fill="#7FB8C2"/><path d="M14 11.5L8 6" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><path d="M18 11.5L24 6" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><line x1="16" y1="10" x2="16" y2="20" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><line x1="16" y1="20" x2="11" y2="26" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/><line x1="16" y1="20" x2="21" y2="26" stroke="#7FB8C2" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <div><span className="sidebar-brand">Beyond The Body</span><span className="sidebar-role">Admin Portal</span></div>
        </div>

        <nav className="sidebar-nav">
          {[
            {id:"home",label:"Home",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><path d="M2 9L8 3l6 6M4 7.5V13h2.5v-2.5h3V13H12V7.5"/></svg>,badge:""},
            {id:"programs",label:"Programs",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="5" height="5" rx="0.5"/><rect x="9" y="2" width="5" height="5" rx="0.5"/><rect x="2" y="9" width="5" height="5" rx="0.5"/><rect x="9" y="9" width="5" height="5" rx="0.5"/></svg>,badge:""},
            {id:"catalog",label:"Catalog",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><path d="M2 2h5.5l6.5 6.5-5 5L2.5 7 2 2z"/><circle cx="5.5" cy="5.5" r="1" fill="currentColor" stroke="none"/></svg>,badge:""},
            {id:"masterclasses",label:"Masterclasses",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M2 6h12M5 1.5V4M11 1.5V4" strokeLinecap="round"/><circle cx="8" cy="10" r="1.6"/></svg>,badge:""},
            {id:"clients",label:"Clients",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-5 6-5s6 1.7 6 5" strokeLinecap="round"/></svg>,badge:clients.filter(c=>c.paid).length},
            {id:"payments",label:"Payments",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1.5" y="4" width="13" height="9" rx="1.5"/><line x1="1.5" y1="7" x2="14.5" y2="7"/><line x1="4" y1="11" x2="7" y2="11" strokeLinecap="round"/></svg>,badge:""},
            {id:"journal",label:"Reflections",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="1" width="10" height="14" rx="1"/><path d="M5 5h6M5 8h6M5 11h4"/></svg>,badge:unrepliedCount||""},
            {id:"broadcast",label:"Broadcast",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M11 2v12"/><path d="M4 5.5H2a1 1 0 00-1 1v3a1 1 0 001 1h2l7 3V2.5L4 5.5z" strokeLinejoin="round"/><path d="M11 6a3 3 0 010 4"/></svg>,badge:""},
            {id:"feedback",label:"Feedback",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5A6 6 0 002 9.5c0 1.2.35 2.3.95 3.2L2 14l1.8-.9A6 6 0 0014 9.5z"/></svg>,badge:""},
            {id:"links",label:"Ext. Links",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6.5 9.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1m-2 5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1"/></svg>,badge:""},
            ...(isSuperAdmin?[{id:"coaches",label:"Coaches",icon:<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="5" y="1.5" width="6" height="7" rx="3"/><path d="M1.5 8a6.5 6.5 0 0013 0"/><line x1="8" y1="14.5" x2="8" y2="11"/><line x1="5.5" y1="14.5" x2="10.5" y2="14.5"/></svg>,badge:""}]:[])
          ].map(item=>(
            <button key={item.id} className={`nav-item ${tab===item.id?"active":""}`} onClick={()=>setTab(item.id)}>
              <span className="nav-icon">{item.icon}</span><span className="nav-label">{item.label}</span>
              {item.badge !== "" && <span className="nav-badge">{item.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          {onPreviewAsClient && (
            <button className="btn-signout" style={{marginBottom:8,background:'none',border:'1px solid rgba(159,202,210,0.4)',color:'#9FCAD2'}} onClick={onPreviewAsClient}>
              View as client
            </button>
          )}
          {onGoToLanding && (
            <button className="btn-signout" style={{marginBottom:8,background:'none',border:'1px solid rgba(255,255,255,0.15)',color:'rgba(255,255,255,0.55)'}} onClick={onGoToLanding}>
              ← View Site
            </button>
          )}
          <button className="btn-signout" onClick={()=>signOut(auth)}>Sign out</button>
        </div>
      </aside>

      <main className="main-content">
        {loadingData && <div className="loading-overlay"><div className="spinner-lg"/></div>}

        {tab==="home" && <HomeTab clients={clients} onOpenClient={openClient} onDrill={drillToClients} loading={loadingData} onUpdateClient={(id,updates)=>setClients(prev=>prev.map(c=>c.id===id?{...c,...updates}:c))} />}
        {tab==="programs" && <ProgramsTab />}
        {tab==="catalog" && <CatalogTab />}
        {tab==="masterclasses" && <MasterclassesTab />}

        {tab==="clients" && (
          <div className="tab-content">
            <div className="tab-header">
              <div><h1 className="tab-title">Clients</h1><p className="tab-sub">{sortedClients.length} of {clients.length} clients shown</p></div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <button className={`btn-sm${showRevenue?" btn-sm--primary":""}`} onClick={()=>setShowRevenue(v=>!v)}>
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6.5"/><path d="M8 5v6M6.5 5.5h2.5a1.5 1.5 0 010 3H7m0 0h2a1.5 1.5 0 010 3H6.5" strokeLinecap="round"/></svg>
                  Revenue
                </button>
                <button className="btn-sm" onClick={()=>exportCsv(clients)}>
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v8M5 7l3 3 3-3M3 13h10"/></svg>
                  Export
                </button>
              </div>
            </div>

            {/* Clickable KPI chips — each applies a facet */}
            <div className="kpi-row">
              <KpiCard value={clients.filter(c=>c.paid && !c.allCompleted).length} label="Active" accent="var(--moss)" onClick={()=>{setClientFilter('active');setActivityFilter('any');}} active={clientFilter==='active'&&activityFilter==='any'} />
              <KpiCard value={clients.filter(c=>c.paymentPending&&!c.paid).length} label="Pending" accent="var(--warn)" onClick={()=>{setClientFilter('pending');setActivityFilter('any');}} active={clientFilter==='pending'} />
              <KpiCard value={clients.filter(isRecentPayment).length} label="New (48h)" accent="var(--bark)" onClick={()=>{setClientFilter('all');setActivityFilter('new');}} active={activityFilter==='new'} />
              <KpiCard value={clients.filter(c=>isInactiveClient(c)).length} label="At-risk" danger onClick={()=>{setClientFilter('all');setActivityFilter('atrisk');}} active={activityFilter==='atrisk'} />
              <KpiCard value={clients.filter(c=>!c.paid&&!c.paymentPending).length} label="Unpaid" accent="var(--danger)" onClick={()=>{setClientFilter('unpaid');setActivityFilter('any');}} active={clientFilter==='unpaid'} />
            </div>

            {showRevenue && <RevenuePanel clients={clients} />}

            {/* FACETS */}
            <div className="facets">
              <div className="facet-row">
                <span className="facet-label">Status</span>
                <div className="seg">
                  {[["all","All"],["active","Active"],["pending","Pending"],["completed","Completed"],["free","Free"],["unpaid","Unpaid"],["dismissed","Dismissed"]].map(([id,label])=>(
                    <button key={id} className={`seg-btn ${clientFilter===id?"active":""}`} onClick={()=>setClientFilter(id)}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="facet-row">
                <span className="facet-label">Activity</span>
                <div className="seg">
                  {[["any","Any"],["new","New"],["active","Active"],["atrisk","At-risk"]].map(([id,label])=>(
                    <button key={id} className={`seg-btn ${activityFilter===id?"active":""}`} onClick={()=>setActivityFilter(id)}>{label}</button>
                  ))}
                </div>
                {programOptions.length>0 && (
                  <select className="facet-select" value={programFilter} onChange={e=>setProgramFilter(e.target.value)} aria-label="Filter by program">
                    <option value="all">All programs</option>
                    {programOptions.map(p=><option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                )}
                <select className="facet-select" value={clientSort} onChange={e=>setClientSort(e.target.value)} aria-label="Sort clients">
                  <option value="name">Name (A–Z)</option>
                  <option value="newest">Newest joined</option>
                  <option value="oldest">Oldest joined</option>
                  <option value="lastseen">Last seen</option>
                  <option value="progress">Most progress</option>
                </select>
                {(clientFilter!=='all'||activityFilter!=='any'||programFilter!=='all'||searchQuery) &&
                  <button className="sec-link" onClick={()=>{setClientFilter('all');setActivityFilter('any');setProgramFilter('all');setSearchQuery('');}}>Clear filters</button>}
              </div>
              <div className="search-bar" style={{marginBottom:0}}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10 10l3 3"/></svg>
                <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search by name, email, or phone…"/>
              </div>
            </div>

            {/* BULK ACTION BAR */}
            {selectedIds.size>0 && (
              <div className="bulk-bar">
                <span className="bulk-count">{selectedIds.size} selected</span>
                <button className="btn-sm" onClick={bulkMessage} disabled={bulkBusy}>
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9.5A6 6 0 002 9.5c0 1.2.35 2.3.95 3.2L2 14l1.8-.9A6 6 0 0014 9.5z"/></svg>
                  Message
                </button>
                {programOptions.length>0 && (
                  <select className="facet-select" value={bulkProgram} onChange={e=>setBulkProgram(e.target.value)} disabled={bulkBusy} aria-label="Grant access to program" style={{background:'rgba(255,255,255,.12)',color:'#fff',borderColor:'rgba(255,255,255,.22)'}}>
                    <option value="" style={{color:'#1E2E33'}}>Grant access to…</option>
                    {programOptions.map(p=><option key={p.id} value={p.id} style={{color:'#1E2E33'}}>{p.title}</option>)}
                  </select>
                )}
                <button className="btn-sm" onClick={bulkGrant} disabled={bulkBusy||!bulkProgram}>Grant</button>
                <button className="btn-sm" onClick={()=>exportCsv(selectedClients())} disabled={bulkBusy}>Export</button>
                <button className="bulk-clear" onClick={clearSelection}>Clear</button>
              </div>
            )}

            {/* TABLE */}
            {loadingData ? <Skeleton rows={6} /> : (
              <div className="clients-table">
                <div className="clients-thead">
                  <span className="col-check"><input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} aria-label="Select all shown" /></span>
                  <span>Client</span><span>Program</span><span>Status</span><span>Progress</span><span/>
                </div>
                {sortedClients.length===0 ? (
                  <div className="empty-action">
                    <span>{clients.length===0?"No clients yet.":"No clients match these filters."}</span>
                    {clients.length>0 && <button className="btn-sm" onClick={()=>{setClientFilter('all');setActivityFilter('any');setProgramFilter('all');setSearchQuery('');}}>Clear filters</button>}
                  </div>
                ) : sortedClients.map(c=>(
                  <ClientRow key={c.id} client={c}
                    completedTotal={currentTotal}
                    selected={selectedIds.has(c.id)}
                    onToggle={toggleSelect}
                    onOpen={openClient}
                    onMessage={messageOne}
                    onNudge={nudgeOne}
                    onUndismiss={undismissOne} />
                ))}
              </div>
            )}
          </div>
        )}
        {tab==="payments" && <PaymentsTab />}
        {tab==="journal" && <JournalAdminTab clients={clients} onSelectClient={setSelectedClient} />}
        {tab==="broadcast" && <BroadcastTab />}
        {tab==="feedback" && <FeedbackTab />}
        {tab==="links" && <CoachProgramsManager />}
        {tab==="coaches" && <CoachesManager clients={clients} />}
      </main>

      {selectedClient&&<ClientDetail client={selectedClient} onClose={()=>setSelectedClient(null)} onApprove={(id,updates)=>{setClients(prev=>prev.map(c=>c.id===id?{...c,...updates}:c));setSelectedClient(prev=>({...prev,...updates}));}}/>}
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{--cream:#F2F6F7;--parchment:#E3EEF0;--bark:#4F9CA8;--soil:#1F4E5A;--moss:#3E7D6B;--dawn:#9FCAD2;--stone:#8FA6AD;--text:#1E2E33;--light-text:#5A7480;--border:#D6E2E5;--white:#FFFFFF;--ok:#3E7D6B;--ok-bg:#D4E8CF;--warn:#856404;--warn-bg:#FFF3CD;--danger:#8C3A2E;--danger-bg:#F5DDD8;--info:#4F9CA8;--info-bg:#E3EEF0;--violet:#6B3FA0;--violet-bg:#E8D5F5;--shadow-sm:0 1px 2px rgba(20,50,58,.06);--shadow-md:0 4px 16px rgba(20,50,58,.08);--shadow-lg:0 12px 40px rgba(20,50,58,.16)}
body{font-family:'Inter',sans-serif;color:var(--text);background:#E8EFF0}
.loading-screen{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--cream)}
.loading-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(247,243,237,.7);z-index:10}
.spinner-lg{width:36px;height:36px;border:3px solid var(--parchment);border-top-color:var(--bark);border-radius:50%;animation:spin .8s linear infinite}
.mini-spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:white;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.login-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:linear-gradient(160deg,#DCEBED,#A6CDD4)}
.login-box{background:var(--cream);border-radius:16px;padding:36px;width:360px;box-shadow:0 16px 48px rgba(20,50,58,.18)}
.login-logo{display:flex;align-items:center;gap:10px;margin-bottom:4px;font-family:'Plus Jakarta Sans',serif;font-size:1.3rem;font-weight:600;color:var(--soil)}
.login-sub{font-size:.78rem;color:var(--stone);margin-bottom:24px;text-transform:uppercase;letter-spacing:.08em}
.login-error{background:#FBF0EE;border:1px solid #E8C4BE;color:#8C3A2E;font-size:.8rem;padding:10px 12px;border-radius:8px;margin-bottom:16px}
.login-field{margin-bottom:14px}
.login-field label{display:block;font-size:.75rem;font-weight:600;color:var(--bark);margin-bottom:5px;text-transform:uppercase;letter-spacing:.06em}
.login-field input{width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:8px;font-family:'Inter',sans-serif;font-size:.9rem;background:white;color:var(--text)}
.login-field input:focus{outline:none;border-color:var(--bark)}
.btn-login{width:100%;padding:12px;background:var(--soil);color:white;border:none;border-radius:10px;font-family:'Inter',sans-serif;font-size:.9rem;font-weight:500;cursor:pointer;margin-top:8px}
.btn-login:hover:not(:disabled){background:var(--bark)}.btn-login:disabled{opacity:.6;cursor:not-allowed}
.admin-app{display:flex;min-height:100vh;position:relative}
.sidebar{width:230px;flex-shrink:0;background:var(--soil);display:flex;flex-direction:column;position:sticky;top:0;height:100vh;overflow-y:auto}
.sidebar-logo{display:flex;align-items:center;gap:10px;padding:20px 16px 16px;border-bottom:1px solid rgba(255,255,255,.1)}
.sidebar-brand{display:block;font-family:'Plus Jakarta Sans',serif;font-size:1rem;font-weight:600;color:var(--dawn)}
.sidebar-role{display:block;font-size:.65rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.08em}
.program-selector{padding:12px 10px 8px;border-bottom:1px solid rgba(255,255,255,.1)}
.ps-label-top{display:block;font-size:.62rem;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.1em;margin-bottom:6px;padding:0 4px}
.program-btn{width:100%;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:8px 10px;cursor:pointer;text-align:left;margin-bottom:4px;transition:all .15s}
.program-btn:hover{background:rgba(255,255,255,.1)}.program-btn.active{background:rgba(159,202,210,.2);border-color:rgba(159,202,210,.3)}
.program-btn-name{display:block;font-size:.82rem;font-weight:500;color:var(--dawn)}
.program-btn-coach{display:block;font-size:.65rem;color:rgba(255,255,255,.4);margin-top:1px}
.program-btn-add{width:100%;background:none;border:1px dashed rgba(255,255,255,.15);border-radius:8px;padding:7px;color:rgba(255,255,255,.3);font-family:'Inter',sans-serif;font-size:.75rem;cursor:pointer;transition:all .15s}
.program-btn-add:hover{border-color:rgba(255,255,255,.3);color:rgba(255,255,255,.5)}
.sidebar-nav{flex:1;padding:10px 8px;display:flex;flex-direction:column;gap:2px}
.nav-item{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;background:none;border:none;cursor:pointer;color:rgba(255,255,255,.6);font-family:'Inter',sans-serif;font-size:.85rem;width:100%;text-align:left;transition:all .15s}
.nav-item:hover{background:rgba(255,255,255,.08);color:rgba(255,255,255,.9)}.nav-item.active{background:rgba(159,202,210,.2);color:var(--dawn)}
.nav-icon{font-size:.9rem;width:16px;text-align:center;flex-shrink:0}.nav-label{flex:1}
.nav-badge{font-size:.65rem;background:rgba(255,255,255,.12);color:rgba(255,255,255,.6);padding:2px 7px;border-radius:20px}
.nav-item.active .nav-badge{background:rgba(159,202,210,.25);color:var(--dawn)}
.sidebar-footer{padding:16px;border-top:1px solid rgba(255,255,255,.1)}
.ou-label{font-size:.68rem;color:rgba(255,255,255,.4);margin-bottom:6px;text-transform:uppercase;letter-spacing:.06em}
.ou-bar{height:4px;background:rgba(255,255,255,.12);border-radius:2px;overflow:hidden;margin-bottom:4px}
.ou-fill{height:100%;background:var(--dawn);border-radius:2px;transition:width .5s}
.ou-count{font-size:.72rem;color:rgba(255,255,255,.5);margin-bottom:12px}
.btn-signout{width:100%;padding:7px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);color:rgba(255,255,255,.5);border-radius:7px;font-family:'Inter',sans-serif;font-size:.75rem;cursor:pointer;transition:all .15s}
.btn-signout:hover{background:rgba(255,255,255,.14);color:rgba(255,255,255,.8)}
.main-content{flex:1;overflow-y:auto;padding:32px;min-width:0;position:relative}
.tab-content{max-width:860px}
.tab-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;gap:16px;flex-wrap:wrap}
.tab-title{font-family:'Plus Jakarta Sans',serif;font-size:1.8rem;font-weight:600;color:var(--soil);margin-bottom:4px}
.tab-sub{font-size:.83rem;color:var(--light-text);font-weight:300}
.header-stats{display:flex;gap:12px;flex-wrap:wrap}
.hstat{background:var(--white);border:1px solid var(--border);border-radius:10px;padding:10px 16px;text-align:center}
.hstat-val{display:block;font-size:1.4rem;font-weight:600;color:var(--soil)}
.hstat-lab{font-size:.68rem;color:var(--stone);text-transform:uppercase;letter-spacing:.06em}
.day-section{border:1px solid var(--border);border-radius:12px;background:var(--white);overflow:hidden;margin-bottom:8px}
.day-toggle{width:100%;background:none;border:none;cursor:pointer;padding:14px 20px;display:flex;align-items:center;justify-content:space-between;font-family:'Inter',sans-serif;transition:background .15s}
.day-toggle:hover{background:rgba(79,156,168,.04)}
.day-toggle-left{display:flex;align-items:center;gap:10px}
.dt-day{font-size:.72rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.08em;min-width:38px}
.dt-title{font-size:.9rem;font-weight:500;color:var(--text)}
.dt-tag{font-size:.65rem;background:var(--parchment);color:var(--bark);padding:2px 7px;border-radius:20px}
.day-toggle-right{display:flex;align-items:center;gap:10px}
.dt-count{font-size:.75rem;color:var(--stone)}
.dt-chevron{color:var(--stone);transition:transform .2s}.dt-chevron.open{transform:rotate(180deg)}
.day-rows{border-top:1px solid var(--border);padding:8px 12px;display:flex;flex-direction:column;gap:6px}
.upload-row{display:flex;align-items:center;padding:10px 12px;border-radius:8px;gap:12px;border:1px dashed transparent;transition:all .15s}
.upload-row.empty{background:#FAFAF8;border-color:var(--border)}
.upload-row.uploaded{background:#EDF5EB;border-color:#C4D4BF;border-style:solid}
.upload-row.drag-over{background:var(--parchment);border-color:var(--bark)}
.upload-thumb{width:48px;height:48px;border-radius:8px;border:1px solid var(--border);overflow:hidden;flex-shrink:0;cursor:pointer;background:var(--parchment);display:flex;align-items:center;justify-content:center;position:relative;transition:all .15s}
.upload-thumb:hover{border-color:var(--bark);opacity:.85}
.thumb-placeholder{display:flex;align-items:center;justify-content:center;font-size:1.4rem;width:100%;height:100%}
.thumb-progress{position:absolute;bottom:0;left:0;right:0;background:rgba(0,0,0,.5);color:white;font-size:.6rem;text-align:center;padding:1px 0}
.upload-row-left{flex:1;min-width:0}
.upload-meta{display:flex;flex-direction:column;gap:2px}
.upload-label{font-size:.82rem;font-weight:500;color:var(--text)}
.upload-path{font-size:.68rem;color:var(--stone);font-family:monospace}
.upload-filename{font-size:.72rem;color:var(--moss)}
.upload-row-right{display:flex;align-items:center;gap:8px;flex-shrink:0;flex-wrap:wrap;justify-content:flex-end}
.upload-error{font-size:.72rem;color:#8C3A2E}
.badge-uploaded{font-size:.7rem;color:var(--moss);background:#D4E8CF;padding:3px 9px;border-radius:20px}
.badge-thumb{font-size:.7rem;color:var(--bark);background:var(--parchment);padding:3px 9px;border-radius:20px}
.btn-upload{background:var(--soil);color:white;border:none;border-radius:6px;padding:6px 14px;font-size:.78rem;cursor:pointer;font-family:'Inter',sans-serif;font-weight:500;transition:background .15s}
.btn-upload:hover{background:var(--bark)}.btn-replace{background:var(--parchment);color:var(--soil)}
.btn-delete{background:none;border:1px solid #E8C4BE;color:#8C3A2E;border-radius:6px;padding:4px 8px;font-size:.72rem;cursor:pointer;transition:all .15s}
.btn-delete:hover{background:#FBF0EE}
.uploading-state{display:flex;align-items:center;gap:8px}
.upload-progress-bar{width:80px;height:5px;background:var(--parchment);border-radius:3px;overflow:hidden}
.upload-progress-fill{height:100%;background:var(--moss);border-radius:3px;transition:width .2s}
.uploading-state span{font-size:.72rem;color:var(--bark);min-width:28px}
.search-bar{display:flex;align-items:center;gap:10px;background:var(--white);border:1px solid var(--border);border-radius:10px;padding:10px 14px;margin-bottom:12px;color:var(--stone)}
.search-bar input{flex:1;border:none;background:none;font-family:'Inter',sans-serif;font-size:.85rem;color:var(--text)}
.search-bar input:focus{outline:none}.search-bar input::placeholder{color:var(--stone)}
.clients-table{background:var(--white);border:1px solid var(--border);border-radius:12px;overflow:hidden}
.clients-thead{display:grid;grid-template-columns:1fr 120px 80px 140px 24px;padding:10px 16px;gap:12px;border-bottom:1px solid var(--border);font-size:.68rem;font-weight:600;color:var(--stone);text-transform:uppercase;letter-spacing:.07em}
.client-row{display:grid;grid-template-columns:1fr 120px 80px 140px 24px;padding:12px 16px;gap:12px;align-items:center;border-bottom:1px solid var(--border);cursor:pointer;transition:background .15s}
.client-row:last-child{border-bottom:none}.client-row:hover{background:var(--cream)}
.client-avatar{width:32px;height:32px;border-radius:50%;background:var(--parchment);color:var(--bark);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:600;flex-shrink:0}
.client-info{display:flex;flex-direction:column;gap:2px}
.client-name{font-size:.88rem;font-weight:500;color:var(--text)}
.client-contact{font-size:.72rem;color:var(--stone)}
.client-joined{font-size:.78rem;color:var(--light-text)}
.client-progress-col{display:flex;align-items:center;gap:8px}
.badge-paid{font-size:.68rem;background:#D4E8CF;color:var(--moss);padding:3px 9px;border-radius:20px}
.badge-unpaid{font-size:.68rem;background:#F5DDD8;color:#8C3A2E;padding:3px 9px;border-radius:20px}
.badge-pending{font-size:.68rem;background:#FFF3CD;color:#856404;padding:3px 9px;border-radius:20px}
.hstat-pending .hstat-val{color:#856404}
.mini-bar{width:60px;height:4px;background:var(--parchment);border-radius:2px;overflow:hidden}
.mini-fill{height:100%;background:var(--moss);border-radius:2px}
.mini-label{font-size:.7rem;color:var(--stone)}
.empty-state{padding:32px;text-align:center;color:var(--stone);font-size:.85rem}
.overlay{position:fixed;inset:0;z-index:200;background:rgba(20,50,58,.4);backdrop-filter:blur(4px);display:flex;justify-content:flex-end}
.detail-panel{width:480px;max-width:95vw;background:var(--cream);height:100vh;overflow-y:auto;padding:28px 24px;position:relative;box-shadow:-8px 0 32px rgba(20,50,58,.15)}
.detail-close{position:absolute;top:16px;right:16px;background:none;border:none;cursor:pointer;font-size:1rem;color:var(--stone)}
.detail-header{display:flex;align-items:center;gap:14px;margin-bottom:24px;flex-wrap:wrap}
.detail-avatar{width:48px;height:48px;border-radius:50%;background:var(--parchment);color:var(--bark);display:flex;align-items:center;justify-content:center;font-size:1rem;font-weight:600;flex-shrink:0}
.detail-name{font-family:'Plus Jakarta Sans',serif;font-size:1.3rem;font-weight:600;color:var(--soil)}
.detail-sub{font-size:.78rem;color:var(--stone)}.ml{margin-left:auto}
.detail-stats{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:24px}
.stat-card{background:var(--white);border:1px solid var(--border);border-radius:10px;padding:12px 14px}
.stat-val{display:block;font-size:1.1rem;font-weight:600;color:var(--soil);margin-bottom:2px}
.stat-lab{font-size:.68rem;color:var(--stone);text-transform:uppercase;letter-spacing:.06em}
.detail-section-title{font-family:'Plus Jakarta Sans',serif;font-size:1rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px}
.session-progress-list{display:flex;flex-direction:column;gap:18px}
.progress-program{display:flex;flex-direction:column}
.progress-prog-head{display:flex;align-items:center;justify-content:space-between;gap:10px}
.progress-prog-title{font-size:.86rem;font-weight:600;color:var(--soil)}
.progress-prog-count{font-size:.72rem;color:var(--stone);white-space:nowrap}
.progress-program .mini-bar{width:100%}
.progress-day-label{font-size:.7rem;font-weight:600;color:var(--stone);text-transform:uppercase;letter-spacing:.07em;display:block;margin-bottom:4px}
.progress-sessions{display:flex;flex-direction:column;gap:3px}
.progress-session-item{display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--white);border:1px solid var(--border);border-radius:7px;font-size:.78rem}
.detail-tabs{display:flex;gap:4px;margin-bottom:20px;border-bottom:1px solid var(--border);padding-bottom:0}
.detail-tab-btn{background:none;border:none;padding:8px 14px;font-family:'Inter',sans-serif;font-size:.82rem;color:var(--stone);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;transition:all .15s}
.detail-tab-btn:hover{color:var(--bark)}.detail-tab-btn.active{color:var(--soil);border-bottom-color:var(--bark);font-weight:500}
.badge-completed{font-size:.68rem;background:#E8D5F5;color:#6B3FA0;padding:3px 9px;border-radius:20px}
.badge-new-payment{font-size:.6rem;background:#3E7D6B;color:white;padding:2px 7px;border-radius:20px;margin-left:7px;vertical-align:middle;letter-spacing:.04em;text-transform:uppercase}
.hstat-new .hstat-val{color:var(--moss)}
.client-filter-tabs{display:flex;gap:4px;margin-bottom:12px;flex-wrap:wrap}
.client-filter-btn{background:none;border:1px solid var(--border);border-radius:20px;padding:5px 14px;font-family:'Inter',sans-serif;font-size:.75rem;color:var(--stone);cursor:pointer;transition:all .15s}
.client-filter-btn:hover{border-color:var(--bark);color:var(--bark)}
.client-filter-btn.active{background:var(--soil);color:white;border-color:var(--soil)}
.assessment-tab{padding:4px 0}
.assess-completed-at{font-size:.72rem;color:var(--stone);margin-bottom:14px;padding:8px 12px;background:var(--parchment);border-radius:8px}
.assess-scores-list{display:flex;flex-direction:column;gap:12px;margin-bottom:14px}
.assess-score-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
.assess-score-left{flex:1}
.assess-cat-name{display:block;font-size:.82rem;font-weight:500;color:var(--text)}
.assess-cat-label{display:block;font-size:.7rem;font-weight:500}
.assess-score-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
.assess-bar{width:80px;height:6px;background:var(--parchment);border-radius:3px;overflow:hidden}
.assess-bar-fill{height:100%;border-radius:3px;transition:width .8s ease}
.assess-score-num{font-size:.9rem;font-weight:600;min-width:28px;text-align:right}
.assess-out-of{font-size:.65rem;color:var(--stone);font-weight:400}
.assess-total{font-size:.85rem;color:var(--light-text);text-align:right;padding-top:8px;border-top:1px solid var(--border)}
.privacy-toggle-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;background:var(--parchment);border-radius:10px;margin-bottom:12px}
.privacy-label{display:block;font-size:.82rem;font-weight:500;color:var(--text)}
.privacy-sub{display:block;font-size:.72rem;color:var(--stone);margin-top:2px}
.privacy-toggle-btn{padding:6px 14px;border-radius:20px;border:none;font-family:'Inter',sans-serif;font-size:.75rem;font-weight:500;cursor:pointer;transition:all .2s;flex-shrink:0}
.privacy-toggle-btn.on{background:#D4E8CF;color:var(--moss)}
.privacy-toggle-btn.off{background:#F5DDD8;color:#8C3A2E}
.privacy-toggle-btn:disabled{opacity:.6;cursor:not-allowed}
.btn-approve{background:#3E7D6B;color:white;border:none;border-radius:8px;padding:8px 18px;font-family:'Inter',sans-serif;font-size:.82rem;font-weight:500;cursor:pointer;transition:background .15s;margin-left:auto}
.btn-approve:hover:not(:disabled){background:#3A4A33}.btn-approve:disabled{opacity:.6;cursor:not-allowed}
.ps-icon{font-size:.9rem}.ps-label{flex:1;color:var(--text)}
.ps-no-file{font-size:.7rem;color:var(--stone)}.ps-not-started{font-size:.7rem;color:var(--stone)}
.ps-in-progress{font-size:.7rem;color:var(--bark);background:var(--parchment);padding:2px 7px;border-radius:20px}
.ps-done{font-size:.7rem;color:var(--moss);background:#D4E8CF;padding:2px 7px;border-radius:20px}
.programs-list{display:flex;flex-direction:column;gap:8px;margin-bottom:20px}
.program-link-row{background:var(--white);border:1px solid var(--border);border-radius:10px;padding:14px 16px;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;transition:all .15s}
.pl-inactive{opacity:.55}
.pl-info{flex:1;display:flex;flex-direction:column;gap:3px;min-width:0}
.pl-title{font-size:.9rem;font-weight:500;color:var(--text)}
.pl-desc{font-size:.75rem;color:var(--light-text)}
.pl-url{font-size:.72rem;color:var(--bark);word-break:break-all;text-decoration:none}
.pl-url:hover{text-decoration:underline}
.pl-actions{display:flex;align-items:center;gap:6px;flex-shrink:0}
.pl-toggle{padding:4px 10px;border-radius:20px;border:none;font-family:'Inter',sans-serif;font-size:.72rem;font-weight:500;cursor:pointer;transition:all .15s}
.pl-on{background:#D4E8CF;color:var(--moss)}
.pl-off{background:var(--parchment);color:var(--stone)}
.pl-edit-btn{background:none;border:1px solid var(--border);color:var(--stone);border-radius:6px;padding:4px 10px;font-size:.75rem;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
.pl-edit-btn:hover{border-color:var(--bark);color:var(--bark)}
.pl-delete{background:none;border:1px solid #E8C4BE;color:#8C3A2E;border-radius:6px;padding:4px 8px;font-size:.72rem;cursor:pointer;transition:all .15s}
.pl-delete:hover{background:#FBF0EE}
.program-link-edit{width:100%;display:flex;flex-direction:column;gap:8px}
.pl-input{width:100%;padding:8px 12px;border:1px solid var(--border);border-radius:8px;font-family:'Inter',sans-serif;font-size:.85rem;background:white;color:var(--text)}
.pl-input:focus{outline:none;border-color:var(--bark)}
.pl-edit-actions{display:flex;gap:8px}
.btn-save-pl{background:var(--soil);color:white;border:none;border-radius:6px;padding:7px 16px;font-family:'Inter',sans-serif;font-size:.8rem;cursor:pointer;transition:background .15s}
.btn-save-pl:hover{background:var(--bark)}
.btn-cancel-pl{background:none;border:1px solid var(--border);color:var(--stone);border-radius:6px;padding:7px 16px;font-family:'Inter',sans-serif;font-size:.8rem;cursor:pointer}
.program-link-add{background:var(--white);border:1px dashed var(--border);border-radius:12px;padding:20px}
.pla-title{font-size:.82rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px}
.pla-fields{display:flex;flex-direction:column;gap:8px}
.btn-add-pl{background:var(--soil);color:white;border:none;border-radius:8px;padding:10px;font-family:'Inter',sans-serif;font-size:.85rem;font-weight:500;cursor:pointer;transition:background .15s;margin-top:4px}
.btn-add-pl:hover:not(:disabled){background:var(--bark)}
.btn-add-pl:disabled{opacity:.5;cursor:not-allowed}

/* SLOT SECTIONS */
.slot-section{border-top:1px solid var(--border);padding:12px 16px 4px}
.slot-section-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
.slot-section-label{font-size:.78rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.05em}
.btn-add-part{background:none;border:1px solid var(--bark);color:var(--bark);border-radius:6px;padding:4px 12px;font-family:'Inter',sans-serif;font-size:.75rem;font-weight:500;cursor:pointer;transition:all .15s}
.btn-add-part:hover{background:var(--bark);color:white}
.slot-empty{font-size:.78rem;color:var(--stone);font-style:italic;padding:8px 0 12px}

/* ACCESS DENIED */
.access-denied-screen{min-height:100vh;display:flex;align-items:center;justify-content:center;background:var(--cream)}
.access-denied-card{background:var(--white);border:1px solid var(--border);border-radius:16px;padding:40px 32px;max-width:400px;width:100%;text-align:center;display:flex;flex-direction:column;align-items:center;gap:16px}
.access-denied-title{font-family:'Plus Jakarta Sans',serif;font-size:1.6rem;color:var(--soil);margin:0}
.access-denied-msg{font-size:.85rem;color:var(--stone);line-height:1.6;margin:0}

/* COACHES */
.coaches-list{display:flex;flex-direction:column;gap:10px;margin-bottom:28px}
.no-coaches{font-size:.85rem;color:var(--stone);margin-bottom:20px}
.coach-row{display:flex;align-items:center;justify-content:space-between;gap:12px;background:var(--white);border:1px solid var(--border);border-radius:10px;padding:14px 16px}
.coach-info{display:flex;flex-direction:column;gap:2px;min-width:0}
.coach-name{font-size:.88rem;font-weight:600;color:var(--soil)}
.coach-uid{font-size:.72rem;color:var(--stone);font-family:monospace;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:260px}
.coach-actions{display:flex;align-items:center;gap:8px;flex-shrink:0}
.coach-toggle{border:none;border-radius:20px;padding:5px 12px;font-family:'Inter',sans-serif;font-size:.75rem;font-weight:500;cursor:pointer;transition:all .15s}
.coach-toggle.on{background:#EDF2EB;color:var(--moss)}
.coach-toggle.off{background:#F5F0EA;color:var(--stone)}
.coach-remove{background:none;border:1px solid var(--border);color:var(--stone);border-radius:6px;padding:4px 8px;font-size:.8rem;cursor:pointer;transition:all .15s}
.coach-remove:hover{border-color:#C0392B;color:#C0392B}
.coach-add{background:var(--white);border:1px dashed var(--border);border-radius:12px;padding:20px}
.coach-add-title{font-size:.82rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px}
.coach-add-fields{display:flex;flex-direction:column;gap:8px;margin-bottom:10px}
.coach-input{border:1px solid var(--border);border-radius:8px;padding:9px 12px;font-family:'Inter',sans-serif;font-size:.85rem;color:var(--text);background:var(--cream);width:100%}
.coach-input:focus{outline:none;border-color:var(--bark)}
.coach-err{font-size:.8rem;color:#C0392B;margin-bottom:8px}
.btn-add-coach{background:var(--soil);color:white;border:none;border-radius:8px;padding:10px;font-family:'Inter',sans-serif;font-size:.85rem;font-weight:500;cursor:pointer;transition:background .15s;width:100%}
.btn-add-coach:hover:not(:disabled){background:var(--bark)}
.btn-add-coach:disabled{opacity:.5;cursor:not-allowed}
.coach-search-wrap{position:relative;margin-bottom:10px}
.coach-suggestions{position:absolute;top:calc(100% + 4px);left:0;right:0;background:white;border:1px solid var(--border);border-radius:8px;box-shadow:0 8px 24px rgba(20,50,58,.12);z-index:20;overflow:hidden}
.coach-suggestion-row{display:flex;align-items:center;gap:10px;padding:10px 12px;width:100%;background:none;border:none;border-bottom:1px solid var(--border);cursor:pointer;text-align:left;transition:background .15s;font-family:'Inter',sans-serif}
.coach-suggestion-row:last-child{border-bottom:none}.coach-suggestion-row:hover{background:var(--cream)}
.cs-avatar{width:28px;height:28px;border-radius:50%;background:var(--parchment);color:var(--bark);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:600;flex-shrink:0}
.cs-info{display:flex;flex-direction:column;gap:1px}
.cs-name{font-size:.82rem;font-weight:500;color:var(--text)}
.cs-email{font-size:.7rem;color:var(--stone)}
.coach-no-results{font-size:.78rem;color:var(--stone);margin-top:6px;margin-bottom:10px}
.coach-selected-user{display:flex;align-items:center;gap:10px;background:var(--parchment);border:1px solid var(--border);border-radius:8px;padding:10px 12px;margin-bottom:10px}
.csu-avatar{width:32px;height:32px;border-radius:50%;background:var(--dawn);color:var(--soil);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:600;flex-shrink:0}
.csu-info{flex:1;display:flex;flex-direction:column;gap:2px}
.csu-name{font-size:.85rem;font-weight:500;color:var(--text)}
.csu-email{font-size:.72rem;color:var(--stone)}
.csu-clear{background:none;border:none;color:var(--stone);cursor:pointer;font-size:.8rem;padding:4px;line-height:1}
.csu-clear:hover{color:var(--text)}

/* REVENUE TOGGLE */
.btn-revenue-toggle{background:var(--parchment);border:1px solid var(--border);border-radius:10px;padding:10px 16px;font-family:'Inter',sans-serif;font-size:.78rem;font-weight:500;color:var(--bark);cursor:pointer;transition:all .15s;white-space:nowrap}
.btn-revenue-toggle:hover{border-color:var(--bark)}.btn-revenue-toggle.active{background:var(--soil);color:white;border-color:var(--soil)}

/* REVENUE PANEL */
.revenue-panel{background:var(--white);border:1px solid var(--border);border-radius:14px;padding:20px 24px;margin-bottom:20px;animation:fadeUp .2s ease}
.revenue-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:20px}
.rev-card{border-radius:10px;padding:16px 18px;display:flex;flex-direction:column;gap:3px}
.rev-all{background:var(--soil);color:white}
.rev-month{background:var(--parchment);color:var(--soil)}
.rev-week{background:#EDF2EB;color:var(--moss)}
.rev-amount{font-family:'Plus Jakarta Sans',serif;font-size:1.25rem;font-weight:600;line-height:1.2}
.rev-label{font-size:.68rem;font-weight:600;text-transform:uppercase;letter-spacing:.07em;opacity:.75}
.rev-clients{font-size:.72rem;opacity:.65;margin-top:2px}
.rev-breakdown{border-top:1px solid var(--border);padding-top:16px}
.rev-breakdown-title{font-size:.72rem;font-weight:600;color:var(--stone);text-transform:uppercase;letter-spacing:.07em;margin-bottom:12px}
.rev-row{display:grid;grid-template-columns:90px 1fr 50px 130px;align-items:center;gap:12px;margin-bottom:8px}
.rev-month{font-size:.78rem;color:var(--light-text)}
.rev-bar-track{height:6px;background:var(--parchment);border-radius:3px;overflow:hidden}
.rev-bar-fill{height:100%;background:var(--bark);border-radius:3px;transition:width .4s ease}
.rev-row-clients{font-size:.75rem;color:var(--stone);text-align:right}
.rev-row-amount{font-size:.78rem;font-weight:500;color:var(--soil);text-align:right}

/* FEEDBACK TAB */
.feedback-list{display:flex;flex-direction:column;gap:10px;margin-top:4px}
.feedback-entry{background:white;border:1px solid var(--border);border-radius:12px;padding:18px 20px}
.fe-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.fe-user{display:flex;flex-direction:column;gap:2px}
.fe-name{font-size:.88rem;font-weight:500;color:var(--text)}
.fe-date{font-size:.7rem;color:var(--stone)}
.fe-stars{font-size:1.1rem;color:#C4A348;letter-spacing:2px}
.fe-block{margin-top:10px}
.fe-label{display:block;font-size:.68rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.07em;margin-bottom:4px}
.fe-text{font-size:.85rem;color:var(--text);line-height:1.65;margin:0}

/* CUSTOM PROGRAM BUTTONS */
.program-btn-wrap{position:relative;display:flex;align-items:stretch;gap:0;margin-bottom:4px}
.program-btn-wrap .program-btn{flex:1;margin-bottom:0;border-radius:8px 0 0 8px}
.program-btn-delete{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-left:none;border-radius:0 8px 8px 0;color:rgba(255,255,255,.3);font-size:.7rem;padding:0 8px;cursor:pointer;transition:all .15s}
.program-btn-delete:hover{background:rgba(200,80,60,.25);color:rgba(255,160,150,.9)}

/* FLAT SESSIONS */
.flat-sessions-wrap{display:flex;flex-direction:column;gap:6px}

/* NEW PROGRAM MODAL */
.modal-overlay{position:fixed;inset:0;z-index:300;background:rgba(20,50,58,.5);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px}
.modal-box{background:var(--cream);border-radius:16px;width:100%;max-width:480px;box-shadow:0 20px 60px rgba(20,50,58,.25);max-height:90vh;display:flex;flex-direction:column}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:20px 24px 0}
.modal-title{font-family:'Plus Jakarta Sans',serif;font-size:1.4rem;font-weight:600;color:var(--soil)}
.modal-close{background:none;border:none;font-size:1rem;color:var(--stone);cursor:pointer;padding:4px}
.modal-body{padding:20px 24px 24px;overflow-y:auto}
.mf-group{margin-bottom:14px}
.mf-label{display:block;font-size:.72rem;font-weight:600;color:var(--bark);text-transform:uppercase;letter-spacing:.07em;margin-bottom:5px}
.mf-input{width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:8px;font-family:'Inter',sans-serif;font-size:.88rem;color:var(--text);background:white}
.mf-input:focus{outline:none;border-color:var(--bark)}
.mf-count{max-width:100px}
.modal-step-hint{font-size:.8rem;color:var(--light-text);margin-bottom:14px}
.modal-err{font-size:.8rem;color:#8C3A2E;margin-bottom:10px}
.session-names-list{display:flex;flex-direction:column;gap:6px;margin-bottom:16px;max-height:340px;overflow-y:auto;padding-right:4px}
.sn-row{display:flex;align-items:center;gap:8px}
.sn-num{font-size:.7rem;font-weight:600;color:var(--stone);min-width:20px;text-align:right}
.sn-icon{font-size:1rem;width:22px;text-align:center;flex-shrink:0}
.sn-input{flex:1;margin:0}
.modal-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:4px}
.btn-modal-cancel{background:none;border:1px solid var(--border);color:var(--stone);border-radius:8px;padding:9px 18px;font-family:'Inter',sans-serif;font-size:.85rem;cursor:pointer}
.btn-modal-next{background:var(--soil);color:white;border:none;border-radius:8px;padding:9px 20px;font-family:'Inter',sans-serif;font-size:.85rem;font-weight:500;cursor:pointer;transition:background .15s}
.btn-modal-next:hover{background:var(--bark)}
.btn-modal-save{background:var(--moss);color:white;border:none;border-radius:8px;padding:9px 20px;font-family:'Inter',sans-serif;font-size:.85rem;font-weight:500;cursor:pointer;transition:background .15s}
.btn-modal-save:hover:not(:disabled){background:#3A4A33}.btn-modal-save:disabled{opacity:.5;cursor:not-allowed}

/* ════════ REDESIGN: shared primitives ════════ */
.ui-badge{display:inline-flex;align-items:center;gap:5px;font-size:.68rem;font-weight:600;padding:3px 9px;border-radius:20px;white-space:nowrap;line-height:1.4}
.ui-badge-dot{width:6px;height:6px;border-radius:50%;background:currentColor}
.ui-badge--ok{background:var(--ok-bg);color:var(--ok)}
.ui-badge--warn{background:var(--warn-bg);color:var(--warn)}
.ui-badge--danger{background:var(--danger-bg);color:var(--danger)}
.ui-badge--info{background:var(--info-bg);color:var(--bark)}
.ui-badge--violet{background:var(--violet-bg);color:var(--violet)}
.ui-badge--stone{background:var(--parchment);color:var(--light-text)}
.ui-bar{width:100%;height:6px;background:var(--parchment);border-radius:3px;overflow:hidden}
.ui-bar-fill{height:100%;border-radius:3px;transition:width .4s ease}
.ui-bar-fill--ok{background:var(--moss)}.ui-bar-fill--info{background:var(--bark)}.ui-bar-fill--warn{background:#D4A849}
.kpi-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;margin-bottom:24px}
.kpi-card{background:var(--white);border:1px solid var(--border);border-radius:14px;padding:16px 18px;display:flex;flex-direction:column;gap:2px;text-align:left;box-shadow:var(--shadow-sm)}
.kpi-card--btn{cursor:pointer;font-family:inherit;transition:transform .15s,box-shadow .15s,border-color .15s}
.kpi-card--btn:hover{transform:translateY(-2px);box-shadow:var(--shadow-md);border-color:var(--dawn)}
.kpi-card--btn:focus-visible{outline:2px solid var(--bark);outline-offset:2px}
.kpi-card--active{border-color:var(--bark);box-shadow:0 0 0 1px var(--bark)}
.kpi-val{font-family:'Plus Jakarta Sans',sans-serif;font-size:1.7rem;font-weight:600;color:var(--kpi-accent,var(--soil));line-height:1.1}
.kpi-val--danger{color:#C0392B}
.kpi-lab{font-size:.72rem;color:var(--stone);text-transform:uppercase;letter-spacing:.05em;font-weight:600}
.kpi-sub{font-size:.72rem;color:var(--light-text);margin-top:2px}
.sec{margin-bottom:24px}
.sec-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}
.sec-title{font-family:'Plus Jakarta Sans',sans-serif;font-size:.92rem;font-weight:600;color:var(--soil);display:flex;align-items:center;gap:8px}
.sec-count{font-size:.7rem;font-weight:600;color:var(--bark);background:var(--info-bg);padding:1px 8px;border-radius:20px}
.sec-link{background:none;border:none;color:var(--bark);font-family:inherit;font-size:.78rem;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:4px;padding:4px}
.sec-link:hover{color:var(--soil)}.sec-link:focus-visible{outline:2px solid var(--bark);outline-offset:2px;border-radius:4px}
.card{background:var(--white);border:1px solid var(--border);border-radius:14px;overflow:hidden;box-shadow:var(--shadow-sm)}
.arow{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);background:none;border-left:none;border-right:none;border-top:none;width:100%;text-align:left;font-family:inherit;cursor:pointer;transition:background .15s}
.arow:last-child{border-bottom:none}.arow:hover{background:var(--cream)}
.arow-open{flex:1;min-width:0;display:flex;align-items:center;gap:12px;background:none;border:none;padding:0;text-align:left;font-family:inherit;cursor:pointer}
.arow-open:focus-visible{outline:2px solid var(--bark);outline-offset:2px;border-radius:6px}
.arow-dismiss{background:none;border:1px solid var(--border);color:var(--stone);border-radius:8px;padding:6px 10px;font-family:inherit;font-size:.74rem;font-weight:500;cursor:pointer;white-space:nowrap;transition:all .15s}
.arow-dismiss:hover{border-color:var(--danger);color:var(--danger);background:var(--white)}
.arow-dismiss:disabled{opacity:.55;cursor:not-allowed}
.arow:focus-visible{outline:2px solid var(--bark);outline-offset:-2px}
.arow-av{width:34px;height:34px;border-radius:50%;background:var(--parchment);color:var(--bark);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:600;flex-shrink:0}
.arow-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px}
.arow-title{font-size:.86rem;font-weight:500;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.arow-sub{font-size:.72rem;color:var(--stone)}
.arow-actions{display:flex;align-items:center;gap:6px;flex-shrink:0}
.btn-sm{display:inline-flex;align-items:center;gap:5px;border-radius:8px;padding:6px 12px;font-family:inherit;font-size:.75rem;font-weight:500;cursor:pointer;border:1px solid var(--border);background:var(--white);color:var(--soil);transition:all .15s;white-space:nowrap}
.btn-sm:hover{border-color:var(--bark);color:var(--bark)}
.btn-sm:focus-visible{outline:2px solid var(--bark);outline-offset:2px}
.btn-sm--primary{background:var(--soil);color:#fff;border-color:var(--soil)}
.btn-sm--primary:hover{background:var(--bark);border-color:var(--bark);color:#fff}
.btn-sm--ok{background:var(--ok);color:#fff;border-color:var(--ok)}
.btn-sm--ok:hover{background:#356b5c;color:#fff}
.btn-sm:disabled{opacity:.55;cursor:not-allowed}
.btn-icon{display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:8px;border:1px solid transparent;background:none;color:var(--stone);cursor:pointer;transition:all .15s;flex-shrink:0}
.btn-icon:hover{background:var(--parchment);color:var(--soil)}
.btn-icon:focus-visible{outline:2px solid var(--bark);outline-offset:1px}
.pp-row{display:grid;grid-template-columns:1.3fr 1.4fr 96px;gap:14px;align-items:center;padding:14px 16px;border-bottom:1px solid var(--border);width:100%;text-align:left;background:none;border-left:none;border-right:none;border-top:none;font-family:inherit;cursor:pointer;transition:background .15s}
.pp-row:last-child{border-bottom:none}.pp-row:hover{background:var(--cream)}
.pp-row:focus-visible{outline:2px solid var(--bark);outline-offset:-2px}
.pp-name{font-size:.88rem;font-weight:600;color:var(--soil)}
.pp-metrics{display:flex;gap:16px;flex-wrap:wrap}
.pp-metric{display:flex;flex-direction:column}
.pp-metric b{font-size:.9rem;font-weight:600;color:var(--text)}
.pp-metric span{font-size:.62rem;color:var(--stone);text-transform:uppercase;letter-spacing:.04em}
.pp-completion{display:flex;flex-direction:column;gap:4px}
.pp-completion small{font-size:.64rem;color:var(--stone)}
.trend{display:flex;flex-direction:column;gap:6px}
.trend-svg{width:100%;display:block}
.trend-area{fill:rgba(79,156,168,.14);stroke:none}
.trend-line{stroke:var(--bark);stroke-width:2}
.trend-dot{fill:var(--soil);stroke:#fff;stroke-width:1}
.trend-labels{display:flex;justify-content:space-between;font-size:.64rem;color:var(--stone)}
.skeleton-list{display:flex;flex-direction:column;gap:2px;background:var(--white);border:1px solid var(--border);border-radius:12px;padding:6px}
.skeleton-row{display:flex;align-items:center;gap:12px;padding:12px}
.skeleton-avatar{width:34px;height:34px;border-radius:50%;background:var(--parchment);flex-shrink:0}
.skeleton-lines{flex:1;display:flex;flex-direction:column;gap:6px}
.skeleton-line{height:10px;border-radius:5px;background:var(--parchment);width:60%}
.skeleton-line.short{width:35%}
.skeleton-avatar,.skeleton-line{animation:shimmer 1.3s ease-in-out infinite}
@keyframes shimmer{0%,100%{opacity:.5}50%{opacity:1}}
.empty-action{display:flex;flex-direction:column;align-items:center;gap:12px;padding:28px;text-align:center;color:var(--stone);font-size:.85rem}

/* ════════ REDESIGN: clients facets + bulk + rows ════════ */
.facets{display:flex;flex-direction:column;gap:10px;margin-bottom:14px}
.facet-row{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.seg{display:inline-flex;background:var(--white);border:1px solid var(--border);border-radius:10px;padding:3px;gap:2px}
.seg-btn{border:none;background:none;font-family:inherit;font-size:.76rem;font-weight:500;color:var(--stone);padding:6px 12px;border-radius:7px;cursor:pointer;transition:all .15s;white-space:nowrap}
.seg-btn:hover{color:var(--soil)}.seg-btn.active{background:var(--soil);color:#fff}
.seg-btn:focus-visible{outline:2px solid var(--bark);outline-offset:1px}
.facet-select{padding:8px 12px;border:1px solid var(--border);border-radius:10px;font-family:inherit;font-size:.78rem;color:var(--text);background:white;cursor:pointer}
.facet-select:focus-visible{outline:2px solid var(--bark);outline-offset:1px}
.facet-label{font-size:.64rem;font-weight:600;color:var(--stone);text-transform:uppercase;letter-spacing:.05em}
.result-count{font-size:.75rem;color:var(--light-text);margin-left:auto}
.bulk-bar{position:sticky;top:0;z-index:5;display:flex;align-items:center;gap:10px;background:var(--soil);color:#fff;border-radius:12px;padding:10px 14px;margin-bottom:12px;box-shadow:var(--shadow-md);flex-wrap:wrap}
.bulk-count{font-size:.82rem;font-weight:600}
.bulk-bar .btn-sm{background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.22);color:#fff}
.bulk-bar .btn-sm:hover{background:rgba(255,255,255,.22);border-color:rgba(255,255,255,.45);color:#fff}
.bulk-clear{margin-left:auto;background:none;border:none;color:rgba(255,255,255,.7);cursor:pointer;font-family:inherit;font-size:.78rem;padding:4px 8px}
.bulk-clear:hover{color:#fff}
/* Actions column must fit every quick-action icon a row can show — undismiss +
   WhatsApp + nudge + message (32px each) + the chevron + gaps = 150px. It was 60px,
   so the icons overflowed and spilled over the progress counter. The header and the
   rows are separate grid containers, so this has to be a fixed width (not auto) or
   their columns stop lining up with each other. */
.clients-thead{grid-template-columns:34px 1fr 140px 100px 120px 152px}
.client-row{grid-template-columns:34px 1fr 140px 100px 120px 152px}
.client-row:focus-visible{outline:2px solid var(--bark);outline-offset:-2px}
.client-row.selected{background:var(--info-bg)}
.col-check{display:flex;align-items:center;justify-content:center}
.col-check input{width:16px;height:16px;cursor:pointer;accent-color:var(--bark)}
.col-program{font-size:.78rem;color:var(--light-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.col-status{display:flex}
.col-actions{display:flex;align-items:center;justify-content:flex-end;gap:2px}
.client-meta-line{font-size:.72rem;color:var(--stone);display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.client-quick{opacity:0;transition:opacity .15s}
.client-row:hover .client-quick,.client-row:focus-within .client-quick{opacity:1}
.at-risk{color:var(--danger);font-weight:500}

/* ════════ REDESIGN: detail overview ════════ */
.detail-panel{width:560px}
.detail-quick{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:18px}
.overview-card{background:var(--white);border:1px solid var(--border);border-radius:14px;padding:16px 18px;margin-bottom:20px;box-shadow:var(--shadow-sm)}
.ov-top{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px}
.ov-prog-name{font-size:.9rem;font-weight:600;color:var(--soil)}
.ov-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(104px,1fr));gap:10px;margin-top:14px}
.ov-cell{display:flex;flex-direction:column;gap:2px}
.ov-cell b{font-size:1rem;font-weight:600;color:var(--text)}
.ov-cell span{font-size:.64rem;color:var(--stone);text-transform:uppercase;letter-spacing:.04em}

/* ════════ REDESIGN: responsive ════════ */
@media(max-width:1024px){.main-content{padding:20px}.tab-content{max-width:none}}
@media(max-width:768px){
  .sidebar{width:72px}
  .sidebar-brand,.sidebar-role,.nav-label,.nav-badge,.program-selector{display:none}
  .sidebar-logo{justify-content:center;padding:16px 4px}
  .nav-item{justify-content:center;padding:12px 0}
  .sidebar-footer{padding:10px 6px}
  .sidebar-footer .btn-signout{font-size:.6rem;padding:7px 2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .clients-thead{display:none}
  .client-row{display:flex;align-items:center;gap:10px}
  .client-row .col-program,.client-row .col-progress,.client-row .client-progress-col{display:none}
  .col-identity{flex:1;min-width:0}
  .detail-panel{width:100vw;max-width:100vw;padding:20px 16px}
  .kpi-row{grid-template-columns:repeat(2,1fr)}
  .pp-row{grid-template-columns:1fr;gap:8px}
}
@media(max-width:480px){.kpi-row{grid-template-columns:1fr 1fr}.main-content{padding:14px}}

/* ════════ REDESIGN: motion safety ════════ */
@media(prefers-reduced-motion:reduce){
  *,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}
  .kpi-card--btn:hover{transform:none}
}
`;
